import {
  __glob,
  __require
} from "./chunk-QWGCMQQD.js";

// src/hooks/useLocalStorage.ts
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
function useLocalStorage(key, initialValue, options = {}) {
  const {
    serialize = JSON.stringify,
    deserialize = JSON.parse,
    onError,
    syncAcrossTabs = true,
    validateValue,
    fallbackValue,
    encryption
  } = options;
  const [value, setValue] = useState(initialValue);
  const [isHydrated, setIsHydrated] = useState(false);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);
  const lastValidValueRef = useRef(initialValue);
  const storageKeyRef = useRef(key);
  useEffect(() => {
    storageKeyRef.current = key;
  }, [key]);
  const handleError = useCallback(
    (error2, operation) => {
      const errorObj = error2 instanceof Error ? error2 : new Error(String(error2));
      const contextError = new Error(
        `LocalStorage ${operation} failed for key "${storageKeyRef.current}": ${errorObj.message}`
      );
      setError(contextError);
      onError?.(contextError, operation);
      return false;
    },
    [onError]
  );
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  const readFromStorage = useCallback(() => {
    if (typeof window === "undefined") {
      return fallbackValue ?? initialValue;
    }
    try {
      const item = window.localStorage.getItem(storageKeyRef.current);
      if (item === null) {
        return fallbackValue ?? initialValue;
      }
      const decryptedItem = encryption ? encryption.decrypt(item) : item;
      const parsedValue = deserialize(decryptedItem);
      if (validateValue && !validateValue(parsedValue)) {
        console.warn(
          `Invalid value for localStorage key "${storageKeyRef.current}", using fallback`
        );
        return fallbackValue ?? initialValue;
      }
      lastValidValueRef.current = parsedValue;
      return parsedValue;
    } catch (error2) {
      handleError(error2, "read");
      return lastValidValueRef.current;
    }
  }, [
    initialValue,
    fallbackValue,
    deserialize,
    validateValue,
    encryption,
    handleError
  ]);
  const writeToStorage = useCallback(
    async (newValue) => {
      if (typeof window === "undefined") {
        return false;
      }
      try {
        if (validateValue && !validateValue(newValue)) {
          throw new Error("Value failed validation");
        }
        const serializedValue = serialize(newValue);
        const valueToStore = encryption ? encryption.encrypt(serializedValue) : serializedValue;
        window.localStorage.setItem(storageKeyRef.current, valueToStore);
        if (syncAcrossTabs) {
          window.dispatchEvent(
            new CustomEvent(`localStorage:${storageKeyRef.current}`, {
              detail: {
                key: storageKeyRef.current,
                value: newValue,
                timestamp: Date.now()
              }
            })
          );
        }
        lastValidValueRef.current = newValue;
        return true;
      } catch (error2) {
        return handleError(error2, "write");
      }
    },
    [serialize, validateValue, encryption, syncAcrossTabs, handleError]
  );
  const removeFromStorage = useCallback(async () => {
    if (typeof window === "undefined") {
      return false;
    }
    try {
      window.localStorage.removeItem(storageKeyRef.current);
      if (syncAcrossTabs) {
        window.dispatchEvent(
          new CustomEvent(`localStorage:${storageKeyRef.current}`, {
            detail: {
              key: storageKeyRef.current,
              value: null,
              timestamp: Date.now()
            }
          })
        );
      }
      return true;
    } catch (error2) {
      return handleError(error2, "remove");
    }
  }, [syncAcrossTabs, handleError]);
  useEffect(() => {
    if (!isMountedRef.current) return;
    const storedValue = readFromStorage();
    setValue(storedValue);
    setIsHydrated(true);
  }, [readFromStorage]);
  const setValueEnhanced = useCallback(
    async (newValue) => {
      const valueToStore = typeof newValue === "function" ? newValue(value) : newValue;
      setValue(valueToStore);
      setError(null);
      return await writeToStorage(valueToStore);
    },
    [value, writeToStorage]
  );
  const removeValueEnhanced = useCallback(async () => {
    setValue(fallbackValue ?? initialValue);
    setError(null);
    return await removeFromStorage();
  }, [fallbackValue, initialValue, removeFromStorage]);
  useEffect(() => {
    if (!syncAcrossTabs || typeof window === "undefined") return;
    let debounceTimer;
    const handleStorageChange = (e) => {
      if (e.storageArea === localStorage && e.key === storageKeyRef.current) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          try {
            const newValue = e.newValue ? readFromStorage() : fallbackValue ?? initialValue;
            setValue(newValue);
          } catch (error2) {
            handleError(error2, "sync");
          }
        }, 50);
      }
    };
    const handleCustomStorageEvent = (e) => {
      if (e.detail.key === storageKeyRef.current) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          const newValue = e.detail.value ?? fallbackValue ?? initialValue;
          setValue(newValue);
        }, 50);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(
      `localStorage:${storageKeyRef.current}`,
      handleCustomStorageEvent
    );
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        `localStorage:${storageKeyRef.current}`,
        handleCustomStorageEvent
      );
    };
  }, [
    syncAcrossTabs,
    readFromStorage,
    fallbackValue,
    initialValue,
    handleError
  ]);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  return useMemo(
    () => ({
      value,
      setValue: setValueEnhanced,
      removeValue: removeValueEnhanced,
      isHydrated,
      error,
      clearError
    }),
    [
      value,
      setValueEnhanced,
      removeValueEnhanced,
      isHydrated,
      error,
      clearError
    ]
  );
}
function useSimpleLocalStorage(key, initialValue) {
  const { value, setValue } = useLocalStorage(key, initialValue);
  const simpleSetValue = useCallback(
    (newValue) => {
      setValue(newValue);
    },
    [setValue]
  );
  return [value, simpleSetValue];
}
function useValidatedLocalStorage(key, initialValue, validator, fallbackValue) {
  return useLocalStorage(key, initialValue, {
    validateValue: validator,
    fallbackValue
  });
}

// src/hooks/useDebounce.ts
import { useState as useState2, useEffect as useEffect2, useRef as useRef2, useCallback as useCallback2, useMemo as useMemo2 } from "react";
function useDebounce(value, delay, options = {}) {
  const {
    leading = false,
    trailing = true,
    maxWait,
    onDebounceStart,
    onDebounceEnd,
    onMaxWaitReached
  } = options;
  const [debouncedValue, setDebouncedValue] = useState2(value);
  const [isDebouncing, setIsDebouncing] = useState2(false);
  const [isPending, setIsPending] = useState2(false);
  const [timeSinceLastChange, setTimeSinceLastChange] = useState2(0);
  const [callCount, setCallCount] = useState2(0);
  const timeoutRef = useRef2(null);
  const maxTimeoutRef = useRef2(null);
  const intervalRef = useRef2(null);
  const lastCallTimeRef = useRef2(0);
  const lastInvokeTimeRef = useRef2(0);
  const lastValueRef = useRef2(value);
  const leadingInvokedRef = useRef2(false);
  const isMountedRef = useRef2(true);
  useEffect2(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const clearTimers = useCallback2(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
      maxTimeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);
  const updateTimer = useCallback2(() => {
    if (lastCallTimeRef.current > 0) {
      setTimeSinceLastChange(Date.now() - lastCallTimeRef.current);
    }
  }, []);
  const invokeFunc = useCallback2(
    (newValue) => {
      if (!isMountedRef.current) return;
      setDebouncedValue(newValue);
      setIsDebouncing(false);
      setIsPending(false);
      setTimeSinceLastChange(0);
      lastInvokeTimeRef.current = Date.now();
      leadingInvokedRef.current = false;
      clearTimers();
      onDebounceEnd?.();
    },
    [clearTimers, onDebounceEnd]
  );
  const shouldInvoke = useCallback2(
    (time) => {
      const timeSinceLastCall = time - lastCallTimeRef.current;
      const timeSinceLastInvoke = time - lastInvokeTimeRef.current;
      return lastCallTimeRef.current === 0 || timeSinceLastCall >= delay || maxWait !== void 0 && timeSinceLastInvoke >= maxWait;
    },
    [delay, maxWait]
  );
  const timerExpired = useCallback2(() => {
    const time = Date.now();
    if (shouldInvoke(time)) {
      invokeFunc(lastValueRef.current);
    }
  }, [shouldInvoke, invokeFunc]);
  const maxWaitExpired = useCallback2(() => {
    if (!isMountedRef.current) return;
    onMaxWaitReached?.();
    invokeFunc(lastValueRef.current);
  }, [invokeFunc, onMaxWaitReached]);
  const cancel = useCallback2(() => {
    clearTimers();
    setIsDebouncing(false);
    setIsPending(false);
    setTimeSinceLastChange(0);
    lastCallTimeRef.current = 0;
    lastInvokeTimeRef.current = 0;
    leadingInvokedRef.current = false;
  }, [clearTimers]);
  const flush = useCallback2(() => {
    if (isPending || isDebouncing) {
      invokeFunc(lastValueRef.current);
    }
  }, [isPending, isDebouncing, invokeFunc]);
  const reset = useCallback2(() => {
    cancel();
    setDebouncedValue(value);
    setCallCount(0);
    lastValueRef.current = value;
  }, [cancel, value]);
  useEffect2(() => {
    const time = Date.now();
    const isValueChanged = !Object.is(value, lastValueRef.current);
    if (!isValueChanged && callCount > 0) return;
    lastValueRef.current = value;
    lastCallTimeRef.current = time;
    setCallCount((prev) => prev + 1);
    if (leading && !leadingInvokedRef.current) {
      leadingInvokedRef.current = true;
      setDebouncedValue(value);
      lastInvokeTimeRef.current = time;
      if (!trailing) {
        return;
      }
    }
    if (!isDebouncing) {
      setIsDebouncing(true);
      setIsPending(true);
      onDebounceStart?.();
      intervalRef.current = setInterval(updateTimer, 100);
    }
    clearTimers();
    if (trailing) {
      timeoutRef.current = setTimeout(timerExpired, delay);
    }
    if (maxWait !== void 0 && !leading) {
      const timeSinceLastInvoke = time - lastInvokeTimeRef.current;
      const remainingWait = maxWait - timeSinceLastInvoke;
      if (remainingWait > 0) {
        maxTimeoutRef.current = setTimeout(maxWaitExpired, remainingWait);
      } else {
        setTimeout(maxWaitExpired, 0);
      }
    }
    return clearTimers;
  }, [
    value,
    delay,
    leading,
    trailing,
    maxWait,
    isDebouncing,
    callCount,
    onDebounceStart,
    clearTimers,
    timerExpired,
    maxWaitExpired,
    updateTimer
  ]);
  useEffect2(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);
  return useMemo2(
    () => ({
      debouncedValue,
      isDebouncing,
      isPending,
      timeSinceLastChange,
      callCount,
      cancel,
      flush,
      reset
    }),
    [
      debouncedValue,
      isDebouncing,
      isPending,
      timeSinceLastChange,
      callCount,
      cancel,
      flush,
      reset
    ]
  );
}
function useDebouncedCallback(callback, delay, options = {}) {
  const callbackRef = useRef2(callback);
  const argsRef = useRef2(void 0);
  const [isDebouncing, setIsDebouncing] = useState2(false);
  const [callCount, setCallCount] = useState2(0);
  const [lastExecutionTime, setLastExecutionTime] = useState2(0);
  useEffect2(() => {
    callbackRef.current = callback;
  }, [callback]);
  const {
    leading = false,
    trailing = true,
    maxWait,
    onDebounceStart,
    onDebounceEnd
  } = options;
  const timeoutRef = useRef2(null);
  const maxTimeoutRef = useRef2(null);
  const lastCallTimeRef = useRef2(0);
  const lastInvokeTimeRef = useRef2(0);
  const leadingInvokedRef = useRef2(false);
  const clearTimers = useCallback2(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
      maxTimeoutRef.current = null;
    }
  }, []);
  const invokeFunc = useCallback2(() => {
    if (argsRef.current) {
      const result = callbackRef.current(...argsRef.current);
      setIsDebouncing(false);
      setLastExecutionTime(Date.now());
      lastInvokeTimeRef.current = Date.now();
      leadingInvokedRef.current = false;
      onDebounceEnd?.();
      return result;
    }
  }, [onDebounceEnd]);
  const cancel = useCallback2(() => {
    clearTimers();
    setIsDebouncing(false);
    lastCallTimeRef.current = 0;
    lastInvokeTimeRef.current = 0;
    leadingInvokedRef.current = false;
    argsRef.current = void 0;
  }, [clearTimers]);
  const flush = useCallback2(() => {
    if (timeoutRef.current && argsRef.current) {
      clearTimers();
      return invokeFunc();
    }
  }, [clearTimers, invokeFunc]);
  const debouncedCallback = useCallback2(
    ((...args) => {
      argsRef.current = args;
      const time = Date.now();
      lastCallTimeRef.current = time;
      setCallCount((prev) => prev + 1);
      if (leading && !leadingInvokedRef.current) {
        leadingInvokedRef.current = true;
        const result = callbackRef.current(...args);
        lastInvokeTimeRef.current = time;
        setLastExecutionTime(time);
        return result;
      }
      if (!isDebouncing) {
        setIsDebouncing(true);
        onDebounceStart?.();
      }
      clearTimers();
      if (trailing) {
        timeoutRef.current = setTimeout(invokeFunc, delay);
      }
      if (maxWait !== void 0) {
        const timeSinceLastInvoke = time - lastInvokeTimeRef.current;
        const remainingWait = maxWait - timeSinceLastInvoke;
        if (remainingWait > 0) {
          maxTimeoutRef.current = setTimeout(invokeFunc, remainingWait);
        }
      }
    }),
    [
      delay,
      leading,
      trailing,
      maxWait,
      isDebouncing,
      onDebounceStart,
      clearTimers,
      invokeFunc
    ]
  );
  useEffect2(() => {
    return clearTimers;
  }, [clearTimers]);
  return useMemo2(
    () => ({
      debouncedCallback,
      cancel,
      flush,
      isDebouncing,
      callCount,
      lastExecutionTime
    }),
    [
      debouncedCallback,
      cancel,
      flush,
      isDebouncing,
      callCount,
      lastExecutionTime
    ]
  );
}
function useSimpleDebounce(value, delay) {
  return useDebounce(value, delay).debouncedValue;
}

// src/hooks/useAsync.ts
import { useState as useState3, useEffect as useEffect3, useCallback as useCallback3, useRef as useRef3, useMemo as useMemo3 } from "react";
var asyncCache = /* @__PURE__ */ new Map();
function useAsync(asyncFunction, options = {}) {
  const {
    onSuccess,
    onError,
    onCancel,
    retry,
    timeout,
    executeOnMount = false,
    deps = [],
    cacheKey,
    staleTime = 0
  } = options;
  const [state, setState] = useState3({
    data: null,
    error: null,
    status: "idle",
    isLoading: false,
    isSuccess: false,
    isError: false,
    isIdle: true,
    isCancelled: false
  });
  const [lastExecutedAt, setLastExecutedAt] = useState3(0);
  const [executionCount, setExecutionCount] = useState3(0);
  const [retryCount, setRetryCount] = useState3(0);
  const abortControllerRef = useRef3(null);
  const lastArgsRef = useRef3(null);
  const isMountedRef = useRef3(true);
  const timeoutRef = useRef3(null);
  useEffect3(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  const safeSetState = useCallback3((newState) => {
    if (isMountedRef.current && abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
      setState((prevState) => {
        const updatedState = { ...prevState, ...newState };
        return {
          ...updatedState,
          isLoading: updatedState.status === "loading",
          isSuccess: updatedState.status === "success",
          isError: updatedState.status === "error",
          isIdle: updatedState.status === "idle",
          isCancelled: updatedState.status === "cancelled"
        };
      });
    }
  }, []);
  const calculateRetryDelay = useCallback3(
    (attempt) => {
      if (!retry) return 0;
      const { delay, backoff, maxDelay = Infinity } = retry;
      let retryDelay = delay;
      if (backoff === "exponential") {
        retryDelay = delay * Math.pow(2, attempt);
      } else if (backoff === "linear") {
        retryDelay = delay * (attempt + 1);
      }
      return Math.min(retryDelay, maxDelay);
    },
    [retry]
  );
  const getCachedData = useCallback3(
    (key) => {
      if (!cacheKey) return null;
      const cached = asyncCache.get(key);
      if (cached && Date.now() - cached.timestamp < cached.staleTime) {
        return cached.data;
      }
      return null;
    },
    [cacheKey]
  );
  const setCachedData = useCallback3(
    (key, data) => {
      if (!cacheKey) return;
      asyncCache.set(key, {
        data,
        timestamp: Date.now(),
        staleTime
      });
    },
    [cacheKey, staleTime]
  );
  const execute = useCallback3(
    async (..._args) => {
      if (cacheKey) {
        const cached = getCachedData(cacheKey);
        if (cached) {
          safeSetState({
            data: cached,
            status: "success",
            error: null
          });
          return cached;
        }
      }
      lastArgsRef.current = _args;
      setExecutionCount((prev) => prev + 1);
      setLastExecutedAt(Date.now());
      setRetryCount(0);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;
      safeSetState({
        status: "loading",
        error: null
      });
      const executeWithRetry = async (attempt = 0) => {
        try {
          let timeoutPromise = null;
          if (timeout) {
            timeoutPromise = new Promise((_, reject) => {
              timeoutRef.current = setTimeout(() => {
                reject(new Error(`Operation timed out after ${timeout}ms`));
              }, timeout);
            });
          }
          const asyncPromise = asyncFunction(..._args);
          const result = timeoutPromise ? await Promise.race([asyncPromise, timeoutPromise]) : await asyncPromise;
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          if (signal.aborted) {
            throw new Error("Operation was cancelled");
          }
          if (isMountedRef.current) {
            safeSetState({
              data: result,
              status: "success",
              error: null
            });
            if (cacheKey) {
              setCachedData(cacheKey, result);
            }
            onSuccess?.(result);
          }
          return result;
        } catch (error) {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          if (signal.aborted) {
            if (isMountedRef.current) {
              safeSetState({
                status: "cancelled",
                error: new Error("Operation was cancelled")
              });
              onCancel?.();
            }
            throw new Error("Operation was cancelled");
          }
          const errorObj = error instanceof Error ? error : new Error(String(error));
          if (retry && attempt < retry.attempts) {
            setRetryCount(attempt + 1);
            const retryDelay = calculateRetryDelay(attempt);
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
            if (!signal.aborted && isMountedRef.current) {
              return executeWithRetry(attempt + 1);
            }
          }
          if (isMountedRef.current) {
            safeSetState({
              error: errorObj,
              status: "error"
            });
            onError?.(errorObj);
          }
          throw errorObj;
        }
      };
      return executeWithRetry();
    },
    [
      asyncFunction,
      retry,
      timeout,
      onSuccess,
      onError,
      onCancel,
      cacheKey,
      getCachedData,
      setCachedData,
      calculateRetryDelay,
      safeSetState
    ]
  );
  const reset = useCallback3(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setState({
      data: null,
      error: null,
      status: "idle",
      isLoading: false,
      isSuccess: false,
      isError: false,
      isIdle: true,
      isCancelled: false
    });
    setRetryCount(0);
    lastArgsRef.current = null;
  }, []);
  const cancel = useCallback3(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);
  const retryFunction = useCallback3(async () => {
    if (lastArgsRef.current) {
      return execute(...lastArgsRef.current);
    }
    throw new Error("No previous execution to retry");
  }, [execute]);
  const mutate = useCallback3((updater) => {
    setState((prevState) => {
      const newData = updater(prevState.data);
      return {
        ...prevState,
        data: newData,
        status: "success",
        isSuccess: true,
        isError: false,
        error: null
      };
    });
  }, []);
  const invalidate = useCallback3(() => {
    if (cacheKey) {
      asyncCache.delete(cacheKey);
    }
  }, [cacheKey]);
  useEffect3(() => {
    if (executeOnMount && deps.length === 0) {
      if (executeOnMount) {
      }
    }
  }, [executeOnMount]);
  useEffect3(() => {
    if (deps.length > 0 && lastArgsRef.current) {
      execute(...lastArgsRef.current);
    }
  }, deps);
  return useMemo3(
    () => ({
      ...state,
      execute,
      reset,
      cancel,
      retry: retryFunction,
      mutate,
      invalidate,
      lastExecutedAt,
      executionCount,
      retryCount
    }),
    [
      state,
      execute,
      reset,
      cancel,
      retryFunction,
      mutate,
      invalidate,
      lastExecutedAt,
      executionCount,
      retryCount
    ]
  );
}
function useAsyncQuery(queryFn, options = {}) {
  const {
    enabled = true,
    refetchOnWindowFocus = false,
    ...asyncOptions
  } = options;
  const { execute, ...rest } = useAsync(queryFn, {
    ...asyncOptions,
    executeOnMount: enabled
  });
  useEffect3(() => {
    if (!refetchOnWindowFocus) return;
    const handleFocus = () => {
      if (enabled && !rest.isLoading) {
        execute();
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [refetchOnWindowFocus, enabled, rest.isLoading, execute]);
  return {
    ...rest,
    refetch: execute
  };
}
function useAsyncMutation(mutationFn, options = {}) {
  const { execute, ...rest } = useAsync(mutationFn, {
    ...options,
    executeOnMount: false
  });
  return {
    ...rest,
    mutate: execute,
    mutateAsync: execute
  };
}

// src/hooks/useProfiles.ts
import { useState as useState4, useEffect as useEffect4, useCallback as useCallback4, useMemo as useMemo4 } from "react";
import { useTranslation } from "react-i18next";
var ProfilesAPI = class {
  baseUrl = "http://localhost:3001/api/v1/users";
  async makeRequest(endpoint, options = {}) {
    const token = localStorage.getItem("glowp_access_token");
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...token && { "Authorization": `Bearer ${token}` },
        ...options.headers
      }
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Network error" }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }
    return response.json();
  }
  // GET /api/v1/users/profiles - VOTRE ROUTE EXISTANTE
  async getFamilyProfiles(params = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set("page", params.page.toString());
    if (params.limit) query.set("limit", params.limit.toString());
    if (params.profile_type) query.set("profile_type", params.profile_type);
    const response = await this.makeRequest(`/profiles?${query}`);
    return response.data;
  }
  // POST /api/v1/users/profiles - VOTRE ROUTE EXISTANTE
  async createFamilyProfile(profileData) {
    const response = await this.makeRequest("/profiles", {
      method: "POST",
      body: JSON.stringify(profileData)
    });
    return response.data.profile;
  }
  // PUT /api/v1/users/profiles/:profileId - VOTRE ROUTE EXISTANTE
  async updateFamilyProfile(profileId, updates) {
    const response = await this.makeRequest(`/profiles/${profileId}`, {
      method: "PUT",
      body: JSON.stringify(updates)
    });
    return response.data.profile;
  }
  // DELETE /api/v1/users/profiles/:profileId - VOTRE ROUTE EXISTANTE
  async deleteFamilyProfile(profileId) {
    await this.makeRequest(`/profiles/${profileId}`, {
      method: "DELETE"
    });
  }
  // GET /api/v1/users/profiles/:parentId/children - VOTRE ROUTE EXISTANTE
  async getChildrenProfiles(parentId) {
    const response = await this.makeRequest(`/profiles/${parentId}/children`);
    return response.data.children;
  }
};
function useProfiles() {
  const { t } = useTranslation("common");
  const api = useMemo4(() => new ProfilesAPI(), []);
  const [state, setState] = useState4({
    profiles: [],
    activeProfileId: null,
    isLoading: false,
    error: null,
    hasUnsavedChanges: false
  });
  const activeProfile = useMemo4(() => {
    return state.profiles.find((p) => p.id === state.activeProfileId) || null;
  }, [state.profiles, state.activeProfileId]);
  const childProfiles = useMemo4(() => {
    return state.profiles.filter((p) => p.profile_type === "CHILD");
  }, [state.profiles]);
  const parentProfile = useMemo4(() => {
    return state.profiles.find((p) => p.profile_type === "PARENT") || null;
  }, [state.profiles]);
  const loadProfiles = useCallback4(async (params = {}) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const data = await api.getFamilyProfiles(params);
      setState((prev) => ({
        ...prev,
        profiles: data.profiles,
        activeProfileId: prev.activeProfileId || data.profiles[0]?.id || null,
        isLoading: false
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        // 🌍 UTILISE VOS CLÉS DE TRADUCTION
        error: error instanceof Error ? error.message : t("profiles.loadError")
      }));
      throw error;
    }
  }, [api, t]);
  const createProfile = useCallback4(async (profileData) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const newProfile = await api.createFamilyProfile(profileData);
      setState((prev) => ({
        ...prev,
        profiles: [...prev.profiles, newProfile],
        activeProfileId: newProfile.id,
        isLoading: false
      }));
      return newProfile.id;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        // 🌍 UTILISE VOS CLÉS DE TRADUCTION
        error: error instanceof Error ? error.message : t("profiles.createError")
      }));
      throw error;
    }
  }, [api, t]);
  const switchProfile = useCallback4(async (profileId) => {
    const profile = state.profiles.find((p) => p.id === profileId);
    if (!profile) {
      throw new Error(t("profiles.profileNotFound"));
    }
    setState((prev) => ({
      ...prev,
      activeProfileId: profileId
    }));
  }, [state.profiles, t]);
  useEffect4(() => {
    loadProfiles();
  }, [loadProfiles]);
  return {
    // État
    profiles: state.profiles,
    activeProfile,
    childProfiles,
    parentProfile,
    isLoading: state.isLoading,
    error: state.error,
    // Actions CRUD connectées à votre API
    loadProfiles,
    createProfile,
    switchProfile,
    // État helper avec vos clés i18n
    hasProfiles: state.profiles.length > 0,
    hasActiveProfile: !!activeProfile,
    // 🌍 UTILISE VOS CLÉS DE TRADUCTION
    activeProfileName: activeProfile?.profile_name || t("navigation.profile"),
    activeProfileType: activeProfile?.profile_type || null
  };
}
var useProfiles_default = useProfiles;

// src/i18n/types/Locale.ts
var CURRENCIES = {
  "fr-FR": "EUR",
  "en-US": "USD",
  "de-DE": "EUR",
  "es-ES": "EUR",
  "it-IT": "EUR",
  "nl-NL": "EUR",
  "pl-PL": "PLN",
  "cs-CZ": "CZK",
  "lt-LT": "EUR"
};
var DATE_FORMATS = {
  "fr-FR": { dateStyle: "short" },
  "en-US": { dateStyle: "short" },
  "de-DE": { dateStyle: "short" },
  "es-ES": { dateStyle: "short" },
  "it-IT": { dateStyle: "short" },
  "nl-NL": { dateStyle: "short" },
  "pl-PL": { dateStyle: "short" },
  "cs-CZ": { dateStyle: "short" },
  "lt-LT": { dateStyle: "short" }
};

// src/utils/formatters.ts
function formatCurrency(amount, locale = "fr-FR", currency, options = {}) {
  const cur = currency ?? CURRENCIES[locale] ?? "EUR";
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency: cur, ...options }).format(amount);
  } catch {
    const fixed = Number.isFinite(amount) ? amount.toFixed(2) : String(amount);
    return `${fixed} ${cur}`;
  }
}
var formatPrice = (amount, locale = "fr-FR", showCurrency = true) => {
  if (!showCurrency) {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }
  return formatCurrency(amount, locale);
};
var formatNumber = (number, locale = "fr-FR") => {
  return new Intl.NumberFormat(locale).format(number);
};
var formatPercentage = (value, locale = "fr-FR", decimals = 1) => {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value / 100);
};
var formatFileSize = (bytes, locale = "fr-FR") => {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${new Intl.NumberFormat(locale, {
    maximumFractionDigits: 1
  }).format(size)} ${units[unitIndex]}`;
};
var formatDate = (date, locale = "fr-FR", style = "medium") => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const options = {
    short: { dateStyle: "short" },
    medium: { dateStyle: "medium" },
    long: { dateStyle: "long" },
    full: { dateStyle: "full" }
  };
  return new Intl.DateTimeFormat(locale, options[style]).format(dateObj);
};
var formatDateTime = (date, locale = "fr-FR") => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(dateObj);
};
var formatRelativeTime = (date, locale = "fr-FR") => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const now = /* @__PURE__ */ new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1e3);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (diffInSeconds < 60) {
    return rtf.format(-diffInSeconds, "second");
  } else if (diffInSeconds < 3600) {
    return rtf.format(-Math.floor(diffInSeconds / 60), "minute");
  } else if (diffInSeconds < 86400) {
    return rtf.format(-Math.floor(diffInSeconds / 3600), "hour");
  } else if (diffInSeconds < 2592e3) {
    return rtf.format(-Math.floor(diffInSeconds / 86400), "day");
  } else if (diffInSeconds < 31536e3) {
    return rtf.format(-Math.floor(diffInSeconds / 2592e3), "month");
  } else {
    return rtf.format(-Math.floor(diffInSeconds / 31536e3), "year");
  }
};
var slugify = (text) => {
  return text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").replace(/-+/g, "-");
};
var truncateText = (text, maxLength, suffix = "...") => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - suffix.length) + suffix;
};

// src/utils/preferences.ts
function generateBrandSlug(name) {
  return name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
var CORE_BRANDS_DATA = [
  // ====================== LUXURY ======================
  // 🇫🇷 France — Luxe
  { name: "Chanel", tier: "luxury", styles: ["chic", "classic"] },
  { name: "Christian Dior", tier: "luxury", styles: ["chic", "romantic"] },
  { name: "Louis Vuitton", tier: "luxury", styles: ["chic", "classic"] },
  { name: "Herm\xE8s", tier: "luxury", styles: ["chic", "classic"] },
  { name: "Yves Saint Laurent", tier: "luxury", styles: ["chic", "rock"] },
  { name: "Givenchy", tier: "luxury", styles: ["chic", "streetwear"] },
  { name: "Balmain", tier: "luxury", styles: ["chic", "rock"] },
  { name: "Saint Laurent", tier: "luxury", styles: ["chic", "rock"] },
  { name: "Celine", tier: "luxury", styles: ["minimal", "chic"] },
  {
    name: "Maison Margiela",
    tier: "luxury",
    styles: ["avant-garde", "minimal"]
  },
  {
    name: "MM6 Maison Margiela",
    tier: "luxury",
    styles: ["avant-garde", "casual"]
  },
  { name: "Lanvin", tier: "luxury", styles: ["chic", "classic"] },
  {
    name: "Jean Paul Gaultier",
    tier: "luxury",
    styles: ["avant-garde", "rock"]
  },
  { name: "Thierry Mugler", tier: "luxury", styles: ["avant-garde", "chic"] },
  { name: "Pierre Cardin", tier: "luxury", styles: ["classic", "avant-garde"] },
  { name: "Courr\xE8ges", tier: "luxury", styles: ["minimal", "avant-garde"] },
  { name: "Chlo\xE9", tier: "luxury", styles: ["boheme", "romantic"] },
  {
    name: "Dries Van Noten",
    tier: "luxury",
    styles: ["avant-garde", "boheme"]
  },
  { name: "Lemaire", tier: "luxury", styles: ["minimal", "chic"] },
  // 🇮🇹 Italie — Luxe
  { name: "Gucci", tier: "luxury", styles: ["chic", "streetwear"] },
  { name: "Prada", tier: "luxury", styles: ["minimal", "chic"] },
  { name: "Versace", tier: "luxury", styles: ["chic", "rock"] },
  { name: "Dolce & Gabbana", tier: "luxury", styles: ["chic", "romantic"] },
  { name: "Armani", tier: "luxury", styles: ["minimal", "classic"] },
  { name: "Emporio Armani", tier: "luxury", styles: ["minimal", "classic"] },
  { name: "Valentino", tier: "luxury", styles: ["chic", "romantic"] },
  { name: "Fendi", tier: "luxury", styles: ["chic", "classic"] },
  { name: "Bottega Veneta", tier: "luxury", styles: ["minimal", "chic"] },
  { name: "Moncler", tier: "luxury", styles: ["sporty", "chic"] },
  {
    name: "Brunello Cucinelli",
    tier: "luxury",
    styles: ["minimal", "classic"]
  },
  { name: "Loro Piana", tier: "luxury", styles: ["classic", "minimal"] },
  { name: "Marni", tier: "luxury", styles: ["avant-garde", "boheme"] },
  { name: "Etro", tier: "luxury", styles: ["boheme", "classic"] },
  { name: "Missoni", tier: "luxury", styles: ["boheme", "chic"] },
  { name: "Roberto Cavalli", tier: "luxury", styles: ["chic", "rock"] },
  { name: "Moschino", tier: "luxury", styles: ["streetwear", "avant-garde"] },
  { name: "Max Mara", tier: "luxury", styles: ["classic", "minimal"] },
  { name: "Salvatore Ferragamo", tier: "luxury", styles: ["classic", "chic"] },
  { name: "Golden Goose", tier: "luxury", styles: ["casual", "vintage"] },
  { name: "Stone Island", tier: "luxury", styles: ["streetwear", "sporty"] },
  { name: "Hogan", tier: "luxury", styles: ["casual", "classic"] },
  { name: "Tod's", tier: "luxury", styles: ["classic", "chic"] },
  // 🇬🇧 UK — Luxe
  { name: "Burberry", tier: "luxury", styles: ["classic", "chic"] },
  {
    name: "Alexander McQueen",
    tier: "luxury",
    styles: ["avant-garde", "rock"]
  },
  { name: "Stella McCartney", tier: "luxury", styles: ["minimal", "chic"] },
  {
    name: "Vivienne Westwood",
    tier: "luxury",
    styles: ["avant-garde", "rock"]
  },
  { name: "JW Anderson", tier: "luxury", styles: ["avant-garde", "minimal"] },
  { name: "Mulberry", tier: "luxury", styles: ["classic", "chic"] },
  { name: "Christopher Kane", tier: "luxury", styles: ["avant-garde", "chic"] },
  { name: "Roksanda", tier: "luxury", styles: ["chic", "avant-garde"] },
  { name: "Erdem", tier: "luxury", styles: ["romantic", "chic"] },
  { name: "Richard Quinn", tier: "luxury", styles: ["avant-garde", "rock"] },
  { name: "Aquascutum", tier: "luxury", styles: ["classic", "chic"] },
  {
    name: "Pringle of Scotland",
    tier: "luxury",
    styles: ["classic", "preppy"]
  },
  { name: "Dunhill", tier: "luxury", styles: ["classic", "chic"] },
  // 🇪🇸 Espagne — Luxe
  { name: "Loewe", tier: "luxury", styles: ["minimal", "chic"] },
  { name: "Palomo Spain", tier: "luxury", styles: ["avant-garde", "romantic"] },
  // 🇩🇪 Allemagne — Luxe
  { name: "Hugo Boss", tier: "luxury", styles: ["classic", "minimal"] },
  { name: "Jil Sander", tier: "luxury", styles: ["minimal", "chic"] },
  { name: "MCM", tier: "luxury", styles: ["streetwear", "chic"] },
  { name: "Escada", tier: "luxury", styles: ["chic", "classic"] },
  // 🇺🇸 USA — Luxe
  { name: "Tom Ford", tier: "luxury", styles: ["chic", "classic"] },
  {
    name: "Ralph Lauren Purple Label",
    tier: "luxury",
    styles: ["classic", "preppy"]
  },
  { name: "Calvin Klein", tier: "luxury", styles: ["minimal", "casual"] },
  { name: "Marc Jacobs", tier: "luxury", styles: ["chic", "streetwear"] },
  { name: "Carolina Herrera", tier: "luxury", styles: ["chic", "romantic"] },
  { name: "Oscar de la Renta", tier: "luxury", styles: ["chic", "romantic"] },
  { name: "Vera Wang", tier: "luxury", styles: ["romantic", "chic"] },
  { name: "Jason Wu", tier: "luxury", styles: ["chic", "minimal"] },
  { name: "Diane von Furstenberg", tier: "luxury", styles: ["chic", "boheme"] },
  { name: "Off-White", tier: "luxury", styles: ["streetwear", "avant-garde"] },
  { name: "Amiri", tier: "luxury", styles: ["rock", "streetwear"] },
  { name: "Fear of God", tier: "luxury", styles: ["streetwear", "minimal"] },
  { name: "Rick Owens", tier: "luxury", styles: ["avant-garde", "rock"] },
  { name: "The Row", tier: "luxury", styles: ["minimal", "chic"] },
  { name: "Rhude", tier: "luxury", styles: ["streetwear", "casual"] },
  { name: "Palm Angels", tier: "luxury", styles: ["streetwear", "sporty"] },
  { name: "Chrome Hearts", tier: "luxury", styles: ["rock", "streetwear"] },
  { name: "Gallery Dept", tier: "luxury", styles: ["streetwear", "vintage"] },
  { name: "MSGM", tier: "luxury", styles: ["streetwear", "chic"] },
  // 🇯🇵 Japon — Luxe
  { name: "Comme des Gar\xE7ons", tier: "luxury", styles: ["avant-garde"] },
  { name: "Yohji Yamamoto", tier: "luxury", styles: ["avant-garde", "rock"] },
  { name: "Issey Miyake", tier: "luxury", styles: ["avant-garde", "minimal"] },
  { name: "Junya Watanabe", tier: "luxury", styles: ["avant-garde"] },
  { name: "Sacai", tier: "luxury", styles: ["avant-garde", "streetwear"] },
  { name: "Undercover", tier: "luxury", styles: ["avant-garde", "rock"] },
  { name: "Mastermind Japan", tier: "luxury", styles: ["streetwear", "rock"] },
  // 🇦🇺 Australie — Luxe
  { name: "Zimmermann", tier: "luxury", styles: ["romantic", "boheme"] },
  { name: "Dion Lee", tier: "luxury", styles: ["minimal", "chic"] },
  { name: "Ellery", tier: "luxury", styles: ["chic", "minimal"] },
  // Suisse/Autriche — Luxe
  { name: "Bally", tier: "luxury", styles: ["classic", "chic"] },
  { name: "Akris", tier: "luxury", styles: ["minimal", "chic"] },
  // Divers — Luxe
  { name: "Canada Goose", tier: "luxury", styles: ["outdoor", "sporty"] },
  { name: "Mackage", tier: "luxury", styles: ["chic", "classic"] },
  { name: "Moose Knuckles", tier: "luxury", styles: ["outdoor", "sporty"] },
  // ====================== PREMIUM ======================
  // 🇫🇷 France — Premium
  { name: "Jacquemus", tier: "premium", styles: ["chic", "minimal"] },
  { name: "AMI Paris", tier: "premium", styles: ["casual", "chic"] },
  { name: "Kenzo", tier: "premium", styles: ["streetwear", "chic"] },
  { name: "Isabel Marant", tier: "premium", styles: ["boheme", "rock"] },
  { name: "S\xE9zane", tier: "premium", styles: ["romantic", "chic"] },
  { name: "Rouje", tier: "premium", styles: ["romantic", "vintage"] },
  { name: "Ba&sh", tier: "premium", styles: ["boheme", "casual"] },
  { name: "Maje", tier: "premium", styles: ["chic", "rock"] },
  { name: "Sandro", tier: "premium", styles: ["chic", "casual"] },
  { name: "Claudie Pierlot", tier: "premium", styles: ["chic", "romantic"] },
  { name: "APC", tier: "premium", styles: ["minimal", "casual"] },
  { name: "The Kooples", tier: "premium", styles: ["rock", "chic"] },
  { name: "Zadig & Voltaire", tier: "premium", styles: ["rock", "casual"] },
  { name: "Iro", tier: "premium", styles: ["rock", "chic"] },
  { name: "Agn\xE8s B", tier: "premium", styles: ["minimal", "casual"] },
  { name: "Vanessa Bruno", tier: "premium", styles: ["boheme", "chic"] },
  { name: "Sessun", tier: "premium", styles: ["boheme", "vintage"] },
  { name: "V\xE9ronique Leroy", tier: "premium", styles: ["chic", "minimal"] },
  {
    name: "Comptoir des Cotonniers",
    tier: "premium",
    styles: ["casual", "chic"]
  },
  { name: "Des Petits Hauts", tier: "premium", styles: ["romantic", "boheme"] },
  { name: "Gat Rimon", tier: "premium", styles: ["boheme", "chic"] },
  { name: "Pablo", tier: "premium", styles: ["chic", "minimal"] },
  { name: "Soeur", tier: "premium", styles: ["minimal", "casual"] },
  { name: "Tara Jarmon", tier: "premium", styles: ["chic", "classic"] },
  { name: "Antik Batik", tier: "premium", styles: ["boheme", "vintage"] },
  { name: "Berenice", tier: "premium", styles: ["boheme", "casual"] },
  { name: "Manoush", tier: "premium", styles: ["boheme", "romantic"] },
  { name: "Heimstone", tier: "premium", styles: ["rock", "boheme"] },
  { name: "Essentiel Antwerp", tier: "premium", styles: ["chic", "boheme"] },
  { name: "American Vintage", tier: "premium", styles: ["casual", "minimal"] },
  // 🇮🇹 Italie — Premium
  { name: "Dondup", tier: "premium", styles: ["casual", "chic"] },
  { name: "Diesel", tier: "premium", styles: ["rock", "casual"] },
  { name: "Liu Jo", tier: "premium", styles: ["chic", "romantic"] },
  { name: "Pinko", tier: "premium", styles: ["chic", "rock"] },
  { name: "Max&Co", tier: "premium", styles: ["chic", "casual"] },
  { name: "Patrizia Pepe", tier: "premium", styles: ["chic", "rock"] },
  { name: "Paolo Pecora", tier: "premium", styles: ["classic", "casual"] },
  { name: "Luisa Spagnoli", tier: "premium", styles: ["chic", "classic"] },
  { name: "Department 5", tier: "premium", styles: ["casual", "vintage"] },
  { name: "Siviglia", tier: "premium", styles: ["casual", "vintage"] },
  { name: "Gas", tier: "premium", styles: ["casual", "rock"] },
  { name: "Stefanel", tier: "premium", styles: ["minimal", "classic"] },
  { name: "Gcds", tier: "premium", styles: ["streetwear", "avant-garde"] },
  { name: "Benetton", tier: "premium", styles: ["casual", "classic"] },
  // 🇬🇧 UK — Premium
  { name: "Paul Smith", tier: "premium", styles: ["classic", "chic"] },
  { name: "Barbour", tier: "premium", styles: ["classic", "outdoor"] },
  { name: "Fred Perry", tier: "premium", styles: ["preppy", "rock"] },
  { name: "Ted Baker", tier: "premium", styles: ["chic", "preppy"] },
  { name: "Reiss", tier: "premium", styles: ["chic", "classic"] },
  { name: "All Saints", tier: "premium", styles: ["rock", "casual"] },
  { name: "Dr. Martens", tier: "premium", styles: ["rock", "casual"] },
  { name: "Ben Sherman", tier: "premium", styles: ["preppy", "classic"] },
  { name: "Karen Millen", tier: "premium", styles: ["chic", "classic"] },
  { name: "Whistles", tier: "premium", styles: ["minimal", "chic"] },
  { name: "Jigsaw", tier: "premium", styles: ["classic", "casual"] },
  { name: "Joseph", tier: "premium", styles: ["minimal", "chic"] },
  { name: "LK Bennett", tier: "premium", styles: ["chic", "classic"] },
  { name: "Hobbs", tier: "premium", styles: ["chic", "classic"] },
  { name: "Phase Eight", tier: "premium", styles: ["chic", "romantic"] },
  { name: "Toast", tier: "premium", styles: ["minimal", "boheme"] },
  { name: "Brora", tier: "premium", styles: ["classic", "casual"] },
  { name: "Pretty Green", tier: "premium", styles: ["rock", "casual"] },
  { name: "Hunter", tier: "premium", styles: ["outdoor", "classic"] },
  { name: "Margaret Howell", tier: "premium", styles: ["minimal", "classic"] },
  { name: "Seasalt Cornwall", tier: "premium", styles: ["casual", "boheme"] },
  // 🇪🇸 Espagne — Premium
  { name: "Desigual", tier: "premium", styles: ["boheme", "casual"] },
  { name: "Custo Barcelona", tier: "premium", styles: ["casual", "rock"] },
  { name: "Miriam Ponsa", tier: "premium", styles: ["avant-garde", "chic"] },
  { name: "Teresa Helbig", tier: "premium", styles: ["chic", "romantic"] },
  {
    name: "Agatha Ruiz de la Prada",
    tier: "premium",
    styles: ["boheme", "enfant"]
  },
  { name: "Casta\xF1er", tier: "premium", styles: ["casual", "boheme"] },
  { name: "Pretty Ballerinas", tier: "premium", styles: ["chic", "classic"] },
  { name: "Camper", tier: "premium", styles: ["casual", "minimal"] },
  // 🇸🇪 Scandinavie — Premium
  { name: "Acne Studios", tier: "premium", styles: ["minimal", "rock"] },
  { name: "Filippa K", tier: "premium", styles: ["minimal", "classic"] },
  { name: "Ganni", tier: "premium", styles: ["casual", "chic"] },
  { name: "COS", tier: "premium", styles: ["minimal", "classic"] },
  { name: "Tiger of Sweden", tier: "premium", styles: ["classic", "minimal"] },
  { name: "J.Lindeberg", tier: "premium", styles: ["preppy", "sporty"] },
  { name: "Baum und Pferdgarten", tier: "premium", styles: ["boheme", "chic"] },
  { name: "Stine Goya", tier: "premium", styles: ["boheme", "romantic"] },
  {
    name: "Rotate Birger Christensen",
    tier: "premium",
    styles: ["chic", "rock"]
  },
  { name: "By Malene Birger", tier: "premium", styles: ["chic", "minimal"] },
  { name: "Norse Projects", tier: "premium", styles: ["minimal", "casual"] },
  { name: "Our Legacy", tier: "premium", styles: ["minimal", "vintage"] },
  { name: "Nanushka", tier: "premium", styles: ["minimal", "chic"] },
  { name: "Hope", tier: "premium", styles: ["minimal", "casual"] },
  { name: "Whyred", tier: "premium", styles: ["minimal", "rock"] },
  { name: "Dagmar", tier: "premium", styles: ["minimal", "chic"] },
  { name: "Rodebjer", tier: "premium", styles: ["boheme", "minimal"] },
  { name: "Wood Wood", tier: "premium", styles: ["streetwear", "casual"] },
  { name: "Marimekko", tier: "premium", styles: ["casual", "boheme"] },
  { name: "Stutterheim", tier: "premium", styles: ["minimal", "outdoor"] },
  { name: "Sandqvist", tier: "premium", styles: ["minimal", "casual"] },
  { name: "Rains", tier: "premium", styles: ["minimal", "casual"] },
  // 🇳🇱 Pays-Bas/Belgique — Premium
  { name: "Scotch & Soda", tier: "premium", styles: ["casual", "vintage"] },
  { name: "DENHAM", tier: "premium", styles: ["casual", "vintage"] },
  { name: "G-Star RAW", tier: "premium", styles: ["casual", "streetwear"] },
  {
    name: "Ann Demeulemeester",
    tier: "premium",
    styles: ["avant-garde", "rock"]
  },
  { name: "A.F. Vandevorst", tier: "premium", styles: ["avant-garde", "rock"] },
  { name: "Kings of Indigo", tier: "premium", styles: ["casual", "vintage"] },
  // 🇩🇪 Allemagne — Premium
  { name: "Closed", tier: "premium", styles: ["casual", "minimal"] },
  { name: "Drykorn", tier: "premium", styles: ["minimal", "classic"] },
  { name: "Bogner", tier: "premium", styles: ["sporty", "classic"] },
  { name: "Marc Cain", tier: "premium", styles: ["chic", "classic"] },
  { name: "Birkenstock", tier: "premium", styles: ["casual", "minimal"] },
  { name: "Laur\xE8l", tier: "premium", styles: ["chic", "classic"] },
  { name: "Strenesse", tier: "premium", styles: ["minimal", "classic"] },
  { name: "Wolford", tier: "premium", styles: ["chic", "minimal"] },
  // 🇺🇸 USA — Premium
  { name: "Ralph Lauren", tier: "premium", styles: ["classic", "preppy"] },
  { name: "Polo Ralph Lauren", tier: "premium", styles: ["classic", "preppy"] },
  { name: "Tommy Hilfiger", tier: "premium", styles: ["preppy", "casual"] },
  { name: "Michael Kors", tier: "premium", styles: ["chic", "classic"] },
  { name: "Kate Spade", tier: "premium", styles: ["chic", "preppy"] },
  { name: "Coach", tier: "premium", styles: ["classic", "casual"] },
  { name: "Tory Burch", tier: "premium", styles: ["classic", "boheme"] },
  { name: "Theory", tier: "premium", styles: ["minimal", "classic"] },
  {
    name: "Proenza Schouler",
    tier: "premium",
    styles: ["avant-garde", "chic"]
  },
  { name: "Alexander Wang", tier: "premium", styles: ["minimal", "rock"] },
  { name: "3.1 Phillip Lim", tier: "premium", styles: ["chic", "minimal"] },
  { name: "Phillip Lim", tier: "premium", styles: ["chic", "minimal"] },
  { name: "Rag & Bone", tier: "premium", styles: ["casual", "rock"] },
  { name: "Helmut Lang", tier: "premium", styles: ["minimal", "rock"] },
  { name: "Vince", tier: "premium", styles: ["minimal", "casual"] },
  { name: "James Perse", tier: "premium", styles: ["casual", "minimal"] },
  { name: "Mansur Gavriel", tier: "premium", styles: ["minimal", "chic"] },
  { name: "Cult Gaia", tier: "premium", styles: ["boheme", "chic"] },
  { name: "Staud", tier: "premium", styles: ["chic", "casual"] },
  { name: "Simon Miller", tier: "premium", styles: ["minimal", "casual"] },
  { name: "John Elliott", tier: "premium", styles: ["streetwear", "minimal"] },
  { name: "Noah", tier: "premium", styles: ["streetwear", "preppy"] },
  { name: "Madewell", tier: "premium", styles: ["casual", "vintage"] },
  { name: "Kit and Ace", tier: "premium", styles: ["minimal", "casual"] },
  { name: "Reformation", tier: "premium", styles: ["romantic", "vintage"] },
  { name: "Smythe", tier: "premium", styles: ["classic", "chic"] },
  {
    name: "Opening Ceremony",
    tier: "premium",
    styles: ["streetwear", "avant-garde"]
  },
  { name: "Everlane", tier: "premium", styles: ["minimal", "casual"] },
  // 🇺🇸 USA — Denim Premium
  {
    name: "Citizens of Humanity",
    tier: "premium",
    styles: ["casual", "vintage"]
  },
  { name: "Frame", tier: "premium", styles: ["casual", "chic"] },
  { name: "Paige", tier: "premium", styles: ["casual", "chic"] },
  { name: "AG Jeans", tier: "premium", styles: ["casual", "classic"] },
  { name: "Mother", tier: "premium", styles: ["casual", "vintage"] },
  { name: "DL1961", tier: "premium", styles: ["casual", "classic"] },
  { name: "J Brand", tier: "premium", styles: ["casual", "minimal"] },
  { name: "Joe's Jeans", tier: "premium", styles: ["casual", "classic"] },
  { name: "True Religion", tier: "premium", styles: ["casual", "rock"] },
  { name: "7 For All Mankind", tier: "premium", styles: ["casual", "classic"] },
  { name: "Nudie Jeans", tier: "premium", styles: ["casual", "rock"] },
  // 🇯🇵 Japon — Premium
  { name: "Visvim", tier: "premium", styles: ["boheme", "vintage"] },
  { name: "Neighborhood", tier: "premium", styles: ["streetwear", "rock"] },
  {
    name: "White Mountaineering",
    tier: "premium",
    styles: ["outdoor", "streetwear"]
  },
  { name: "Kapital", tier: "premium", styles: ["boheme", "vintage"] },
  { name: "Needles", tier: "premium", styles: ["vintage", "streetwear"] },
  {
    name: "Engineered Garments",
    tier: "premium",
    styles: ["casual", "vintage"]
  },
  { name: "Nanamica", tier: "premium", styles: ["outdoor", "minimal"] },
  { name: "Snow Peak", tier: "premium", styles: ["outdoor", "minimal"] },
  {
    name: "Fragment Design",
    tier: "premium",
    styles: ["streetwear", "minimal"]
  },
  { name: "Human Made", tier: "premium", styles: ["streetwear", "vintage"] },
  { name: "Wacko Maria", tier: "premium", styles: ["rock", "vintage"] },
  { name: "Wtaps", tier: "premium", styles: ["streetwear", "rock"] },
  { name: "Sophnet", tier: "premium", styles: ["streetwear", "minimal"] },
  { name: "F.C.R.B.", tier: "premium", styles: ["sporty", "streetwear"] },
  { name: "Porter", tier: "premium", styles: ["minimal", "casual"] },
  { name: "Beams", tier: "premium", styles: ["casual", "preppy"] },
  { name: "United Arrows", tier: "premium", styles: ["casual", "classic"] },
  { name: "Ships", tier: "premium", styles: ["classic", "casual"] },
  { name: "Journal Standard", tier: "premium", styles: ["casual", "vintage"] },
  { name: "Beauty & Youth", tier: "premium", styles: ["casual", "preppy"] },
  { name: "45rpm", tier: "premium", styles: ["vintage", "boheme"] },
  { name: "Evisu", tier: "premium", styles: ["streetwear", "vintage"] },
  // 🇰🇷 Corée — Premium
  {
    name: "Ader Error",
    tier: "premium",
    styles: ["streetwear", "avant-garde"]
  },
  { name: "We11done", tier: "premium", styles: ["streetwear", "avant-garde"] },
  {
    name: "Andersson Bell",
    tier: "premium",
    styles: ["casual", "avant-garde"]
  },
  {
    name: "Gentle Monster",
    tier: "premium",
    styles: ["avant-garde", "minimal"]
  },
  { name: "87mm", tier: "premium", styles: ["casual", "minimal"] },
  { name: "Wooyoungmi", tier: "premium", styles: ["minimal", "chic"] },
  { name: "Juun.J", tier: "premium", styles: ["avant-garde", "streetwear"] },
  { name: "Pushbutton", tier: "premium", styles: ["avant-garde", "rock"] },
  { name: "Low Classic", tier: "premium", styles: ["minimal", "chic"] },
  { name: "Hyein Seo", tier: "premium", styles: ["streetwear", "avant-garde"] },
  { name: "Minju Kim", tier: "premium", styles: ["romantic", "chic"] },
  // 🇦🇺 Australie — Premium
  { name: "Ksubi", tier: "premium", styles: ["rock", "streetwear"] },
  { name: "Sass & Bide", tier: "premium", styles: ["rock", "chic"] },
  { name: "Gorman", tier: "premium", styles: ["boheme", "casual"] },
  { name: "Country Road", tier: "premium", styles: ["classic", "casual"] },
  { name: "Witchery", tier: "premium", styles: ["chic", "casual"] },
  {
    name: "Spell & The Gypsy Collective",
    tier: "premium",
    styles: ["boheme", "romantic"]
  },
  { name: "Bec + Bridge", tier: "premium", styles: ["chic", "minimal"] },
  { name: "Bassike", tier: "premium", styles: ["minimal", "casual"] },
  { name: "Matteau", tier: "premium", styles: ["minimal", "casual"] },
  {
    name: "Faithfull The Brand",
    tier: "premium",
    styles: ["boheme", "romantic"]
  },
  { name: "Sir The Label", tier: "premium", styles: ["minimal", "chic"] },
  { name: "Suboo", tier: "premium", styles: ["boheme", "casual"] },
  { name: "Significant Other", tier: "premium", styles: ["romantic", "chic"] },
  { name: "Camilla", tier: "premium", styles: ["boheme", "chic"] },
  { name: "P.E Nation", tier: "premium", styles: ["sporty", "streetwear"] },
  // Divers — Premium
  { name: "Falke", tier: "premium", styles: ["classic", "sporty"] },
  { name: "Palmers", tier: "premium", styles: ["casual", "classic"] },
  { name: "Sportalm", tier: "premium", styles: ["sporty", "chic"] },
  { name: "Loden Frey", tier: "premium", styles: ["classic", "outdoor"] },
  { name: "Freitag", tier: "premium", styles: ["casual", "streetwear"] },
  { name: "Wales Bonner", tier: "premium", styles: ["chic", "streetwear"] },
  {
    name: "Pharrell Williams",
    tier: "premium",
    styles: ["streetwear", "casual"]
  },
  // ====================== MID ======================
  // 🇫🇷 France — Mid
  { name: "Promod", tier: "mid", styles: ["casual", "chic"] },
  { name: "Pimkie", tier: "budget", styles: ["casual", "chic"] },
  { name: "Cache Cache", tier: "budget", styles: ["casual", "romantic"] },
  { name: "Morgan", tier: "mid", styles: ["chic", "rock"] },
  { name: "Etam", tier: "mid", styles: ["casual", "romantic"] },
  { name: "La Redoute", tier: "mid", styles: ["casual", "classic"] },
  { name: "G\xE9mo", tier: "budget", styles: ["casual"] },
  { name: "Kiabi", tier: "budget", styles: ["casual"] },
  { name: "Cama\xEFeu", tier: "budget", styles: ["casual", "classic"] },
  { name: "Somewhere", tier: "mid", styles: ["casual", "boheme"] },
  { name: "Bash", tier: "mid", styles: ["boheme", "casual"] },
  { name: "Jennyfer", tier: "budget", styles: ["casual", "streetwear"] },
  { name: "Tati", tier: "budget", styles: ["casual"] },
  { name: "In Extenso", tier: "budget", styles: ["casual"] },
  { name: "Inextenso", tier: "budget", styles: ["casual"] },
  { name: "Zeeman", tier: "budget", styles: ["casual"] },
  // 🇪🇸 Espagne — Mid
  { name: "Zara", tier: "mid", styles: ["chic", "casual"] },
  { name: "Zara Home", tier: "mid", styles: ["casual"] },
  { name: "Zara Kids", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Pull & Bear", tier: "mid", styles: ["casual", "streetwear"] },
  { name: "Bershka", tier: "mid", styles: ["casual", "streetwear"] },
  { name: "Stradivarius", tier: "mid", styles: ["casual", "chic"] },
  { name: "Massimo Dutti", tier: "mid", styles: ["classic", "chic"] },
  { name: "Lefties", tier: "budget", styles: ["casual"] },
  { name: "Uterq\xFCe", tier: "mid", styles: ["chic", "casual"] },
  { name: "Mango", tier: "mid", styles: ["chic", "casual"] },
  { name: "Mango Kids", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Women'secret", tier: "mid", styles: ["casual", "romantic"] },
  { name: "Oysho", tier: "mid", styles: ["sporty", "casual"] },
  { name: "Cortefiel", tier: "mid", styles: ["classic", "casual"] },
  { name: "Springfield", tier: "mid", styles: ["casual", "preppy"] },
  // 🇸🇪 Scandinavie — Mid
  { name: "H&M", tier: "mid", styles: ["casual", "chic"] },
  { name: "H&M Kids", tier: "mid", styles: ["casual", "enfant"] },
  { name: "H&M Home", tier: "mid", styles: ["casual"] },
  { name: "Weekday", tier: "mid", styles: ["casual", "minimal"] },
  { name: "Monki", tier: "mid", styles: ["casual", "streetwear"] },
  { name: "Cheap Monday", tier: "budget", styles: ["rock", "casual"] },
  { name: "Arket", tier: "mid", styles: ["minimal", "casual"] },
  { name: "ARKET", tier: "mid", styles: ["minimal", "casual"] },
  { name: "Other Stories", tier: "mid", styles: ["chic", "minimal"] },
  // 🇩🇪 Allemagne — Mid
  { name: "Esprit", tier: "mid", styles: ["casual", "classic"] },
  { name: "Tom Tailor", tier: "mid", styles: ["casual", "classic"] },
  { name: "S.Oliver", tier: "mid", styles: ["casual", "classic"] },
  { name: "Marc O'Polo", tier: "mid", styles: ["casual", "classic"] },
  { name: "Betty Barclay", tier: "mid", styles: ["classic", "chic"] },
  { name: "Street One", tier: "mid", styles: ["casual"] },
  { name: "Gerry Weber", tier: "mid", styles: ["classic", "casual"] },
  { name: "Comma", tier: "mid", styles: ["chic", "classic"] },
  { name: "Brax", tier: "mid", styles: ["classic", "casual"] },
  { name: "Burlington", tier: "mid", styles: ["classic", "preppy"] },
  // 🇬🇧 UK — Mid
  { name: "Topshop", tier: "mid", styles: ["casual", "rock"] },
  { name: "ASOS", tier: "mid", styles: ["casual", "streetwear"] },
  { name: "Asos", tier: "mid", styles: ["casual", "streetwear"] },
  { name: "Primark", tier: "budget", styles: ["casual"] },
  { name: "Primark Kids", tier: "budget", styles: ["casual", "enfant"] },
  { name: "River Island", tier: "mid", styles: ["casual", "chic"] },
  { name: "New Look", tier: "mid", styles: ["casual", "chic"] },
  { name: "M&S", tier: "mid", styles: ["classic", "casual"] },
  { name: "Marks & Spencer", tier: "mid", styles: ["classic", "casual"] },
  { name: "Marks & Spencer Kids", tier: "mid", styles: ["classic", "enfant"] },
  { name: "Next", tier: "mid", styles: ["classic", "casual"] },
  { name: "Next Kids", tier: "mid", styles: ["classic", "enfant"] },
  { name: "Oasis", tier: "mid", styles: ["romantic", "chic"] },
  { name: "Warehouse", tier: "mid", styles: ["chic", "casual"] },
  { name: "Dorothy Perkins", tier: "mid", styles: ["casual", "chic"] },
  { name: "Miss Selfridge", tier: "mid", styles: ["casual", "chic"] },
  { name: "Fat Face", tier: "mid", styles: ["casual", "outdoor"] },
  { name: "White Stuff", tier: "mid", styles: ["casual", "boheme"] },
  { name: "Joules", tier: "mid", styles: ["casual", "preppy"] },
  { name: "Boden", tier: "mid", styles: ["casual", "preppy"] },
  { name: "Superdry", tier: "mid", styles: ["casual", "sporty"] },
  { name: "John Lewis", tier: "mid", styles: ["classic", "casual"] },
  { name: "John Lewis Kids", tier: "mid", styles: ["classic", "enfant"] },
  { name: "Debenhams", tier: "mid", styles: ["classic", "casual"] },
  // 🇺🇸 USA — Mid
  { name: "Gap", tier: "mid", styles: ["casual", "classic"] },
  { name: "GAP Kids", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Banana Republic", tier: "mid", styles: ["classic", "casual"] },
  { name: "Old Navy", tier: "budget", styles: ["casual"] },
  { name: "Old Navy Kids", tier: "budget", styles: ["casual", "enfant"] },
  { name: "J.Crew", tier: "mid", styles: ["preppy", "classic"] },
  { name: "Urban Outfitters", tier: "mid", styles: ["casual", "vintage"] },
  { name: "Anthropologie", tier: "mid", styles: ["boheme", "romantic"] },
  { name: "Free People", tier: "mid", styles: ["boheme", "romantic"] },
  { name: "Abercrombie & Fitch", tier: "mid", styles: ["casual", "preppy"] },
  { name: "Hollister", tier: "mid", styles: ["casual", "sporty"] },
  { name: "American Eagle", tier: "mid", styles: ["casual", "preppy"] },
  { name: "Aerie", tier: "mid", styles: ["casual", "sporty"] },
  { name: "Aeropostale", tier: "budget", styles: ["casual", "preppy"] },
  { name: "Express", tier: "mid", styles: ["chic", "casual"] },
  { name: "The Limited", tier: "budget", styles: ["classic", "casual"] },
  { name: "Lucky Brand", tier: "mid", styles: ["casual", "boheme"] },
  { name: "Aritzia", tier: "mid", styles: ["minimal", "casual"] },
  { name: "Uniqlo", tier: "mid", styles: ["minimal", "casual"] },
  { name: "Muji", tier: "mid", styles: ["minimal", "casual"] },
  { name: "Levi's", tier: "mid", styles: ["casual", "vintage"] },
  { name: "Wrangler", tier: "mid", styles: ["casual", "vintage"] },
  { name: "Lee", tier: "mid", styles: ["casual", "vintage"] },
  { name: "Replay", tier: "mid", styles: ["casual", "rock"] },
  { name: "Pepe Jeans", tier: "mid", styles: ["casual", "rock"] },
  { name: "Champion", tier: "mid", styles: ["sporty", "streetwear"] },
  { name: "Converse", tier: "mid", styles: ["casual", "streetwear"] },
  { name: "Vans", tier: "mid", styles: ["streetwear", "casual"] },
  { name: "Timberland", tier: "mid", styles: ["casual", "outdoor"] },
  { name: "Sperry", tier: "mid", styles: ["preppy", "casual"] },
  // USA — Fast Fashion (gardé sauf Shein)
  { name: "Forever 21", tier: "budget", styles: ["casual", "chic"] },
  { name: "Boohoo", tier: "budget", styles: ["casual", "chic"] },
  { name: "Pretty Little Thing", tier: "budget", styles: ["casual", "chic"] },
  { name: "Missguided", tier: "budget", styles: ["casual", "chic"] },
  { name: "Fashion Nova", tier: "budget", styles: ["casual", "chic"] },
  { name: "American Apparel", tier: "mid", styles: ["casual", "vintage"] },
  // 🇺🇸 USA — Stores
  { name: "Nordstrom", tier: "mid", styles: ["classic"] },
  { name: "Macy's", tier: "mid", styles: ["classic"] },
  { name: "Bloomingdale's", tier: "mid", styles: ["classic"] },
  { name: "Saks Fifth Avenue", tier: "mid", styles: ["chic"] },
  { name: "Neiman Marcus", tier: "mid", styles: ["chic"] },
  { name: "Barneys New York", tier: "mid", styles: ["chic"] },
  { name: "Dillard's", tier: "mid", styles: ["classic"] },
  { name: "JCPenney", tier: "budget", styles: ["casual"] },
  { name: "Kohl's", tier: "mid", styles: ["casual"] },
  { name: "TJ Maxx", tier: "budget", styles: ["casual"] },
  { name: "Marshalls", tier: "budget", styles: ["casual"] },
  { name: "Ross", tier: "budget", styles: ["casual"] },
  { name: "Target", tier: "budget", styles: ["casual"] },
  { name: "Target Kids", tier: "budget", styles: ["casual", "enfant"] },
  { name: "Costco", tier: "budget", styles: ["casual"] },
  { name: "Walmart", tier: "budget", styles: ["casual"] },
  // Divers — Mid
  { name: "Zalando", tier: "mid", styles: ["casual"] },
  { name: "About You", tier: "mid", styles: ["casual"] },
  // ====================== SPORT ======================
  // Grandes marques sport
  { name: "Nike", tier: "sport", styles: ["sporty", "streetwear"] },
  { name: "Adidas", tier: "sport", styles: ["sporty", "streetwear"] },
  { name: "Puma", tier: "sport", styles: ["sporty", "streetwear"] },
  { name: "Reebok", tier: "sport", styles: ["sporty", "casual"] },
  { name: "New Balance", tier: "sport", styles: ["sporty", "casual"] },
  { name: "Asics", tier: "sport", styles: ["sporty"] },
  { name: "Under Armour", tier: "sport", styles: ["sporty"] },
  { name: "Jordan", tier: "sport", styles: ["sporty", "streetwear"] },
  { name: "On Running", tier: "sport", styles: ["sporty", "minimal"] },
  { name: "Hoka One One", tier: "sport", styles: ["sporty"] },
  { name: "Saucony", tier: "sport", styles: ["sporty", "casual"] },
  { name: "Brooks", tier: "sport", styles: ["sporty"] },
  { name: "Mizuno", tier: "sport", styles: ["sporty"] },
  { name: "Fila", tier: "sport", styles: ["sporty", "vintage"] },
  { name: "Ellesse", tier: "sport", styles: ["sporty", "vintage"] },
  { name: "Sergio Tacchini", tier: "sport", styles: ["sporty", "vintage"] },
  { name: "Kappa", tier: "sport", styles: ["sporty", "streetwear"] },
  { name: "Le Coq Sportif", tier: "sport", styles: ["sporty", "casual"] },
  { name: "Diadora", tier: "sport", styles: ["sporty", "vintage"] },
  { name: "Lotto", tier: "sport", styles: ["sporty"] },
  { name: "Umbro", tier: "sport", styles: ["sporty", "casual"] },
  { name: "Hummel", tier: "sport", styles: ["sporty"] },
  { name: "Joma", tier: "sport", styles: ["sporty"] },
  { name: "Kempa", tier: "sport", styles: ["sporty"] },
  { name: "Salomon", tier: "sport", styles: ["sporty", "outdoor"] },
  { name: "Arc'teryx", tier: "sport", styles: ["outdoor", "sporty"] },
  { name: "The North Face", tier: "sport", styles: ["outdoor", "sporty"] },
  { name: "The North Face Kids", tier: "sport", styles: ["outdoor", "enfant"] },
  { name: "Patagonia", tier: "sport", styles: ["outdoor", "casual"] },
  { name: "Patagonia Kids", tier: "sport", styles: ["outdoor", "enfant"] },
  { name: "Columbia", tier: "sport", styles: ["outdoor", "casual"] },
  { name: "Columbia Kids", tier: "sport", styles: ["outdoor", "enfant"] },
  { name: "Merrell", tier: "sport", styles: ["outdoor", "sporty"] },
  { name: "Keen", tier: "sport", styles: ["outdoor", "casual"] },
  { name: "Teva", tier: "sport", styles: ["outdoor", "casual"] },
  { name: "Mountain Hardwear", tier: "sport", styles: ["outdoor"] },
  { name: "Outdoor Research", tier: "sport", styles: ["outdoor"] },
  { name: "Rab", tier: "sport", styles: ["outdoor"] },
  { name: "Montane", tier: "sport", styles: ["outdoor"] },
  { name: "Black Diamond", tier: "sport", styles: ["outdoor"] },
  { name: "Mammut", tier: "sport", styles: ["outdoor"] },
  { name: "Marmot", tier: "sport", styles: ["outdoor"] },
  { name: "Jack Wolfskin", tier: "sport", styles: ["outdoor", "casual"] },
  { name: "Vaude", tier: "sport", styles: ["outdoor"] },
  { name: "Berghaus", tier: "sport", styles: ["outdoor"] },
  { name: "Peak Performance", tier: "sport", styles: ["sporty", "outdoor"] },
  { name: "Hagl\xF6fs", tier: "sport", styles: ["outdoor"] },
  { name: "Fj\xE4llr\xE4ven", tier: "sport", styles: ["outdoor", "casual"] },
  { name: "Gymshark", tier: "sport", styles: ["sporty"] },
  { name: "Lululemon", tier: "sport", styles: ["sporty", "minimal"] },
  { name: "Alo Yoga", tier: "sport", styles: ["sporty", "minimal"] },
  { name: "Manduka", tier: "sport", styles: ["sporty"] },
  { name: "Beyond Yoga", tier: "sport", styles: ["sporty", "casual"] },
  { name: "Spiritual Gangster", tier: "sport", styles: ["sporty", "boheme"] },
  { name: "Onzie", tier: "sport", styles: ["sporty"] },
  { name: "Teeki", tier: "sport", styles: ["sporty", "boheme"] },
  {
    name: "Girlfriend Collective",
    tier: "sport",
    styles: ["sporty", "casual"]
  },
  { name: "Outdoor Voices", tier: "sport", styles: ["sporty", "casual"] },
  { name: "Splits59", tier: "sport", styles: ["sporty"] },
  { name: "Varley", tier: "sport", styles: ["sporty", "chic"] },
  { name: "Electric & Rose", tier: "sport", styles: ["sporty", "casual"] },
  { name: "Carbon38", tier: "sport", styles: ["sporty", "chic"] },
  { name: "Set Active", tier: "sport", styles: ["sporty", "casual"] },
  { name: "Year of Ours", tier: "sport", styles: ["sporty"] },
  { name: "Fabletics", tier: "sport", styles: ["sporty", "casual"] },
  { name: "Athleta", tier: "sport", styles: ["sporty", "casual"] },
  { name: "Sweaty Betty", tier: "sport", styles: ["sporty", "chic"] },
  { name: "Vuori", tier: "sport", styles: ["sporty", "casual"] },
  { name: "Roots", tier: "sport", styles: ["casual", "sporty"] },
  // Sport distributeurs
  { name: "Decathlon", tier: "sport", styles: ["sporty"] },
  { name: "Quechua", tier: "sport", styles: ["outdoor"] },
  { name: "Wedze", tier: "sport", styles: ["sporty"] },
  { name: "Domyos", tier: "sport", styles: ["sporty"] },
  { name: "Kipsta", tier: "sport", styles: ["sporty"] },
  { name: "Kalenji", tier: "sport", styles: ["sporty"] },
  { name: "Tribord", tier: "sport", styles: ["sporty", "outdoor"] },
  { name: "Forclaz", tier: "sport", styles: ["outdoor"] },
  { name: "Intersport", tier: "sport", styles: ["sporty"] },
  { name: "Sport 2000", tier: "sport", styles: ["sporty"] },
  { name: "Go Sport", tier: "sport", styles: ["sporty"] },
  { name: "JD Sports", tier: "sport", styles: ["sporty", "streetwear"] },
  { name: "Foot Locker", tier: "sport", styles: ["sporty", "streetwear"] },
  { name: "Finish Line", tier: "sport", styles: ["sporty"] },
  { name: "REI", tier: "sport", styles: ["outdoor"] },
  { name: "L.L.Bean", tier: "sport", styles: ["outdoor", "classic"] },
  { name: "Eddie Bauer", tier: "sport", styles: ["outdoor", "casual"] },
  { name: "Lands' End", tier: "sport", styles: ["casual", "classic"] },
  // Surf & Skate
  { name: "Quicksilver", tier: "sport", styles: ["sporty", "casual"] },
  { name: "Roxy", tier: "sport", styles: ["sporty", "casual"] },
  { name: "Billabong", tier: "sport", styles: ["sporty", "casual"] },
  { name: "Rip Curl", tier: "sport", styles: ["sporty", "casual"] },
  { name: "Volcom", tier: "sport", styles: ["streetwear", "casual"] },
  { name: "O'Neill", tier: "sport", styles: ["sporty", "casual"] },
  { name: "Element", tier: "sport", styles: ["streetwear", "casual"] },
  { name: "DC Shoes", tier: "sport", styles: ["streetwear", "sporty"] },
  { name: "Etnies", tier: "sport", styles: ["streetwear"] },
  { name: "Globe", tier: "sport", styles: ["streetwear"] },
  { name: "Hurley", tier: "sport", styles: ["sporty", "casual"] },
  { name: "RVCA", tier: "sport", styles: ["streetwear", "casual"] },
  // ====================== STREETWEAR ======================
  { name: "Supreme", tier: "premium", styles: ["streetwear"] },
  { name: "Stussy", tier: "premium", styles: ["streetwear", "casual"] },
  { name: "Bape", tier: "premium", styles: ["streetwear"] },
  { name: "A Bathing Ape", tier: "premium", styles: ["streetwear"] },
  { name: "Palace", tier: "premium", styles: ["streetwear"] },
  { name: "Kith", tier: "premium", styles: ["streetwear", "sporty"] },
  { name: "Carhartt", tier: "mid", styles: ["casual", "streetwear"] },
  { name: "Carhartt WIP", tier: "premium", styles: ["streetwear", "casual"] },
  { name: "Essentials", tier: "premium", styles: ["streetwear", "minimal"] },
  { name: "Aim\xE9 Leon Dore", tier: "premium", styles: ["streetwear", "preppy"] },
  { name: "Reigning Champ", tier: "premium", styles: ["sporty", "minimal"] },
  { name: "Wings + Horns", tier: "premium", styles: ["minimal", "streetwear"] },
  { name: "Anti Social Social Club", tier: "mid", styles: ["streetwear"] },
  { name: "Billionaire Boys Club", tier: "premium", styles: ["streetwear"] },
  { name: "Diamond Supply Co", tier: "mid", styles: ["streetwear"] },
  { name: "HUF", tier: "mid", styles: ["streetwear", "casual"] },
  { name: "Obey", tier: "mid", styles: ["streetwear"] },
  { name: "Thrasher", tier: "mid", styles: ["streetwear", "rock"] },
  { name: "Odd Future", tier: "mid", styles: ["streetwear"] },
  { name: "Golf Wang", tier: "mid", styles: ["streetwear", "casual"] },
  {
    name: "Brain Dead",
    tier: "premium",
    styles: ["streetwear", "avant-garde"]
  },
  { name: "Awake NY", tier: "premium", styles: ["streetwear"] },
  { name: "Raised by Wolves", tier: "mid", styles: ["streetwear"] },
  { name: "Fucking Awesome", tier: "mid", styles: ["streetwear"] },
  { name: "Independent", tier: "mid", styles: ["streetwear"] },
  { name: "Primitive", tier: "mid", styles: ["streetwear"] },
  { name: "RIPNDIP", tier: "mid", styles: ["streetwear"] },
  { name: "Spitfire", tier: "mid", styles: ["streetwear"] },
  { name: "Santa Cruz", tier: "mid", styles: ["streetwear"] },
  { name: "Powell Peralta", tier: "mid", styles: ["streetwear"] },
  { name: "The Hundreds", tier: "mid", styles: ["streetwear"] },
  { name: "Gcds", tier: "premium", styles: ["streetwear", "avant-garde"] },
  // ====================== ENFANT ======================
  // France enfant
  { name: "Petit Bateau", tier: "premium", styles: ["casual", "enfant"] },
  { name: "Catimini", tier: "premium", styles: ["boheme", "enfant"] },
  { name: "Jacadi", tier: "premium", styles: ["classic", "enfant"] },
  { name: "Du Pareil au M\xEAme", tier: "mid", styles: ["casual", "enfant"] },
  { name: "DPAM", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Cyrillus", tier: "premium", styles: ["classic", "enfant"] },
  { name: "Oka\xEFdi", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Obaibi", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Tape \xE0 l'\u0152il", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Noukie's", tier: "premium", styles: ["casual", "enfant"] },
  { name: "Vertbaudet", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Orchestra", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Sergent Major", tier: "mid", styles: ["chic", "enfant"] },
  { name: "Bonpoint", tier: "luxury", styles: ["chic", "enfant"] },
  { name: "Tartine et Chocolat", tier: "luxury", styles: ["chic", "enfant"] },
  { name: "Bonton", tier: "premium", styles: ["chic", "enfant"] },
  { name: "Lili Gaufrette", tier: "premium", styles: ["chic", "enfant"] },
  {
    name: "Caramel Baby & Child",
    tier: "premium",
    styles: ["classic", "enfant"]
  },
  { name: "Milk on the Rocks", tier: "premium", styles: ["rock", "enfant"] },
  { name: "Emile et Ida", tier: "premium", styles: ["boheme", "enfant"] },
  { name: "April Showers", tier: "premium", styles: ["casual", "enfant"] },
  { name: "Hundred Pieces", tier: "premium", styles: ["casual", "enfant"] },
  { name: "Poudre Organic", tier: "premium", styles: ["minimal", "enfant"] },
  { name: "Lalaby", tier: "premium", styles: ["minimal", "enfant"] },
  { name: "Grain de Bl\xE9", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Lulu Castagnette", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Les Chouettes", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Naissance", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Week-End \xE0 la Mer", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Bout'chou", tier: "mid", styles: ["casual", "enfant"] },
  { name: "P'tit Bisou", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Sucre d'Orge", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Moulin Roty", tier: "premium", styles: ["casual", "enfant"] },
  { name: "Absorba", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Natalys", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Joone", tier: "premium", styles: ["minimal", "enfant"] },
  { name: "Bulle de BB", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Chicco", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Stokke", tier: "premium", styles: ["minimal", "enfant"] },
  { name: "Babybjorn", tier: "premium", styles: ["minimal", "enfant"] },
  { name: "Babymoov", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Ingenuity", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Sauthon", tier: "mid", styles: ["classic", "enfant"] },
  { name: "B\xE9b\xE9 Confort", tier: "mid", styles: ["casual", "enfant"] },
  // Chaussures enfant
  { name: "Kickers", tier: "mid", styles: ["casual", "enfant"] },
  { name: "GBB", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Mod8", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Pom d'Api", tier: "premium", styles: ["chic", "enfant"] },
  { name: "Little Mary", tier: "premium", styles: ["classic", "enfant"] },
  { name: "Bellamy", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Aster", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Stones and Bones", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Babybotte", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Bisgaard", tier: "premium", styles: ["classic", "enfant"] },
  { name: "Superfit", tier: "mid", styles: ["sporty", "enfant"] },
  { name: "Ricosta", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Lurchi", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Geox Kids", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Clarks Kids", tier: "mid", styles: ["classic", "enfant"] },
  { name: "Lupilu", tier: "budget", styles: ["casual", "enfant"] },
  { name: "Impidimpi", tier: "budget", styles: ["casual", "enfant"] },
  { name: "C&A Kids", tier: "mid", styles: ["casual", "enfant"] },
  // International enfant
  { name: "Mothercare", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Frugi", tier: "premium", styles: ["casual", "enfant"] },
  { name: "Maxomorra", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Loud + Proud", tier: "mid", styles: ["casual", "enfant"] },
  { name: "DUNS Sweden", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Green Cotton", tier: "mid", styles: ["casual", "enfant"] },
  { name: "M\xFCsli by Green Cotton", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Sense Organics", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Organic Zoo", tier: "premium", styles: ["minimal", "enfant"] },
  { name: "Mini Rodini", tier: "premium", styles: ["casual", "enfant"] },
  { name: "Bobo Choses", tier: "premium", styles: ["casual", "enfant"] },
  {
    name: "The Animals Observatory",
    tier: "premium",
    styles: ["casual", "enfant"]
  },
  { name: "Tiny Cottons", tier: "premium", styles: ["casual", "enfant"] },
  { name: "Piccalilly", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Turtledove London", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Blade & Rose", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Kite Clothing", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Disana", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Engel", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Joha", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Nui Organics", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Touched by Nature", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Colored Organics", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Burt's Bees Baby", tier: "mid", styles: ["casual", "enfant"] },
  { name: "L'oved Baby", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Pact", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Parade Organics", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Bamberry", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Monica + Andy", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Primary", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Tea Collection", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Hanna Andersson", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Carter's", tier: "mid", styles: ["casual", "enfant"] },
  { name: "OshKosh B'gosh", tier: "mid", styles: ["casual", "enfant"] },
  { name: "The Children's Place", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Tucker + Tate", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Gymboree", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Maisonette", tier: "premium", styles: ["chic", "enfant"] },
  { name: "Janie and Jack", tier: "premium", styles: ["classic", "enfant"] },
  { name: "LEGO Wear", tier: "mid", styles: ["casual", "enfant"] },
  // Licences enfant
  { name: "Disney", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Marvel", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Star Wars", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Pok\xE9mon", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Pat Patrouille", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Reine des Neiges", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Spiderman", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Batman", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Minions", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Bluey", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Peppa Pig", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Cars", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Toy Story", tier: "mid", styles: ["casual", "enfant"] },
  { name: "Harry Potter", tier: "mid", styles: ["casual", "enfant"] },
  // ====================== DIVERS ======================
  { name: "Vitra", tier: "premium", styles: ["minimal"] },
  { name: "Qwstion", tier: "premium", styles: ["minimal", "casual"] }
];
var CORE_BRANDS = CORE_BRANDS_DATA.map((b) => b.name);
function findBrandByName(name) {
  const lower = name.toLowerCase();
  return CORE_BRANDS_DATA.find((b) => b.name.toLowerCase() === lower);
}
function findBrandsByTier(tier) {
  return CORE_BRANDS_DATA.filter((b) => b.tier === tier);
}
function findBrandsByStyle(style) {
  return CORE_BRANDS_DATA.filter((b) => b.styles.includes(style));
}
var _BRAND_NAMES_DEDUPED = [...new Set(CORE_BRANDS)].sort(
  (a, b) => a.localeCompare(b)
);
var CORE_BRAND_NAMES = _BRAND_NAMES_DEDUPED;
var TOP_BRANDS = [
  "Zara",
  "H&M",
  "Nike",
  "Adidas",
  "Mango",
  "Uniqlo",
  "Louis Vuitton",
  "Chanel",
  "Gucci",
  "S\xE9zane",
  "Levi's",
  "The North Face",
  "Ralph Lauren",
  "Balenciaga",
  "Petit Bateau",
  "Jacadi"
];
var CORE_COLORS = [
  // 💗 ROSES (8 nuances)
  { key: "colors.pink", color: "#FFB6C1", searchTags: ["rose", "pink"] },
  {
    key: "colors.hotPink",
    color: "#FF69B4",
    searchTags: ["rose", "fuchsia", "vif"]
  },
  {
    key: "colors.powderPink",
    color: "#F8D7DA",
    searchTags: ["rose", "pastel", "poudr\xE9"]
  },
  {
    key: "colors.coral",
    color: "#FF7F50",
    searchTags: ["corail", "orange", "rose"]
  },
  {
    key: "colors.roseGold",
    color: "#E8B4B8",
    searchTags: ["rose", "gold", "dor\xE9"]
  },
  {
    key: "colors.fuchsia",
    color: "#C41E3A",
    searchTags: ["fuchsia", "rose", "intense"]
  },
  {
    key: "colors.antiquePink",
    color: "#FAEBD7",
    searchTags: ["rose", "antique", "vintage"]
  },
  {
    key: "colors.magenta",
    color: "#FF00FF",
    searchTags: ["magenta", "rose", "\xE9lectrique"]
  },
  // 💙 BLEUS (8 nuances)
  {
    key: "colors.skyBlue",
    color: "#87CEEB",
    searchTags: ["bleu", "ciel", "clair"]
  },
  {
    key: "colors.navyBlue",
    color: "#000080",
    searchTags: ["bleu", "marine", "fonc\xE9"]
  },
  {
    key: "colors.royalBlue",
    color: "#4169E1",
    searchTags: ["bleu", "royal", "\xE9lectrique"]
  },
  {
    key: "colors.turquoise",
    color: "#40E0D0",
    searchTags: ["turquoise", "bleu", "vert"]
  },
  {
    key: "colors.midnightBlue",
    color: "#191970",
    searchTags: ["bleu", "nuit", "sombre"]
  },
  {
    key: "colors.tealBlue",
    color: "#008B8B",
    searchTags: ["bleu", "canard", "p\xE9trole"]
  },
  {
    key: "colors.cyan",
    color: "#00FFFF",
    searchTags: ["cyan", "bleu", "\xE9lectrique"]
  },
  {
    key: "colors.indigo",
    color: "#4B0082",
    searchTags: ["indigo", "bleu", "violet"]
  },
  // 💜 VIOLETS & MAUVES (6 nuances)
  { key: "colors.violet", color: "#DDA0DD", searchTags: ["violet", "mauve"] },
  {
    key: "colors.lilac",
    color: "#C8A2C8",
    searchTags: ["lilas", "violet", "clair"]
  },
  {
    key: "colors.plum",
    color: "#663399",
    searchTags: ["prune", "violet", "fonc\xE9"]
  },
  {
    key: "colors.lavender",
    color: "#E6E6FA",
    searchTags: ["lavande", "violet", "pastel"]
  },
  {
    key: "colors.mauve",
    color: "#CDA4DE",
    searchTags: ["parme", "violet", "doux"]
  },
  {
    key: "colors.eggplant",
    color: "#614051",
    searchTags: ["aubergine", "violet", "profond"]
  },
  // 💚 VERTS (8 nuances)
  { key: "colors.green", color: "#98FB98", searchTags: ["vert", "menthe"] },
  {
    key: "colors.appleGreen",
    color: "#9ACD32",
    searchTags: ["vert", "pomme", "vif"]
  },
  {
    key: "colors.khaki",
    color: "#8FBC8F",
    searchTags: ["kaki", "vert", "olive"]
  },
  {
    key: "colors.emeraldGreen",
    color: "#50C878",
    searchTags: ["vert", "\xE9meraude", "pr\xE9cieux"]
  },
  {
    key: "colors.forestGreen",
    color: "#013220",
    searchTags: ["vert", "sapin", "for\xEAt"]
  },
  {
    key: "colors.mint",
    color: "#3EB489",
    searchTags: ["menthe", "vert", "frais"]
  },
  {
    key: "colors.lime",
    color: "#00FF00",
    searchTags: ["lime", "vert", "\xE9lectrique"]
  },
  {
    key: "colors.seaGreen",
    color: "#B0E57C",
    searchTags: ["vert", "eau", "pastel"]
  },
  // ❤️ ROUGES (6 nuances)
  { key: "colors.red", color: "#FF0000", searchTags: ["rouge", "vif"] },
  {
    key: "colors.burgundy",
    color: "#800020",
    searchTags: ["bordeaux", "rouge", "fonc\xE9"]
  },
  {
    key: "colors.cherry",
    color: "#DE3163",
    searchTags: ["cerise", "rouge", "fruit"]
  },
  {
    key: "colors.vermilion",
    color: "#E34234",
    searchTags: ["vermillon", "rouge", "orange"]
  },
  {
    key: "colors.garnet",
    color: "#722F37",
    searchTags: ["grenat", "rouge", "profond"]
  },
  {
    key: "colors.carmine",
    color: "#960018",
    searchTags: ["carmin", "rouge", "intense"]
  },
  // 💛 JAUNES & ORANGES (8 nuances)
  { key: "colors.yellow", color: "#FFD700", searchTags: ["jaune", "soleil"] },
  { key: "colors.orange", color: "#FFA500", searchTags: ["orange", "vif"] },
  {
    key: "colors.peach",
    color: "#FFCBA4",
    searchTags: ["p\xEAche", "orange", "pastel"]
  },
  {
    key: "colors.lemonYellow",
    color: "#FFF700",
    searchTags: ["jaune", "citron", "acide"]
  },
  {
    key: "colors.burntOrange",
    color: "#CC5500",
    searchTags: ["orange", "br\xFBl\xE9", "terre"]
  },
  {
    key: "colors.apricot",
    color: "#FBCEB1",
    searchTags: ["abricot", "orange", "doux"]
  },
  {
    key: "colors.mustard",
    color: "#FFDB58",
    searchTags: ["moutarde", "jaune", "\xE9pice"]
  },
  {
    key: "colors.saffron",
    color: "#F4C430",
    searchTags: ["safran", "jaune", "\xE9pice"]
  },
  // 🖤 NEUTRES ESSENTIELS (8 nuances)
  { key: "colors.white", color: "#FFFFFF", searchTags: ["blanc", "white"] },
  { key: "colors.black", color: "#000000", searchTags: ["noir", "black"] },
  { key: "colors.gray", color: "#808080", searchTags: ["gris", "gray"] },
  {
    key: "colors.lightGray",
    color: "#D3D3D3",
    searchTags: ["gris", "clair", "light"]
  },
  {
    key: "colors.charcoal",
    color: "#36454F",
    searchTags: ["gris", "anthracite", "fonc\xE9"]
  },
  { key: "colors.beige", color: "#F5F5DC", searchTags: ["beige", "cr\xE8me"] },
  {
    key: "colors.cream",
    color: "#FFFDD0",
    searchTags: ["cr\xE8me", "beige", "clair"]
  },
  {
    key: "colors.ecru",
    color: "#F5F5DC",
    searchTags: ["\xE9cru", "naturel", "brut"]
  },
  // 🌟 MÉTALLIQUES (5 nuances) - NOUVEAUTÉ
  { key: "colors.gold", color: "#FFD700", searchTags: ["or", "dor\xE9", "gold"] },
  {
    key: "colors.silver",
    color: "#C0C0C0",
    searchTags: ["argent", "argent\xE9", "silver"]
  },
  { key: "colors.bronze", color: "#CD7F32", searchTags: ["bronze", "cuivre"] },
  {
    key: "colors.champagne",
    color: "#F7E7CE",
    searchTags: ["champagne", "dor\xE9", "clair"]
  },
  {
    key: "colors.copper",
    color: "#B87333",
    searchTags: ["cuivre", "copper", "bronze"]
  },
  // 🎨 AUTRES (3 couleurs)
  {
    key: "colors.brown",
    color: "#8B4513",
    searchTags: ["marron", "brun", "brown"]
  },
  { key: "colors.taupe", color: "#483C32", searchTags: ["taupe", "gris-brun"] },
  {
    key: "colors.multicolor",
    color: "#FF00FF",
    searchTags: ["multicolore", "multiple", "m\xE9lange", "color\xE9"]
  }
];
var CORE_STYLES = [
  // 👔 BASIQUES & INTEMPORELS (8)
  {
    key: "styles.casual",
    icon: "\u25E6",
    searchTags: ["casual", "d\xE9contract\xE9", "confortable", "quotidien"]
  },
  {
    key: "styles.chic",
    icon: "\u25C6",
    searchTags: ["chic", "\xE9l\xE9gant", "raffin\xE9", "sophistiqu\xE9"]
  },
  {
    key: "styles.classic",
    icon: "\u25FC",
    searchTags: ["classique", "intemporel", "sobre", "traditionnel"]
  },
  {
    key: "styles.modern",
    icon: "\u25B2",
    searchTags: ["moderne", "contemporain", "tendance", "actuel"]
  },
  {
    key: "styles.minimalist",
    icon: "\u25CB",
    searchTags: ["minimal", "simple", "\xE9pur\xE9", "uni"]
  },
  {
    key: "styles.vintage",
    icon: "\u25C9",
    searchTags: ["vintage", "r\xE9tro", "ancien", "nostalgique"]
  },
  {
    key: "styles.bohemian",
    icon: "\u25C8",
    searchTags: ["boh\xE8me", "boho", "libre", "artistique"]
  },
  {
    key: "styles.romantic",
    icon: "\u2661",
    searchTags: ["romantique", "f\xE9minin", "doux", "tendre"]
  },
  // 🎭 THÉMATIQUES ENFANTS (10)
  {
    key: "styles.princess",
    icon: "\u2654",
    searchTags: ["princesse", "rose", "robe", "fille", "conte"]
  },
  {
    key: "styles.superhero",
    icon: "\u2605",
    searchTags: ["superh\xE9ros", "h\xE9ros", "marvel", "batman", "cape"]
  },
  {
    key: "styles.unicorn",
    icon: "\u2666",
    searchTags: ["licorne", "magique", "arc-en-ciel", "fantaisie"]
  },
  {
    key: "styles.animals",
    icon: "\u25C8",
    searchTags: ["animaux", "chat", "chien", "motif", "mignon"]
  },
  {
    key: "styles.dinosaurs",
    icon: "\u25B2",
    searchTags: ["dinosaure", "pr\xE9histoire", "gar\xE7on", "aventure"]
  },
  {
    key: "styles.space",
    icon: "\u2726",
    searchTags: ["espace", "\xE9toiles", "plan\xE8tes", "astronaute"]
  },
  {
    key: "styles.pirates",
    icon: "\u25C9",
    searchTags: ["pirates", "aventure", "mer", "tr\xE9sor"]
  },
  {
    key: "styles.fairies",
    icon: "\u2662",
    searchTags: ["f\xE9es", "magie", "paillettes", "enchantement"]
  },
  {
    key: "styles.cars",
    icon: "\u25C6",
    searchTags: ["voitures", "course", "vitesse", "m\xE9canique"]
  },
  {
    key: "styles.music",
    icon: "\u266A",
    searchTags: ["musique", "instruments", "notes", "m\xE9lodie"]
  }
];
var CORE_MATERIALS = [
  { key: "materials.cotton", value: "COTTON", searchTags: ["coton", "cotton"] },
  {
    key: "materials.organicCotton",
    value: "ORGANIC_COTTON",
    searchTags: ["coton bio", "organic cotton"]
  },
  { key: "materials.linen", value: "LINEN", searchTags: ["lin", "linen"] },
  { key: "materials.wool", value: "WOOL", searchTags: ["laine", "wool"] },
  {
    key: "materials.merinoWool",
    value: "MERINO_WOOL",
    searchTags: ["laine m\xE9rinos", "merino"]
  },
  {
    key: "materials.cashmere",
    value: "CASHMERE",
    searchTags: ["cachemire", "cashmere"]
  },
  { key: "materials.silk", value: "SILK", searchTags: ["soie", "silk"] },
  { key: "materials.polyester", value: "POLYESTER", searchTags: ["polyester"] },
  {
    key: "materials.viscose",
    value: "VISCOSE",
    searchTags: ["viscose", "rayon"]
  },
  { key: "materials.modal", value: "MODAL", searchTags: ["modal"] },
  {
    key: "materials.tencel",
    value: "TENCEL",
    searchTags: ["tencel", "lyocell"]
  },
  {
    key: "materials.elastane",
    value: "ELASTANE",
    searchTags: ["\xE9lasthanne", "elastane", "spandex", "lycra"]
  },
  {
    key: "materials.nylon",
    value: "NYLON",
    searchTags: ["nylon", "polyamide"]
  },
  {
    key: "materials.acrylic",
    value: "ACRYLIC",
    searchTags: ["acrylique", "acrylic"]
  },
  {
    key: "materials.leather",
    value: "LEATHER",
    searchTags: ["cuir", "leather"]
  },
  { key: "materials.suede", value: "SUEDE", searchTags: ["daim", "suede"] },
  { key: "materials.denim", value: "DENIM", searchTags: ["jean", "denim"] },
  {
    key: "materials.velvet",
    value: "VELVET",
    searchTags: ["velours", "velvet"]
  },
  { key: "materials.satin", value: "SATIN", searchTags: ["satin"] },
  {
    key: "materials.fleece",
    value: "FLEECE",
    searchTags: ["polaire", "fleece"]
  },
  { key: "materials.canvas", value: "CANVAS", searchTags: ["toile", "canvas"] },
  {
    key: "materials.other",
    value: "OTHER",
    searchTags: ["autre", "other", "m\xE9lange"]
  }
];
var CORE_CONDITIONS = [
  { value: "NEW_WITH_TAG", key: "conditions.newWithTag", icon: "\u{1F3F7}\uFE0F" },
  { value: "PERFECT", key: "conditions.perfect", icon: "\u2728" },
  { value: "EXCELLENT", key: "conditions.excellent", icon: "\u2B50" },
  { value: "GOOD", key: "conditions.good", icon: "\u{1F44D}" },
  { value: "FAIR", key: "conditions.fair", icon: "\u{1F44C}" }
];
var BABY_SIZES = [
  {
    display: "Naissance (56 cm)",
    searchTags: [
      "naissance",
      "0-1 mois",
      "56",
      "56 cm",
      "newborn",
      "0-3M",
      "pr\xE9matur\xE9"
    ],
    ageMonths: 0,
    ageRange: [0, 1],
    cmSizes: ["56"]
  },
  {
    display: "1 mois (62 cm)",
    searchTags: ["1 mois", "0-3 mois", "1-3 mois", "62", "62 cm", "0-3M"],
    ageMonths: 1,
    ageRange: [0, 3],
    cmSizes: ["62"]
  },
  {
    display: "3 mois (68 cm)",
    searchTags: ["3 mois", "0-3 mois", "2-4 mois", "68", "68 cm", "0-3M"],
    ageMonths: 3,
    ageRange: [1, 4],
    cmSizes: ["68"]
  },
  {
    display: "3-6 mois (68-74 cm)",
    searchTags: ["3-6 mois", "3-6M", "68", "74", "68 cm", "74 cm"],
    ageMonths: 4,
    ageRange: [3, 6],
    cmSizes: ["68", "74"]
  },
  {
    display: "6 mois (74 cm)",
    searchTags: ["6 mois", "4-6 mois", "74", "74 cm", "3-6M"],
    ageMonths: 6,
    ageRange: [5, 7],
    cmSizes: ["74"]
  },
  {
    display: "6-9 mois (74-80 cm)",
    searchTags: ["6-9 mois", "6-9M", "74", "80", "74 cm", "80 cm"],
    ageMonths: 7,
    ageRange: [6, 9],
    cmSizes: ["74", "80"]
  },
  {
    display: "9 mois (80 cm)",
    searchTags: ["9 mois", "80", "80 cm", "6-9M"],
    ageMonths: 9,
    ageRange: [8, 10],
    cmSizes: ["80"]
  },
  {
    display: "9-12 mois (80-86 cm)",
    searchTags: ["9-12 mois", "9-12M", "80", "86", "80 cm", "86 cm"],
    ageMonths: 10,
    ageRange: [9, 12],
    cmSizes: ["80", "86"]
  },
  {
    display: "12 mois (86 cm)",
    searchTags: ["12 mois", "1 an", "86", "86 cm", "9-12M"],
    ageMonths: 12,
    ageRange: [11, 13],
    cmSizes: ["86"]
  },
  {
    display: "12-18 mois (86-92 cm)",
    searchTags: [
      "12-18 mois",
      "12-18M",
      "86",
      "92",
      "86 cm",
      "92 cm",
      "1-2 ans"
    ],
    ageMonths: 15,
    ageRange: [12, 18],
    cmSizes: ["86", "92"]
  },
  {
    display: "18 mois (92 cm)",
    searchTags: ["18 mois", "92", "92 cm", "12-18M"],
    ageMonths: 18,
    ageRange: [17, 20],
    cmSizes: ["92"]
  },
  {
    display: "18-24 mois (92-98 cm)",
    searchTags: ["18-24 mois", "18-24M", "92", "98", "92 cm", "98 cm"],
    ageMonths: 21,
    ageRange: [18, 24],
    cmSizes: ["92", "98"]
  },
  {
    display: "24 mois / 2 ans (98 cm)",
    searchTags: ["24 mois", "2 ans", "98", "98 cm", "18-24M", "2T"],
    ageMonths: 24,
    ageRange: [23, 30],
    cmSizes: ["98"]
  }
];
var KIDS_SIZES = [
  {
    display: "2-3 ans (98 cm)",
    searchTags: ["2 ans", "3 ans", "2-3 ans", "98", "98 cm", "2T", "3T"],
    ageMonths: 30,
    ageRange: [24, 42],
    cmSizes: ["98"]
  },
  {
    display: "4 ans (104 cm)",
    searchTags: ["4 ans", "3-4 ans", "4-5 ans", "104", "104 cm", "4T"],
    ageMonths: 48,
    ageRange: [42, 54],
    cmSizes: ["104"]
  },
  {
    display: "5 ans (110 cm)",
    searchTags: ["5 ans", "4-5 ans", "5-6 ans", "110", "110 cm", "5T"],
    ageMonths: 60,
    ageRange: [54, 66],
    cmSizes: ["110"]
  },
  {
    display: "6 ans (116 cm)",
    searchTags: ["6 ans", "5-6 ans", "6-7 ans", "116", "116 cm", "6T"],
    ageMonths: 72,
    ageRange: [66, 78],
    cmSizes: ["116"]
  },
  {
    display: "7 ans (122 cm)",
    searchTags: ["7 ans", "6-7 ans", "7-8 ans", "122", "122 cm", "7T"],
    ageMonths: 84,
    ageRange: [78, 90],
    cmSizes: ["122"]
  },
  {
    display: "8 ans (128 cm)",
    searchTags: ["8 ans", "7-8 ans", "8-9 ans", "128", "128 cm"],
    ageMonths: 96,
    ageRange: [90, 102],
    cmSizes: ["128"]
  },
  {
    display: "9 ans (134 cm)",
    searchTags: ["9 ans", "8-9 ans", "9-10 ans", "134", "134 cm"],
    ageMonths: 108,
    ageRange: [102, 114],
    cmSizes: ["134"]
  },
  {
    display: "10 ans (140 cm)",
    searchTags: ["10 ans", "9-10 ans", "10-11 ans", "140", "140 cm"],
    ageMonths: 120,
    ageRange: [114, 132],
    cmSizes: ["140"]
  },
  {
    display: "12 ans (152 cm)",
    searchTags: ["12 ans", "11-12 ans", "12-13 ans", "152", "152 cm"],
    ageMonths: 144,
    ageRange: [132, 156],
    cmSizes: ["152"]
  },
  {
    display: "14 ans (164 cm)",
    searchTags: [
      "14 ans",
      "13-14 ans",
      "14-15 ans",
      "164",
      "164 cm",
      "XS",
      "S"
    ],
    ageMonths: 168,
    ageRange: [156, 180],
    cmSizes: ["164"]
  }
];
var BABY_SHOE_SIZES = [
  {
    display: "16-17 EU (Chaussons naissance)",
    searchTags: [
      "16",
      "17",
      "16 EU",
      "17 EU",
      "chaussons",
      "naissance",
      "0-3 mois"
    ],
    ageMonths: 0,
    ageRange: [0, 3],
    cmSizes: ["16", "17"]
  },
  {
    display: "18-19 EU (Premi\xE8res chaussures)",
    searchTags: [
      "18",
      "19",
      "18 EU",
      "19 EU",
      "premi\xE8res chaussures",
      "3-6 mois",
      "6-9 mois"
    ],
    ageMonths: 6,
    ageRange: [3, 9],
    cmSizes: ["18", "19"]
  },
  {
    display: "20-21 EU (D\xE9but marche)",
    searchTags: [
      "20",
      "21",
      "20 EU",
      "21 EU",
      "d\xE9but marche",
      "9-12 mois",
      "12-15 mois"
    ],
    ageMonths: 12,
    ageRange: [9, 15],
    cmSizes: ["20", "21"]
  },
  {
    display: "22-23 EU (Petit marcheur)",
    searchTags: [
      "22",
      "23",
      "22 EU",
      "23 EU",
      "marcheur",
      "15-18 mois",
      "18-24 mois"
    ],
    ageMonths: 18,
    ageRange: [15, 24],
    cmSizes: ["22", "23"]
  }
];
var KIDS_SHOE_SIZES = [
  {
    display: "24-27 EU (2-4 ans)",
    searchTags: [
      "24",
      "25",
      "26",
      "27",
      "24 EU",
      "25 EU",
      "26 EU",
      "27 EU",
      "2 ans",
      "3 ans",
      "4 ans"
    ],
    ageMonths: 36,
    ageRange: [24, 48],
    cmSizes: ["24", "25", "26", "27"]
  },
  {
    display: "28-31 EU (4-6 ans)",
    searchTags: [
      "28",
      "29",
      "30",
      "31",
      "28 EU",
      "29 EU",
      "30 EU",
      "31 EU",
      "4 ans",
      "5 ans",
      "6 ans"
    ],
    ageMonths: 60,
    ageRange: [48, 72],
    cmSizes: ["28", "29", "30", "31"]
  },
  {
    display: "32-35 EU (6-8 ans)",
    searchTags: [
      "32",
      "33",
      "34",
      "35",
      "32 EU",
      "33 EU",
      "34 EU",
      "35 EU",
      "6 ans",
      "7 ans",
      "8 ans"
    ],
    ageMonths: 84,
    ageRange: [72, 96],
    cmSizes: ["32", "33", "34", "35"]
  },
  {
    display: "36-39 EU (8-12 ans)",
    searchTags: [
      "36",
      "37",
      "38",
      "39",
      "36 EU",
      "37 EU",
      "38 EU",
      "39 EU",
      "8 ans",
      "9 ans",
      "10 ans",
      "11 ans",
      "12 ans"
    ],
    ageMonths: 120,
    ageRange: [96, 144],
    cmSizes: ["36", "37", "38", "39"]
  },
  {
    display: "40-43 EU (12+ ans)",
    searchTags: [
      "40",
      "41",
      "42",
      "43",
      "40 EU",
      "41 EU",
      "42 EU",
      "43 EU",
      "12 ans",
      "13 ans",
      "14 ans",
      "ado"
    ],
    ageMonths: 168,
    ageRange: [144, 216],
    cmSizes: ["40", "41", "42", "43"]
  }
];
var ADULT_CLOTHING_SIZES = [
  {
    display: "XXXS",
    searchTags: ["XXXS", "28", "28 EU", "triple extra small"],
    category: "petite"
  },
  {
    display: "XXS",
    searchTags: ["XXS", "30", "32", "30 EU", "32 EU", "double extra small"],
    category: "petite"
  },
  {
    display: "XS",
    searchTags: ["XS", "34", "34 EU", "extra small"],
    category: "petite"
  },
  {
    display: "S",
    searchTags: ["S", "36", "36 EU", "small", "petit"],
    category: "standard"
  },
  {
    display: "M",
    searchTags: ["M", "38", "40", "38 EU", "40 EU", "medium", "moyen"],
    category: "standard"
  },
  {
    display: "L",
    searchTags: ["L", "42", "44", "42 EU", "44 EU", "large", "grand"],
    category: "standard"
  },
  {
    display: "XL",
    searchTags: ["XL", "46", "46 EU", "extra large"],
    category: "standard"
  },
  {
    display: "XXL",
    searchTags: ["XXL", "48", "48 EU", "double extra large"],
    category: "standard"
  },
  {
    display: "XXXL",
    searchTags: ["XXXL", "50", "50 EU", "triple extra large"],
    category: "standard"
  },
  {
    display: "4XL",
    searchTags: ["4XL", "52", "52 EU", "quadruple extra large"],
    category: "plus"
  },
  { display: "5XL", searchTags: ["5XL", "54", "54 EU"], category: "plus" },
  { display: "6XL", searchTags: ["6XL", "56", "56 EU"], category: "plus" },
  { display: "7XL", searchTags: ["7XL", "58", "58 EU"], category: "plus" },
  { display: "8XL", searchTags: ["8XL", "60", "60 EU"], category: "plus" },
  {
    display: "Taille Unique",
    searchTags: ["ONE_SIZE", "one size", "TU", "taille unique"],
    category: "standard"
  }
];
var ADULT_SHOE_SIZES_WOMAN = [
  { display: "34", searchTags: ["34", "34 EU", "4 US"], category: "petite" },
  { display: "35", searchTags: ["35", "35 EU", "5 US"], category: "petite" },
  { display: "36", searchTags: ["36", "36 EU", "6 US"], category: "standard" },
  { display: "37", searchTags: ["37", "37 EU", "7 US"], category: "standard" },
  { display: "38", searchTags: ["38", "38 EU", "8 US"], category: "standard" },
  { display: "39", searchTags: ["39", "39 EU", "9 US"], category: "standard" },
  { display: "40", searchTags: ["40", "40 EU", "10 US"], category: "standard" },
  { display: "41", searchTags: ["41", "41 EU", "11 US"], category: "standard" },
  { display: "42", searchTags: ["42", "42 EU", "12 US"], category: "plus" },
  { display: "43", searchTags: ["43", "43 EU", "13 US"], category: "plus" },
  { display: "44", searchTags: ["44", "44 EU", "14 US"], category: "plus" }
];
var ADULT_SHOE_SIZES_MAN = [
  { display: "38", searchTags: ["38", "38 EU", "5 US"], category: "petite" },
  { display: "39", searchTags: ["39", "39 EU", "6 US"], category: "petite" },
  { display: "40", searchTags: ["40", "40 EU", "7 US"], category: "standard" },
  { display: "41", searchTags: ["41", "41 EU", "8 US"], category: "standard" },
  { display: "42", searchTags: ["42", "42 EU", "9 US"], category: "standard" },
  { display: "43", searchTags: ["43", "43 EU", "10 US"], category: "standard" },
  { display: "44", searchTags: ["44", "44 EU", "11 US"], category: "standard" },
  { display: "45", searchTags: ["45", "45 EU", "12 US"], category: "standard" },
  { display: "46", searchTags: ["46", "46 EU", "13 US"], category: "plus" },
  { display: "47", searchTags: ["47", "47 EU", "14 US"], category: "plus" },
  { display: "48", searchTags: ["48", "48 EU", "15 US"], category: "plus" },
  { display: "49", searchTags: ["49", "49 EU", "16 US"], category: "plus" },
  { display: "50", searchTags: ["50", "50 EU", "17 US"], category: "plus" }
];
var getClothingSizesByProfile = (profile) => {
  if (profile === "BABY_GIRL" || profile === "BABY_BOY") {
    return BABY_SIZES.map((s) => s.display);
  }
  if (profile === "CHILD_GIRL" || profile === "CHILD_BOY") {
    return KIDS_SIZES.map((s) => s.display);
  }
  if (profile === "ADULT_WOMAN" || profile === "ADULT_MAN") {
    return ADULT_CLOTHING_SIZES.map((s) => s.display);
  }
  return ADULT_CLOTHING_SIZES.map((s) => s.display);
};
var getShoeSizesByProfile = (profile) => {
  if (profile === "BABY_GIRL" || profile === "BABY_BOY") {
    return BABY_SHOE_SIZES.map((s) => s.display);
  }
  if (profile === "CHILD_GIRL" || profile === "CHILD_BOY") {
    return KIDS_SHOE_SIZES.map((s) => s.display);
  }
  if (profile === "ADULT_WOMAN") {
    return ADULT_SHOE_SIZES_WOMAN.map((s) => s.display);
  }
  if (profile === "ADULT_MAN") {
    return ADULT_SHOE_SIZES_MAN.map((s) => s.display);
  }
  return ADULT_SHOE_SIZES_WOMAN.map((s) => s.display);
};
var getAgeGroupFromProfile = (profile) => {
  if (profile === "BABY_GIRL" || profile === "BABY_BOY") return "BABY";
  if (profile === "CHILD_GIRL" || profile === "CHILD_BOY") return "CHILD";
  return "ADULT";
};
var getGenderFromProfile = (profile) => {
  if (profile === "ADULT_WOMAN") return "WOMAN";
  if (profile === "ADULT_MAN") return "MAN";
  if (profile === "CHILD_GIRL" || profile === "BABY_GIRL") return "GIRL";
  return "BOY";
};
var migrateOldProfileType = (oldProfile) => {
  const mapping = {
    FEMME: "ADULT_WOMAN",
    HOMME: "ADULT_MAN",
    ENFANT_FILLE: "CHILD_GIRL",
    ENFANT_GARCON: "CHILD_BOY",
    BEBE: "BABY_GIRL"
  };
  return mapping[oldProfile] || "ADULT_WOMAN";
};
var findSizesByAge = (ageInMonths) => {
  const allSizes = [...BABY_SIZES, ...KIDS_SIZES];
  return allSizes.filter(
    (size) => ageInMonths >= size.ageRange[0] && ageInMonths <= size.ageRange[1]
  ).slice(0, 6);
};
var findShoesByAge = (ageInMonths) => {
  const allShoes = [...BABY_SHOE_SIZES, ...KIDS_SHOE_SIZES];
  return allShoes.filter(
    (shoe) => ageInMonths >= shoe.ageRange[0] && ageInMonths <= shoe.ageRange[1]
  ).slice(0, 4);
};
var findSizesBySearch = (searchQuery) => {
  const query = searchQuery.toLowerCase().trim();
  const allSizes = [...BABY_SIZES, ...KIDS_SIZES];
  return allSizes.filter(
    (size) => size.searchTags.some((tag) => tag.toLowerCase().includes(query)) || size.display.toLowerCase().includes(query)
  );
};
var findShoesBySearch = (searchQuery) => {
  const query = searchQuery.toLowerCase().trim();
  const allShoes = [...BABY_SHOE_SIZES, ...KIDS_SHOE_SIZES];
  return allShoes.filter(
    (shoe) => shoe.searchTags.some((tag) => tag.toLowerCase().includes(query)) || shoe.display.toLowerCase().includes(query)
  );
};
var getSizeDisplayName = (sizeQuery) => {
  const found = findSizesBySearch(sizeQuery)[0];
  return found ? found.display : sizeQuery;
};
var getShoeDisplayName = (shoeQuery) => {
  const found = findShoesBySearch(shoeQuery)[0];
  return found ? found.display : shoeQuery;
};
var getClothingSizesForAge = (ageInYears) => {
  const ageInMonths = ageInYears * 12;
  const matchingSizes = findSizesByAge(ageInMonths);
  return matchingSizes.map((size) => size.display);
};
var getShoeSizesForAge = (ageInYears) => {
  const ageInMonths = ageInYears * 12;
  const matchingShoes = findShoesByAge(ageInMonths);
  return matchingShoes.map((shoe) => shoe.display);
};
var getSizeWithDescription = (size, _age) => {
  const displayName = getSizeDisplayName(size);
  return {
    value: size,
    label: displayName,
    description: displayName
  };
};
var getShoeWithDescription = (size, _age) => {
  const displayName = getShoeDisplayName(size);
  return {
    value: size,
    label: displayName,
    description: displayName
  };
};
var findAllSizeMatches = (productSize) => {
  const allSizes = [...BABY_SIZES, ...KIDS_SIZES];
  const matches = [];
  for (const sizeVariant of allSizes) {
    if (sizeVariant.searchTags.some(
      (tag) => tag.toLowerCase() === productSize.toLowerCase() || tag.toLowerCase().includes(productSize.toLowerCase())
    )) {
      matches.push(...sizeVariant.searchTags);
    }
  }
  return [...new Set(matches)];
};
var findAllShoeMatches = (productShoe) => {
  const allShoes = [...BABY_SHOE_SIZES, ...KIDS_SHOE_SIZES];
  const matches = [];
  for (const shoeVariant of allShoes) {
    if (shoeVariant.searchTags.some(
      (tag) => tag.toLowerCase() === productShoe.toLowerCase() || tag.toLowerCase().includes(productShoe.toLowerCase())
    )) {
      matches.push(...shoeVariant.searchTags);
    }
  }
  return [...new Set(matches)];
};
var findMatchingColors = (searchQuery) => {
  const query = searchQuery.toLowerCase();
  return CORE_COLORS.filter(
    (color) => color.searchTags.some((tag) => tag.includes(query))
  );
};
var findMatchingStyles = (searchQuery) => {
  const query = searchQuery.toLowerCase();
  return CORE_STYLES.filter(
    (style) => style.searchTags.some((tag) => tag.includes(query))
  );
};
var findMatchingBrands = (searchQuery) => {
  const query = searchQuery.toLowerCase();
  return CORE_BRANDS.filter((brand) => brand.toLowerCase().includes(query));
};
var saveCustomPreference = async (type, value) => {
  try {
    await fetch("/api/v1/users/preferences/custom", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type,
        value,
        usage_count: 1,
        created_by: "user_input"
      })
    });
    console.log(`\u{1F3AF} Pr\xE9f\xE9rence ${type} ajout\xE9e au syst\xE8me : ${value}`);
  } catch (error) {
    console.error("\u274C Erreur sauvegarde pr\xE9f\xE9rence custom:", error);
  }
};
var getAgeFromSize = (sizeQuery) => {
  const found = findSizesBySearch(sizeQuery)[0];
  if (found) {
    const ageInMonths = found.ageMonths;
    if (ageInMonths <= 24) {
      return `${ageInMonths} mois`;
    } else {
      const years = Math.floor(ageInMonths / 12);
      return `${years} ans`;
    }
  }
  return "\xC2ge inconnu";
};
var getSizeGrowthSuggestions = (currentSize) => {
  const current = findSizesBySearch(currentSize)[0];
  if (!current) return [];
  const nextAgeMonths = current.ageMonths + 6;
  const nextSizes = findSizesByAge(nextAgeMonths);
  return nextSizes.map((size) => size.display).slice(0, 3);
};
var getBrandFitInfo = (fit) => {
  const map = {
    RUNS_SMALL: {
      label: "Taille petit",
      advice: "Prendre une taille au-dessus",
      colorClass: "text-amber-700 bg-amber-50 border-amber-200",
      icon: "\u2191"
    },
    TRUE_TO_SIZE: {
      label: "Taille juste",
      advice: "La taille correspond \xE0 la coupe standard",
      colorClass: "text-green-700 bg-green-50 border-green-200",
      icon: "\u2713"
    },
    RUNS_LARGE: {
      label: "Taille grand",
      advice: "Prendre une taille en-dessous",
      colorClass: "text-blue-700 bg-blue-50 border-blue-200",
      icon: "\u2193"
    }
  };
  return map[fit];
};
var isSizeCompatible = (size1, size2) => {
  const matches1 = findAllSizeMatches(size1);
  const matches2 = findAllSizeMatches(size2);
  return matches1.some((match) => matches2.includes(match));
};
var getSellerClothingSizes = () => {
  const sellerSizes = [];
  [...BABY_SIZES, ...KIDS_SIZES].forEach((sizeVariant) => {
    sizeVariant.searchTags.forEach((tag) => {
      const equivalent = getSizeDisplayName(tag);
      sellerSizes.push({
        label: tag,
        value: tag,
        category: sizeVariant.ageMonths <= 24 ? "B\xE9b\xE9" : "Enfant",
        popularity: getSizePopularityScore(tag),
        equivalentTo: equivalent
      });
    });
  });
  return sellerSizes.filter(
    (size, index, self) => index === self.findIndex((s) => s.value === size.value)
  ).sort((a, b) => b.popularity - a.popularity).slice(0, 50);
};
var getSellerShoesSizes = () => {
  const sellerShoes = [];
  [...BABY_SHOE_SIZES, ...KIDS_SHOE_SIZES].forEach((shoeVariant) => {
    shoeVariant.searchTags.forEach((tag) => {
      const equivalent = getShoeDisplayName(tag);
      sellerShoes.push({
        label: tag,
        value: tag,
        category: shoeVariant.ageMonths <= 24 ? "B\xE9b\xE9" : "Enfant",
        popularity: getSizePopularityScore(tag),
        equivalentTo: equivalent
      });
    });
  });
  return sellerShoes.filter(
    (shoe, index, self) => index === self.findIndex((s) => s.value === shoe.value)
  ).sort((a, b) => b.popularity - a.popularity).slice(0, 30);
};
var getSizePopularityScore = (size) => {
  const popularSizes = {
    "6 mois": 95,
    "12 mois": 90,
    "18 mois": 85,
    "3-6 mois": 80,
    "6-9 mois": 75,
    "12-18 mois": 70,
    "74": 85,
    "80": 80,
    "86": 75,
    "2 ans": 70,
    "4 ans": 65,
    "6 ans": 60
  };
  return popularSizes[size] || 50;
};
var getSellerSizesByCategory = () => {
  const clothingSizes = getSellerClothingSizes();
  const shoeSizes = getSellerShoesSizes();
  return {
    baby: clothingSizes.filter((s) => s.category === "B\xE9b\xE9"),
    kids: clothingSizes.filter((s) => s.category === "Enfant"),
    shoes: shoeSizes
  };
};
var validateSellerSize = (size, category) => {
  const allSizes = category === "clothing" ? [...BABY_SIZES, ...KIDS_SIZES] : [...BABY_SHOE_SIZES, ...KIDS_SHOE_SIZES];
  for (const sizeVariant of allSizes) {
    if (sizeVariant.searchTags.some(
      (tag) => tag.toLowerCase() === size.toLowerCase()
    )) {
      return {
        isValid: true,
        equivalentTo: sizeVariant.display
      };
    }
  }
  const closeMatch = allSizes.find(
    (sv) => sv.searchTags.some((tag) => tag.toLowerCase().includes(size.toLowerCase()))
  );
  return {
    isValid: false,
    suggestion: closeMatch?.display,
    equivalentTo: closeMatch?.display
  };
};
var getSizePopularity = () => {
  return {
    "6 mois (68-74 cm)": 95,
    "12 mois (80-86 cm)": 90,
    "18 mois (86-92 cm)": 85,
    "2-3 ans (92-104 cm)": 80,
    "4 ans (104-110 cm)": 75,
    "6 ans (110-122 cm)": 70,
    "8 ans (122-134 cm)": 65,
    "10 ans (134-146 cm)": 60,
    "12 ans (146-158 cm)": 55,
    "14 ans (158-170 cm)": 50
  };
};
var getBrandsByAge = (ageInMonths) => {
  if (ageInMonths <= 24) {
    return [
      "Petit Bateau",
      "H&M Kids",
      "Zara Kids",
      "DPAM",
      "Catimini",
      "Jacadi"
    ];
  } else if (ageInMonths <= 72) {
    return ["H&M Kids", "Zara Kids", "Nike", "Adidas", "Disney", "Uniqlo"];
  } else {
    return ["Nike", "Adidas", "Zara", "H&M", "Uniqlo", "Vans"];
  }
};
var getPackageSizeByCategory = (category, profile) => {
  const categoryLower = category.toLowerCase();
  if (profile === "BABY_GIRL" || profile === "BABY_BOY") {
    if (categoryLower.match(/manteau|veste|blouson|combinaison/))
      return "MOYEN";
    return "PETIT";
  }
  if (profile === "CHILD_GIRL" || profile === "CHILD_BOY") {
    if (categoryLower.match(/manteau|veste|blouson|doudoune/)) return "MOYEN";
    if (categoryLower.match(/pull|sweat|pantalon|jean|robe/)) return "MOYEN";
    return "PETIT";
  }
  if (categoryLower.match(
    /t-shirt|chemise|top|débardeur|polo|short|jupe|écharpe|foulard|cravate|ceinture|gant|bonnet|casquette|chapeau|bijou|montre|lunettes|portefeuille|porte-clé/
  )) {
    return "PETIT";
  }
  if (categoryLower.match(
    /manteau|parka|doudoune|trench|imperméable|veste longue|blouson|combinaison|robe longue|valise|sac voyage|botte|cuissarde/
  )) {
    return "GRAND";
  }
  return "MOYEN";
};

// src/i18n/constants/locales.ts
var LOCALES_INFO = {
  "fr-FR": {
    code: "fr-FR",
    language: "fr",
    country: "FR",
    name: "Fran\xE7ais",
    nativeName: "Fran\xE7ais",
    flag: "\u{1F1EB}\u{1F1F7}",
    currency: "EUR",
    direction: "ltr",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "HH:mm",
    numberFormat: {
      decimal: ",",
      thousands: " ",
      currency: { symbol: "\u20AC", position: "after" }
    }
  },
  "en-US": {
    code: "en-US",
    language: "en",
    country: "US",
    name: "English",
    nativeName: "English",
    flag: "\u{1F1FA}\u{1F1F8}",
    currency: "USD",
    direction: "ltr",
    dateFormat: "MM/DD/YYYY",
    timeFormat: "h:mm A",
    numberFormat: {
      decimal: ".",
      thousands: ",",
      currency: { symbol: "$", position: "before" }
    }
  },
  "de-DE": {
    code: "de-DE",
    language: "de",
    country: "DE",
    name: "Deutsch",
    nativeName: "Deutsch",
    flag: "\u{1F1E9}\u{1F1EA}",
    currency: "EUR",
    direction: "ltr",
    dateFormat: "DD.MM.YYYY",
    timeFormat: "HH:mm",
    numberFormat: {
      decimal: ",",
      thousands: ".",
      currency: { symbol: "\u20AC", position: "after" }
    }
  },
  "es-ES": {
    code: "es-ES",
    language: "es",
    country: "ES",
    name: "Espa\xF1ol",
    nativeName: "Espa\xF1ol",
    flag: "\u{1F1EA}\u{1F1F8}",
    currency: "EUR",
    direction: "ltr",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "HH:mm",
    numberFormat: {
      decimal: ",",
      thousands: ".",
      currency: { symbol: "\u20AC", position: "after" }
    }
  },
  "it-IT": {
    code: "it-IT",
    language: "it",
    country: "IT",
    name: "Italiano",
    nativeName: "Italiano",
    flag: "\u{1F1EE}\u{1F1F9}",
    currency: "EUR",
    direction: "ltr",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "HH:mm",
    numberFormat: {
      decimal: ",",
      thousands: ".",
      currency: { symbol: "\u20AC", position: "after" }
    }
  },
  "nl-NL": {
    code: "nl-NL",
    language: "nl",
    country: "NL",
    name: "Nederlands",
    nativeName: "Nederlands",
    flag: "\u{1F1F3}\u{1F1F1}",
    currency: "EUR",
    direction: "ltr",
    dateFormat: "DD-MM-YYYY",
    timeFormat: "HH:mm",
    numberFormat: {
      decimal: ",",
      thousands: ".",
      currency: { symbol: "\u20AC", position: "before" }
    }
  },
  "pl-PL": {
    code: "pl-PL",
    language: "pl",
    country: "PL",
    name: "Polski",
    nativeName: "Polski",
    flag: "\u{1F1F5}\u{1F1F1}",
    currency: "PLN",
    direction: "ltr",
    dateFormat: "DD.MM.YYYY",
    timeFormat: "HH:mm",
    numberFormat: {
      decimal: ",",
      thousands: " ",
      currency: { symbol: "z\u0142", position: "after" }
    }
  },
  "cs-CZ": {
    code: "cs-CZ",
    language: "cs",
    country: "CZ",
    name: "\u010Ce\u0161tina",
    nativeName: "\u010Ce\u0161tina",
    flag: "\u{1F1E8}\u{1F1FF}",
    currency: "CZK",
    direction: "ltr",
    dateFormat: "DD.MM.YYYY",
    timeFormat: "HH:mm",
    numberFormat: {
      decimal: ",",
      thousands: " ",
      currency: { symbol: "K\u010D", position: "after" }
    }
  },
  "lt-LT": {
    code: "lt-LT",
    language: "lt",
    country: "LT",
    name: "Lietuvi\u0173",
    nativeName: "Lietuvi\u0173",
    flag: "\u{1F1F1}\u{1F1F9}",
    currency: "EUR",
    direction: "ltr",
    dateFormat: "YYYY-MM-DD",
    timeFormat: "HH:mm",
    numberFormat: {
      decimal: ",",
      thousands: " ",
      currency: { symbol: "\u20AC", position: "after" }
    }
  }
};
var DEFAULT_LOCALE = "fr-FR";
var FALLBACK_LOCALE = "en-US";
var SUPPORTED_LOCALES = Object.keys(
  LOCALES_INFO
);
var RTL_LOCALES = [];
var LOCALE_CURRENCIES = {
  "fr-FR": "EUR",
  "en-US": "USD",
  "de-DE": "EUR",
  "es-ES": "EUR",
  "it-IT": "EUR",
  "nl-NL": "EUR",
  "pl-PL": "PLN",
  "cs-CZ": "CZK",
  "lt-LT": "EUR"
};

// src/i18n/utils/translation.ts
var getLocaleInfo = (locale) => {
  return LOCALES_INFO[locale] || LOCALES_INFO[DEFAULT_LOCALE];
};
var detectLocaleFromBrowser = () => {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  const browserLang = window.navigator.language;
  if (LOCALES_INFO[browserLang]) {
    return browserLang;
  }
  const langCode = browserLang.split("-")[0];
  const matchingLocale = Object.keys(LOCALES_INFO).find(
    (locale) => locale.startsWith(langCode)
  );
  return matchingLocale || DEFAULT_LOCALE;
};
var isRTL = (locale) => {
  return getLocaleInfo(locale).direction === "rtl";
};

// import("./locales/**/*/**/*.json") in src/i18n/config.ts
var globImport_locales_json = __glob({
  "./locales/cs-CZ/ai.json": () => import("./ai-6TFIM7EH.js"),
  "./locales/cs-CZ/auth.json": () => import("./auth-AX4XPXZN.js"),
  "./locales/cs-CZ/categories.json": () => import("./categories-6IJG4PL6.js"),
  "./locales/cs-CZ/chat.json": () => import("./chat-2FTU3ZIE.js"),
  "./locales/cs-CZ/common.json": () => import("./common-UZSFT6F5.js"),
  "./locales/cs-CZ/errors.json": () => import("./errors-NE2X7K6N.js"),
  "./locales/cs-CZ/events.json": () => import("./events-7E4CDWWD.js"),
  "./locales/cs-CZ/payments.json": () => import("./payments-57TIIWXW.js"),
  "./locales/cs-CZ/products.json": () => import("./products-VVMV6OPH.js"),
  "./locales/cs-CZ/referral.json": () => import("./referral-6SH3QQ5O.js"),
  "./locales/cs-CZ/shipping.json": () => import("./shipping-OV57DRZO.js"),
  "./locales/cs-CZ/stories.json": () => import("./stories-5VJ3ZSZO.js"),
  "./locales/cs-CZ/validation.json": () => import("./validation-MQHAEJQ6.js"),
  "./locales/de-DE/ai.json": () => import("./ai-URRTIXHQ.js"),
  "./locales/de-DE/auth.json": () => import("./auth-UUJ4VSBF.js"),
  "./locales/de-DE/categories.json": () => import("./categories-OQHNZEKH.js"),
  "./locales/de-DE/chat.json": () => import("./chat-TWPYHJUC.js"),
  "./locales/de-DE/common.json": () => import("./common-BCMD5LLK.js"),
  "./locales/de-DE/errors.json": () => import("./errors-TZH7U4TK.js"),
  "./locales/de-DE/events.json": () => import("./events-ZEL2Q5TZ.js"),
  "./locales/de-DE/payments.json": () => import("./payments-4GRKVIY3.js"),
  "./locales/de-DE/products.json": () => import("./products-O7ZEPPJ5.js"),
  "./locales/de-DE/referral.json": () => import("./referral-F76IF35I.js"),
  "./locales/en-US/ai.json": () => import("./ai-U7ULANNQ.js"),
  "./locales/en-US/auth.json": () => import("./auth-Z4ZXT6JG.js"),
  "./locales/en-US/cart.json": () => import("./cart-2DV6J5R2.js"),
  "./locales/en-US/categories.json": () => import("./categories-4LSS5NES.js"),
  "./locales/en-US/chat.json": () => import("./chat-X55MH4UC.js"),
  "./locales/en-US/common.json": () => import("./common-BTP6O3VH.js"),
  "./locales/en-US/emails.json": () => import("./emails-34II7UBU.js"),
  "./locales/en-US/errors.json": () => import("./errors-DUYZD6YH.js"),
  "./locales/en-US/events.json": () => import("./events-RMCITL2T.js"),
  "./locales/en-US/growth.json": () => import("./growth-YFQWXTSG.js"),
  "./locales/en-US/payments.json": () => import("./payments-FJPOCYWW.js"),
  "./locales/en-US/preferences-common.json": () => import("./preferences-common-NECJ54QM.js"),
  "./locales/en-US/products.json": () => import("./products-57OAH3KH.js"),
  "./locales/en-US/profiles-adults.json": () => import("./profiles-adults-TE7LOHKI.js"),
  "./locales/en-US/profiles-business.json": () => import("./profiles-business-44HYDPPB.js"),
  "./locales/en-US/profiles-children.json": () => import("./profiles-children-XACPE6UY.js"),
  "./locales/en-US/profiles.json": () => import("./profiles-5U5AI4XG.js"),
  "./locales/en-US/referral.json": () => import("./referral-RJMVICVQ.js"),
  "./locales/en-US/shipping.json": () => import("./shipping-5XTFRVKQ.js"),
  "./locales/en-US/sizes.json": () => import("./sizes-CKTY5BAV.js"),
  "./locales/en-US/stories.json": () => import("./stories-4FQYAL7X.js"),
  "./locales/en-US/transactions.json": () => import("./transactions-FRTY3DIQ.js"),
  "./locales/en-US/validation.json": () => import("./validation-ECLX6DNF.js"),
  "./locales/es-ES/ai.json": () => import("./ai-LORRKE3E.js"),
  "./locales/es-ES/auth.json": () => import("./auth-B2LSD65F.js"),
  "./locales/es-ES/categories.json": () => import("./categories-IPW37EFG.js"),
  "./locales/es-ES/chat.json": () => import("./chat-3FRMXPYB.js"),
  "./locales/es-ES/common.json": () => import("./common-NQOOXLCU.js"),
  "./locales/es-ES/errors.json": () => import("./errors-QNOX25OQ.js"),
  "./locales/es-ES/events.json": () => import("./events-TAN5EA7X.js"),
  "./locales/es-ES/products.json": () => import("./products-IEKCXSRZ.js"),
  "./locales/es-ES/referral.json": () => import("./referral-6KUHESAY.js"),
  "./locales/es-ES/shipping.json": () => import("./shipping-3KCG364Z.js"),
  "./locales/es-ES/stories.json": () => import("./stories-XQIVQOCS.js"),
  "./locales/es-ES/validation.json": () => import("./validation-XCEH5Q6E.js"),
  "./locales/fr-FR/ai.json": () => import("./ai-6367E4T3.js"),
  "./locales/fr-FR/auth.json": () => import("./auth-EMO4CAFN.js"),
  "./locales/fr-FR/cart.json": () => import("./cart-I7M6DQ7Y.js"),
  "./locales/fr-FR/categories.json": () => import("./categories-SIRC6FSP.js"),
  "./locales/fr-FR/chat.json": () => import("./chat-2KYUDWQ2.js"),
  "./locales/fr-FR/checkout.json": () => import("./checkout-UEZULD75.js"),
  "./locales/fr-FR/common.json": () => import("./common-NOQ4FLJM.js"),
  "./locales/fr-FR/emails.json": () => import("./emails-AZG6MDHP.js"),
  "./locales/fr-FR/errors.json": () => import("./errors-HEOVP2R5.js"),
  "./locales/fr-FR/events.json": () => import("./events-Z3IRZOC3.js"),
  "./locales/fr-FR/growth.json": () => import("./growth-MPMRGX5V.js"),
  "./locales/fr-FR/payments.json": () => import("./payments-W3SERZSJ.js"),
  "./locales/fr-FR/preferences-common.json": () => import("./preferences-common-2JVD5TVS.js"),
  "./locales/fr-FR/products.json": () => import("./products-XF5X3TOC.js"),
  "./locales/fr-FR/profiles-adults.json": () => import("./profiles-adults-CXS6L3NA.js"),
  "./locales/fr-FR/profiles-business.json": () => import("./profiles-business-CYFW7Q2Z.js"),
  "./locales/fr-FR/profiles-children.json": () => import("./profiles-children-3ZQADDPO.js"),
  "./locales/fr-FR/profiles.json": () => import("./profiles-J5DK6NR5.js"),
  "./locales/fr-FR/referral.json": () => import("./referral-4JQMEX4O.js"),
  "./locales/fr-FR/shipping.json": () => import("./shipping-UQ6725SE.js"),
  "./locales/fr-FR/shop.json": () => import("./shop-4HQWKFTW.js"),
  "./locales/fr-FR/sizes.json": () => import("./sizes-VRNZODHC.js"),
  "./locales/fr-FR/stories.json": () => import("./stories-NZVMMR74.js"),
  "./locales/fr-FR/transactions.json": () => import("./transactions-ENQYQXQF.js"),
  "./locales/fr-FR/validation.json": () => import("./validation-ZJ3JGPWR.js"),
  "./locales/it-IT/ai.json": () => import("./ai-HZUGXB37.js"),
  "./locales/it-IT/categories.json": () => import("./categories-QUOYPNSN.js"),
  "./locales/ja/ai.json": () => import("./ai-N4JVM7JJ.js"),
  "./locales/ja/auth.json": () => import("./auth-R37U2W7R.js"),
  "./locales/ja/chat.json": () => import("./chat-YH25GL2G.js"),
  "./locales/ja/common.json": () => import("./common-LT76LCN2.js"),
  "./locales/ja/email.json": () => import("./email-AAZLQJC5.js"),
  "./locales/ja/events.json": () => import("./events-L7SGKKAS.js"),
  "./locales/ja/payment.json": () => import("./payment-4N3VGZCZ.js"),
  "./locales/ja/product.json": () => import("./product-F7PS2JZ7.js"),
  "./locales/ja/referral.json": () => import("./referral-KHTCC5SD.js"),
  "./locales/ja/shipping.json": () => import("./shipping-ELMIRWUK.js"),
  "./locales/ja/stories.json": () => import("./stories-D4537HNQ.js"),
  "./locales/ja/upload.json": () => import("./upload-HUXPKWXE.js"),
  "./locales/ja/user.json": () => import("./user-LDFE7Z7M.js"),
  "./locales/ja/validation.json": () => import("./validation-C4DAKE6L.js"),
  "./locales/ko/ai.json": () => import("./ai-Z3L64FJK.js"),
  "./locales/ko/auth.json": () => import("./auth-6UVLZWUG.js"),
  "./locales/ko/chat.json": () => import("./chat-2RG436X2.js"),
  "./locales/ko/common.json": () => import("./common-CDEEUYFT.js"),
  "./locales/ko/email.json": () => import("./email-P4JIVBFK.js"),
  "./locales/ko/events.json": () => import("./events-WYZQLFIP.js"),
  "./locales/ko/fields.json": () => import("./fields-3OITECER.js"),
  "./locales/ko/payment.json": () => import("./payment-7QRMFU4S.js"),
  "./locales/ko/product.json": () => import("./product-BEFKV52X.js"),
  "./locales/ko/referral.json": () => import("./referral-6BPINNNT.js"),
  "./locales/ko/shipping.json": () => import("./shipping-F7IFYQSH.js"),
  "./locales/ko/stories.json": () => import("./stories-EI4ZPTUA.js"),
  "./locales/ko/upload.json": () => import("./upload-LE4KSPTK.js"),
  "./locales/ko/user.json": () => import("./user-GBJDCEWI.js"),
  "./locales/ko/validation.json": () => import("./validation-7RBIHS2C.js"),
  "./locales/lt-LT/ai.json": () => import("./ai-E4QUT4I6.js"),
  "./locales/lt-LT/auth.json": () => import("./auth-LWRGLFNP.js"),
  "./locales/lt-LT/categories.json": () => import("./categories-WKHL5HWL.js"),
  "./locales/lt-LT/chat.json": () => import("./chat-BPLEUCYE.js"),
  "./locales/lt-LT/common.json": () => import("./common-VLNLN2HD.js"),
  "./locales/lt-LT/errors.json": () => import("./errors-T4UB2XSF.js"),
  "./locales/lt-LT/events.json": () => import("./events-NPZELQJZ.js"),
  "./locales/lt-LT/payments.json": () => import("./payments-NF27UW7A.js"),
  "./locales/lt-LT/products.json": () => import("./products-ORHITSPQ.js"),
  "./locales/lt-LT/referral.json": () => import("./referral-CRUYFQFY.js"),
  "./locales/lt-LT/shipping.json": () => import("./shipping-GVIWTWX7.js"),
  "./locales/lt-LT/stories.json": () => import("./stories-42OVMVBZ.js"),
  "./locales/lt-LT/validation.json": () => import("./validation-X5YNAFMH.js"),
  "./locales/nl-NL/ai.json": () => import("./ai-AFOMSZS4.js"),
  "./locales/nl-NL/auth.json": () => import("./auth-KGYNS36A.js"),
  "./locales/nl-NL/categories.json": () => import("./categories-L7XZ6ZDR.js"),
  "./locales/nl-NL/chat.json": () => import("./chat-HRPTZNEQ.js"),
  "./locales/nl-NL/common.json": () => import("./common-B23RC6TJ.js"),
  "./locales/nl-NL/errors.json": () => import("./errors-MB5EBTL4.js"),
  "./locales/nl-NL/events.json": () => import("./events-4AOBLUUQ.js"),
  "./locales/nl-NL/payments.json": () => import("./payments-E3PUJ6JP.js"),
  "./locales/nl-NL/products.json": () => import("./products-YFQSMUBC.js"),
  "./locales/nl-NL/referral.json": () => import("./referral-Q3RRQ3DC.js"),
  "./locales/nl-NL/shipping.json": () => import("./shipping-LQI7T4G2.js"),
  "./locales/nl-NL/stories.json": () => import("./stories-ICPTA3JR.js"),
  "./locales/nl-NL/validation.json": () => import("./validation-YDDPDB3X.js"),
  "./locales/pl-PL/ai.json": () => import("./ai-W4RFYCJ3.js"),
  "./locales/pl-PL/categories.json": () => import("./categories-43F6OR75.js"),
  "./locales/pt-PT/auth.json": () => import("./auth-WD2DTUJC.js"),
  "./locales/pt-PT/chat.json": () => import("./chat-KZWWOLU7.js"),
  "./locales/pt-PT/common.json": () => import("./common-BTZOM6I4.js"),
  "./locales/pt-PT/errors.json": () => import("./errors-5P43ROYX.js"),
  "./locales/pt-PT/events.json": () => import("./events-AUSWGAK6.js"),
  "./locales/pt-PT/payments.json": () => import("./payments-5D5S6YJF.js"),
  "./locales/pt-PT/products.json": () => import("./products-N72FWKYW.js"),
  "./locales/pt-PT/referral.json": () => import("./referral-AW7SQI7C.js"),
  "./locales/pt-PT/shipping.json": () => import("./shipping-ZHGF3KG2.js"),
  "./locales/pt-PT/stories.json": () => import("./stories-K257JIMC.js"),
  "./locales/pt-PT/validation.json": () => import("./validation-XET5CIHA.js"),
  "./locales/vi/ai.json": () => import("./ai-CBYYIKNV.js"),
  "./locales/vi/auth.json": () => import("./auth-4SGSIC25.js"),
  "./locales/vi/chat.json": () => import("./chat-PDQV624B.js"),
  "./locales/vi/common.json": () => import("./common-JV3BBIOQ.js"),
  "./locales/vi/email.json": () => import("./email-OIONVP2E.js"),
  "./locales/vi/events.json": () => import("./events-23W2IIFJ.js"),
  "./locales/vi/fields.json": () => import("./fields-6ZZKOQGK.js"),
  "./locales/vi/payment.json": () => import("./payment-MIMJ6TKN.js"),
  "./locales/vi/product.json": () => import("./product-ZURE65LS.js"),
  "./locales/vi/referral.json": () => import("./referral-K37Y4HY7.js"),
  "./locales/vi/shipping.json": () => import("./shipping-AFK7OBRX.js"),
  "./locales/vi/stories.json": () => import("./stories-FZ5EFBYM.js"),
  "./locales/vi/validation.json": () => import("./validation-AOE4DINS.js"),
  "./locales/zh-CN/auth.json": () => import("./auth-EFPASFEN.js"),
  "./locales/zh-CN/chat.json": () => import("./chat-WI3OEIMU.js"),
  "./locales/zh-CN/common.json": () => import("./common-SHMMUBCQ.js"),
  "./locales/zh-CN/events.json": () => import("./events-OWPYG2VA.js"),
  "./locales/zh-CN/payment.json": () => import("./payment-VJILSRTY.js"),
  "./locales/zh-CN/product.json": () => import("./product-AFHKN2Y7.js"),
  "./locales/zh-CN/referral.json": () => import("./referral-5UT3MVJB.js"),
  "./locales/zh-CN/shipping.json": () => import("./shipping-GEHHR2L2.js"),
  "./locales/zh-CN/stories.json": () => import("./stories-OLRR6BMZ.js"),
  "./locales/zh-CN/validation.json": () => import("./validation-RL6HCAIS.js")
});

// src/i18n/config.ts
var I18N_NAMESPACES = [
  "common",
  "auth",
  "products",
  "chat",
  "payments",
  "shipping",
  "stories",
  "events",
  "referral",
  "emails",
  "errors",
  "validation",
  "cart",
  "preferences-common",
  "profiles-adults",
  "profiles-business",
  "profiles-children",
  "profiles",
  "growth",
  "sizes",
  "transactions",
  "shop"
];
var i18nConfig = {
  defaultLocale: DEFAULT_LOCALE,
  fallbackLocale: FALLBACK_LOCALE,
  namespaces: I18N_NAMESPACES,
  // Options i18next
  debug: false,
  interpolation: {
    escapeValue: false
    // React échappe automatiquement
  },
  // Détection de langue
  detection: {
    order: ["localStorage", "navigator", "htmlTag"],
    caches: ["localStorage"]
  },
  // Fallback
  fallbackNS: "common",
  // React
  react: {
    useSuspense: false
  }
};
var loadTranslations = async (locale) => {
  const translations = {};
  for (const namespace of I18N_NAMESPACES) {
    try {
      const module = await globImport_locales_json(`./locales/${locale}/${namespace}.json`);
      translations[namespace] = module.default || module;
    } catch {
      console.warn(`Missing translation file: ${locale}/${namespace}.json`);
      translations[namespace] = {};
    }
  }
  return translations;
};

// src/i18n/hooks/useTranslation.ts
import { useState as useState5, useEffect as useEffect5, useCallback as useCallback5, useRef as useRef4, useMemo as useMemo5 } from "react";
var translationCache = /* @__PURE__ */ new Map();
var missingKeys = /* @__PURE__ */ new Set();
function useTranslation2(options = {}) {
  const {
    namespace = "common",
    fallbackNamespace = "common",
    keyPrefix = "",
    onMissingKey
  } = options;
  const [locale, setLocale] = useState5("fr-FR");
  const [ready, setReady] = useState5(true);
  const [isLoading, setIsLoading] = useState5(false);
  const [error, setError] = useState5(null);
  const isMountedRef = useRef4(true);
  useEffect5(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const interpolate = useCallback5(
    (text, values) => {
      return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
        const value = values[key];
        return value !== void 0 ? String(value) : match;
      });
    },
    []
  );
  const getPlural = useCallback5(
    (count, _locale) => {
      if (count === 0) return "zero";
      if (count === 1) return "one";
      if (count === 2) return "two";
      return "other";
    },
    []
  );
  const exists = useCallback5(
    (key, ns) => {
      const fullKey = `${ns || namespace}:${keyPrefix}${key}`;
      const cacheKey = `${locale}:${fullKey}`;
      return translationCache.has(cacheKey) || !missingKeys.has(fullKey);
    },
    [locale, namespace, keyPrefix]
  );
  const t = useCallback5(
    (key, options2 = {}) => {
      const {
        count,
        defaultValue,
        interpolation = {},
        fallbackKey,
        skipInterpolation = false
      } = options2;
      try {
        const fullKey = `${keyPrefix}${key}`;
        const cacheKey = `${locale}:${namespace}:${fullKey}`;
        let translation = translationCache.get(cacheKey);
        if (!translation) {
          const missingKeyId = `${namespace}:${fullKey}`;
          missingKeys.add(missingKeyId);
          onMissingKey?.(fullKey, namespace, locale);
          if (fallbackKey) {
            const fallbackCacheKey = `${locale}:${namespace}:${keyPrefix}${fallbackKey}`;
            translation = translationCache.get(fallbackCacheKey);
          }
          if (!translation && fallbackNamespace !== namespace) {
            const fallbackNsCacheKey = `${locale}:${fallbackNamespace}:${fullKey}`;
            translation = translationCache.get(fallbackNsCacheKey);
          }
          if (!translation) {
            translation = defaultValue || fullKey;
          }
        }
        if (count !== void 0 && typeof translation === "string") {
          if (count === 0 && translation.includes("_zero")) {
            translation = translation.replace("_zero", "");
          } else if (count === 1 && translation.includes("_one")) {
            translation = translation.replace("_one", "");
          } else if (translation.includes("_other")) {
            translation = translation.replace("_other", "");
          }
        }
        let result = String(translation);
        if (!skipInterpolation && Object.keys(interpolation).length > 0) {
          result = interpolate(result, interpolation);
        }
        if (count !== void 0 && !skipInterpolation) {
          result = interpolate(result, { count, ...interpolation });
        }
        return result;
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error(String(err));
        setError(errorObj);
        return defaultValue || key;
      }
    },
    [
      locale,
      namespace,
      fallbackNamespace,
      keyPrefix,
      onMissingKey,
      interpolate,
      getPlural
    ]
  );
  const changeLanguage = useCallback5(
    async (newLocale) => {
      if (newLocale === locale) return;
      setIsLoading(true);
      setError(null);
      try {
        setLocale(newLocale);
        await new Promise((resolve) => setTimeout(resolve, 100));
        if (isMountedRef.current) {
          setReady(true);
          setIsLoading(false);
        }
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error(String(err));
        if (isMountedRef.current) {
          setError(errorObj);
          setIsLoading(false);
        }
        throw errorObj;
      }
    },
    [locale]
  );
  const getFixedT = useCallback5(
    (ns) => {
      const fixedNamespace = ns || namespace;
      return (key, options2 = {}) => {
        const cacheKey = `${locale}:${fixedNamespace}:${keyPrefix}${key}`;
        const cached = translationCache.get(cacheKey);
        return cached || options2.defaultValue || key;
      };
    },
    [locale, namespace, keyPrefix]
  );
  useEffect5(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("mode-app-locale");
      const browserLang = navigator.language;
      const initialLocale = stored || browserLang || "fr-FR";
      if (initialLocale !== locale) {
        setLocale(initialLocale);
      }
    }
  }, []);
  return useMemo5(
    () => ({
      t,
      locale,
      changeLanguage,
      ready,
      isLoading,
      error,
      exists,
      getFixedT
    }),
    [t, locale, changeLanguage, ready, isLoading, error, exists, getFixedT]
  );
}

// src/hooks/useResponsive.ts
import { useState as useState6, useEffect as useEffect6, useMemo as useMemo6, useCallback as useCallback6 } from "react";
var DEFAULT_BREAKPOINTS = {
  mobile: 768,
  // < 768px
  tablet: 1024,
  // 768px - 1024px
  desktop: 1920,
  // 1024px - 1920px
  ultrawide: 2560
  // > 1920px
};
var DEVICE_PATTERNS = {
  mobile: /iPhone|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i,
  tablet: /iPad|Android(?!.*Mobile)|Tablet|PlayBook|Silk/i,
  desktop: /Windows|Macintosh|Linux|CrOS/i
};
function useResponsive() {
  const [dimensions, setDimensions] = useState6(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0
  }));
  const [orientation, setOrientation] = useState6(() => {
    if (typeof window === "undefined") return "portrait";
    return window.innerWidth > window.innerHeight ? "landscape" : "portrait";
  });
  const [deviceInfo, setDeviceInfo] = useState6(() => ({
    isTouch: typeof window !== "undefined" && "ontouchstart" in window,
    pixelRatio: typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1
  }));
  const updateDimensions = useCallback6(() => {
    if (typeof window === "undefined") return;
    const newDimensions = {
      width: window.innerWidth,
      height: window.innerHeight
    };
    const newOrientation = newDimensions.width > newDimensions.height ? "landscape" : "portrait";
    setDimensions(newDimensions);
    setOrientation(newOrientation);
  }, []);
  const debouncedUpdateDimensions = useCallback6(() => {
    let timeoutId;
    const debounced = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateDimensions, 150);
    };
    return debounced;
  }, [updateDimensions]);
  useEffect6(() => {
    if (typeof window === "undefined") return;
    updateDimensions();
    const debouncedHandler = debouncedUpdateDimensions();
    window.addEventListener("resize", debouncedHandler, { passive: true });
    window.addEventListener("orientationchange", updateDimensions, { passive: true });
    return () => {
      window.removeEventListener("resize", debouncedHandler);
      window.removeEventListener("orientationchange", updateDimensions);
    };
  }, [updateDimensions, debouncedUpdateDimensions]);
  const responsiveValues = useMemo6(() => {
    const { width, height } = dimensions;
    const currentBreakpoint = width < DEFAULT_BREAKPOINTS.mobile ? "mobile" : width < DEFAULT_BREAKPOINTS.tablet ? "tablet" : width < DEFAULT_BREAKPOINTS.desktop ? "desktop" : "ultrawide";
    const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isMobileDevice = DEVICE_PATTERNS.mobile.test(userAgent);
    const isTabletDevice = DEVICE_PATTERNS.tablet.test(userAgent);
    let deviceType = "desktop";
    if (isMobileDevice) deviceType = "mobile";
    else if (isTabletDevice) deviceType = "tablet";
    else if (width > 3e3) deviceType = "tv";
    const isSmallMobile = width < 375;
    const isMediumMobile = width >= 375 && width < 414;
    const isLargeMobile = width >= 414 && width < DEFAULT_BREAKPOINTS.mobile;
    const isSmallTablet = width >= DEFAULT_BREAKPOINTS.mobile && width < 900;
    const isLargeTablet = width >= 900 && width < DEFAULT_BREAKPOINTS.tablet;
    const isSmallDesktop = width >= DEFAULT_BREAKPOINTS.tablet && width < 1440;
    const isLargeDesktop = width >= 1440 && width < DEFAULT_BREAKPOINTS.desktop;
    const is4K = width > 2560;
    return {
      // Breakpoints basiques
      isMobile: currentBreakpoint === "mobile",
      isTablet: currentBreakpoint === "tablet",
      isDesktop: currentBreakpoint === "desktop",
      isUltrawide: currentBreakpoint === "ultrawide",
      // Informations détaillées
      breakpoint: currentBreakpoint,
      width,
      height,
      // Orientation & aspect ratio
      orientation,
      isLandscape: orientation === "landscape",
      isPortrait: orientation === "portrait",
      // Device detection premium
      deviceType,
      isTouch: deviceInfo.isTouch,
      isRetina: deviceInfo.pixelRatio > 1,
      // Utilitaires avancés
      isSmallMobile,
      isMediumMobile,
      isLargeMobile,
      isSmallTablet,
      isLargeTablet,
      isSmallDesktop,
      isLargeDesktop,
      is4K
    };
  }, [dimensions, orientation, deviceInfo]);
  return responsiveValues;
}
function useBreakpoint() {
  const { isMobile, isTablet, isDesktop, breakpoint } = useResponsive();
  return { isMobile, isTablet, isDesktop, breakpoint };
}
function useBreakpointMatch(targetBreakpoint) {
  const { breakpoint } = useResponsive();
  return breakpoint === targetBreakpoint;
}
function useResponsiveValue(values) {
  const { breakpoint } = useResponsive();
  return values[breakpoint] || values.desktop || values.tablet || values.mobile;
}
function useResponsiveClasses(classes) {
  const { breakpoint } = useResponsive();
  return classes[breakpoint] || classes.default || "";
}

// src/config/pricing.ts
var FOUNDER_LIMIT = 50;
var PLAN_PRICING = {
  BASIC: {
    dbEnum: "BASIC",
    uiLabel: "Premium",
    shortDescription: "Premium \u2014 100 cr\xE9dits IA/mois, 10 vid\xE9os boutique, 1 boost article, 4 th\xE8mes",
    acquisition: {
      monthly: 4.99,
      yearly: 49.9,
      stripe: {
        monthly: "price_1TWrhuKRc5m4cy03MTBNoCof",
        yearly: "price_1TWrhuKRc5m4cy03Ktuy7B3j"
      }
    },
    postFounder: {
      monthly: 6.99,
      yearly: 69.9,
      stripe: {
        monthly: "price_1TWsLpKRc5m4cy03mXDbUQIj",
        yearly: "price_1TWsLqKRc5m4cy03wr8JuMdn"
      }
    }
  },
  PRO: {
    dbEnum: "PRO",
    uiLabel: "Pro Business",
    shortDescription: "Pro Business \u2014 400 cr\xE9dits IA/mois, 25 vid\xE9os, 3 boosts articles, th\xE8mes pro (STREET, LUXURY), badge PRO",
    acquisition: {
      monthly: 9.99,
      yearly: 99.9,
      stripe: {
        monthly: "price_1TWrknKRc5m4cy03XdHlKg8m",
        yearly: "price_1TWrknKRc5m4cy03PEJJ9wrl"
      }
    },
    postFounder: {
      monthly: 14.99,
      yearly: 149.9,
      stripe: {
        monthly: "price_1TWsJmKRc5m4cy03m8uJgNXu",
        yearly: "price_1TWsJmKRc5m4cy03pfBL4dHH"
      }
    }
  }
};
var CURRENT_PRICING_PHASE = "acquisition";
var getCurrentPrice = (plan, cycle) => {
  return PLAN_PRICING[plan][CURRENT_PRICING_PHASE][cycle];
};
var getCurrentStripePriceId = (plan, cycle) => {
  const id = PLAN_PRICING[plan][CURRENT_PRICING_PHASE].stripe[cycle];
  if (!id) {
    throw new Error(
      `Stripe Price ID manquant pour plan=${plan} cycle=${cycle} phase=${CURRENT_PRICING_PHASE}. V\xE9rifie packages/shared/src/config/pricing.ts.`
    );
  }
  return id;
};
var getPlanLabel = (plan) => {
  return PLAN_PRICING[plan].uiLabel;
};
var formatPlanPrice = (price) => {
  return `${price.toFixed(2).replace(".", ",")} \u20AC`;
};

// src/utils/subscriptionLimits.ts
var ALL_THEMES = [
  "VINTAGE",
  "MODERN",
  "LUXURY",
  "BOHO",
  "STREET",
  "PASTEL",
  "DARK",
  "Y2K"
];
var SUBSCRIPTION_LIMITS = {
  FREE: {
    themes_allowed: ["MODERN"],
    videos_per_shop: 1,
    boosts_per_month: 0,
    boost_type: "ARTICLE",
    boost_duration_days: 0,
    custom_css_enabled: false,
    instant_withdrawals_per_month: 1,
    price: 0
  },
  BASIC: {
    themes_allowed: ["VINTAGE", "MODERN", "BOHO", "PASTEL"],
    videos_per_shop: 10,
    boosts_per_month: 1,
    boost_type: "ARTICLE",
    boost_duration_days: 7,
    custom_css_enabled: false,
    instant_withdrawals_per_month: 2,
    // Prix lu depuis la source de vérité unique (config/pricing.ts).
    price: PLAN_PRICING.BASIC[CURRENT_PRICING_PHASE].monthly
  },
  PRO: {
    themes_allowed: [
      "VINTAGE",
      "MODERN",
      "BOHO",
      "PASTEL",
      "STREET",
      "LUXURY"
    ],
    videos_per_shop: 25,
    boosts_per_month: 3,
    boost_type: "ARTICLE",
    boost_duration_days: 7,
    custom_css_enabled: false,
    instant_withdrawals_per_month: 5,
    // Prix lu depuis la source de vérité unique (config/pricing.ts).
    price: PLAN_PRICING.PRO[CURRENT_PRICING_PHASE].monthly
  },
  // PREMIUM = enum legacy non vendu actuellement (pas de Stripe Price ID
  // en LIVE). Garde les limites pour compat historique mais price=0.
  PREMIUM: {
    themes_allowed: [...ALL_THEMES],
    videos_per_shop: 50,
    boosts_per_month: 1,
    boost_type: "BOUTIQUE",
    boost_duration_days: 30,
    custom_css_enabled: true,
    instant_withdrawals_per_month: -1,
    price: 0
  }
};
var loadSubscriptionTranslations = async (locale) => {
  try {
    const fs = __require("fs");
    const path = __require("path");
    const subscriptionPath = path.join(
      __dirname,
      `../../../apps/frontend/public/locales/${locale}/subscription.json`
    );
    if (fs.existsSync(subscriptionPath)) {
      return JSON.parse(fs.readFileSync(subscriptionPath, "utf8"));
    }
    const frPath = path.join(
      __dirname,
      "../../../apps/frontend/public/locales/fr-FR/subscription.json"
    );
    if (fs.existsSync(frPath)) {
      return JSON.parse(fs.readFileSync(frPath, "utf8"));
    }
    return null;
  } catch (error) {
    console.warn("Failed to load subscription translations:", error);
    return null;
  }
};
var getLocalizedPlanInfo = async (plan, locale = "fr-FR") => {
  try {
    const translations = await loadSubscriptionTranslations(locale);
    if (!translations?.plans) return null;
    const planKey = plan.toLowerCase();
    const planTranslations = translations.plans[planKey];
    if (!planTranslations) return null;
    return {
      name: planTranslations.name || plan,
      description: planTranslations.description || "",
      price: planTranslations.price || `${SUBSCRIPTION_LIMITS[plan].price}\u20AC/mois`,
      features: {
        unlimitedVideos: planTranslations.features?.unlimitedVideos || "Vid\xE9os illimit\xE9es",
        boosts: planTranslations.features?.boosts || "Boosts inclus",
        templates: planTranslations.features?.templates || "Templates de boutique",
        support: planTranslations.features?.support || "Support client",
        analytics: planTranslations.features?.analytics,
        customization: planTranslations.features?.customization
      }
    };
  } catch (error) {
    console.error("Error loading localized plan info:", error);
    return null;
  }
};
var getLocalizedBoostInfo = async (boostType, locale = "fr-FR") => {
  try {
    const translations = await loadSubscriptionTranslations(locale);
    if (!translations?.boosts?.type) return null;
    const typeKey = boostType.toLowerCase();
    const boostTranslations = translations.boosts.type[typeKey];
    if (!boostTranslations) return null;
    return {
      title: boostTranslations.title || boostType,
      description: boostTranslations.description || ""
    };
  } catch (error) {
    console.error("Error loading localized boost info:", error);
    return null;
  }
};
var getUserLimits = (subscription) => {
  if (subscription?.is_privileged_account) {
    return SUBSCRIPTION_LIMITS.PREMIUM;
  }
  const plan = subscription?.plan;
  if (plan && plan in SUBSCRIPTION_LIMITS) {
    return SUBSCRIPTION_LIMITS[plan];
  }
  return SUBSCRIPTION_LIMITS.FREE;
};
var canUserPerformAction = (subscription, action, context) => {
  const limits = getUserLimits(subscription);
  switch (action) {
    case "add_video":
      if (context?.currentVideoCount >= limits.videos_per_shop) {
        return { allowed: false, reason: "VIDEO_LIMIT_REACHED" };
      }
      break;
    case "boost_product":
      if (subscription?.boosts_used_this_month >= limits.boosts_per_month) {
        return { allowed: false, reason: "BOOST_LIMIT_REACHED" };
      }
      break;
    case "use_theme":
      if (!limits.themes_allowed.includes(context?.theme)) {
        return { allowed: false, reason: "THEME_NOT_ALLOWED" };
      }
      break;
    case "custom_css":
      if (!limits.custom_css_enabled) {
        return { allowed: false, reason: "CUSTOM_CSS_NOT_ALLOWED" };
      }
      break;
    case "instant_withdrawal":
      if (limits.instant_withdrawals_per_month !== -1 && subscription?.instant_withdrawals_used >= limits.instant_withdrawals_per_month) {
        return { allowed: false, reason: "WITHDRAWAL_LIMIT_REACHED" };
      }
      break;
  }
  return { allowed: true };
};
var canUserBoost = (subscription, targetType) => {
  const limits = getUserLimits(subscription);
  if (limits.boost_type !== targetType) {
    return {
      allowed: false,
      reason: targetType === "BOUTIQUE" ? "BOUTIQUE_BOOST_PREMIUM_REQUIRED" : "ARTICLE_BOOST_ONLY"
    };
  }
  const boostsUsed = subscription?.boosts_used_this_month || 0;
  const remainingBoosts = limits.boosts_per_month - boostsUsed;
  if (remainingBoosts <= 0) {
    return {
      allowed: false,
      reason: "BOOST_LIMIT_REACHED",
      remainingBoosts: 0
    };
  }
  return {
    allowed: true,
    remainingBoosts
  };
};
var calculatePlanValue = (plan) => {
  const limits = SUBSCRIPTION_LIMITS[plan];
  const glowpCost = limits.price;
  let vintedCost = 0;
  if (limits.boost_type === "ARTICLE") {
    vintedCost = limits.boosts_per_month * 1.95;
  } else if (limits.boost_type === "BOUTIQUE") {
    vintedCost = 4 * 6.95;
  }
  const savings = vintedCost - glowpCost;
  const savingsPercentage = glowpCost > 0 ? Math.round(savings / vintedCost * 100) : 0;
  return {
    vintedCost,
    glowpCost,
    savings,
    savingsPercentage
  };
};

// src/utils/sendcloudSanitize.ts
var NULL_SENTINELS = /* @__PURE__ */ new Set([
  "null",
  "none",
  "n/a",
  "na",
  "undefined",
  "nil",
  "-",
  "--"
]);
var sanitizeSendcloudValue = (value) => {
  if (value === null || value === void 0) return null;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  if (NULL_SENTINELS.has(trimmed.toLowerCase())) return null;
  return trimmed;
};
var buildSendcloudStreet = (houseNumber, street) => {
  const cleanHouse = sanitizeSendcloudValue(houseNumber);
  const cleanStreet = sanitizeSendcloudValue(street);
  if (!cleanHouse && !cleanStreet) return null;
  if (!cleanHouse) return cleanStreet;
  if (!cleanStreet) return cleanHouse;
  return `${cleanHouse} ${cleanStreet}`;
};
var normalizeSendcloudPoint = (raw) => {
  const houseNumber = sanitizeSendcloudValue(raw.house_number);
  const street = sanitizeSendcloudValue(raw.street);
  return {
    houseNumber,
    street,
    postalCode: sanitizeSendcloudValue(raw.postal_code),
    city: sanitizeSendcloudValue(raw.city),
    country: sanitizeSendcloudValue(raw.country),
    companyName: sanitizeSendcloudValue(raw.company_name),
    phone: sanitizeSendcloudValue(raw.phone),
    fullAddress: buildSendcloudStreet(houseNumber, street)
  };
};

// src/types/User.ts
var AuthProvider = /* @__PURE__ */ ((AuthProvider2) => {
  AuthProvider2["LOCAL"] = "LOCAL";
  AuthProvider2["GOOGLE"] = "GOOGLE";
  AuthProvider2["FACEBOOK"] = "FACEBOOK";
  AuthProvider2["APPLE"] = "APPLE";
  AuthProvider2["TWITTER"] = "TWITTER";
  AuthProvider2["LINKEDIN"] = "LINKEDIN";
  return AuthProvider2;
})(AuthProvider || {});
var TokenType = /* @__PURE__ */ ((TokenType2) => {
  TokenType2["ACCESS"] = "ACCESS";
  TokenType2["REFRESH"] = "REFRESH";
  TokenType2["EMAIL_VERIFICATION"] = "EMAIL_VERIFICATION";
  TokenType2["PASSWORD_RESET"] = "PASSWORD_RESET";
  TokenType2["TWO_FACTOR"] = "TWO_FACTOR";
  TokenType2["API_KEY"] = "API_KEY";
  return TokenType2;
})(TokenType || {});
var SessionStatus = /* @__PURE__ */ ((SessionStatus2) => {
  SessionStatus2["ACTIVE"] = "ACTIVE";
  SessionStatus2["EXPIRED"] = "EXPIRED";
  SessionStatus2["TERMINATED"] = "TERMINATED";
  SessionStatus2["SUSPICIOUS"] = "SUSPICIOUS";
  return SessionStatus2;
})(SessionStatus || {});
var DeviceType = /* @__PURE__ */ ((DeviceType2) => {
  DeviceType2["WEB"] = "WEB";
  DeviceType2["MOBILE_IOS"] = "MOBILE_IOS";
  DeviceType2["MOBILE_ANDROID"] = "MOBILE_ANDROID";
  DeviceType2["TABLET"] = "TABLET";
  DeviceType2["DESKTOP"] = "DESKTOP";
  DeviceType2["API"] = "API";
  return DeviceType2;
})(DeviceType || {});
var UserRole = /* @__PURE__ */ ((UserRole2) => {
  UserRole2["USER"] = "USER";
  UserRole2["PREMIUM"] = "PREMIUM";
  UserRole2["MODERATOR"] = "MODERATOR";
  UserRole2["ADMIN"] = "ADMIN";
  UserRole2["SUPER_ADMIN"] = "SUPER_ADMIN";
  return UserRole2;
})(UserRole || {});
var AuthEventType = /* @__PURE__ */ ((AuthEventType2) => {
  AuthEventType2["LOGIN_SUCCESS"] = "LOGIN_SUCCESS";
  AuthEventType2["LOGIN_FAILED"] = "LOGIN_FAILED";
  AuthEventType2["LOGOUT"] = "LOGOUT";
  AuthEventType2["PASSWORD_CHANGE"] = "PASSWORD_CHANGE";
  AuthEventType2["EMAIL_CHANGE"] = "EMAIL_CHANGE";
  AuthEventType2["ACCOUNT_LOCKED"] = "ACCOUNT_LOCKED";
  AuthEventType2["ACCOUNT_UNLOCKED"] = "ACCOUNT_UNLOCKED";
  AuthEventType2["SUSPICIOUS_ACTIVITY"] = "SUSPICIOUS_ACTIVITY";
  AuthEventType2["SOCIAL_LOGIN"] = "SOCIAL_LOGIN";
  AuthEventType2["TWO_FACTOR_ENABLED"] = "TWO_FACTOR_ENABLED";
  AuthEventType2["TWO_FACTOR_DISABLED"] = "TWO_FACTOR_DISABLED";
  return AuthEventType2;
})(AuthEventType || {});
var AUTH_CONSTANTS = {
  TOKEN_EXPIRY: {
    ACCESS: 15 * 60,
    // 15 minutes
    REFRESH: 7 * 24 * 60 * 60,
    // 7 days
    EMAIL_VERIFICATION: 24 * 60 * 60,
    // 24 hours
    PASSWORD_RESET: 60 * 60,
    // 1 hour
    TWO_FACTOR: 5 * 60
    // 5 minutes
  },
  SECURITY: {
    MAX_FAILED_ATTEMPTS: 5,
    LOCKOUT_DURATION: 30 * 60,
    // 30 minutes
    SESSION_TIMEOUT: 24 * 60 * 60,
    // 24 hours
    MAX_CONCURRENT_SESSIONS: 5,
    PASSWORD_MIN_LENGTH: 8,
    TOTP_WINDOW: 1,
    BACKUP_CODES_COUNT: 10
  },
  RATE_LIMITS: {
    LOGIN: { windowMs: 15 * 60 * 1e3, maxAttempts: 5 },
    REGISTER: { windowMs: 60 * 60 * 1e3, maxAttempts: 3 },
    PASSWORD_RESET: { windowMs: 60 * 60 * 1e3, maxAttempts: 3 },
    EMAIL_VERIFICATION: { windowMs: 60 * 60 * 1e3, maxAttempts: 3 }
  }
};

// src/types/FamilyProfile.ts
var FAMILY_PROFILE_VALIDATION = {
  profile_name: {
    minLength: 2,
    maxLength: 50,
    pattern: /^[a-zA-ZÀ-ÿ\s\-']+$/,
    forbidden: ["admin", "system", "test", "null", "undefined"]
  },
  age: {
    min: 0,
    max: 120,
    child_max: 17,
    adult_min: 18
  },
  avatar_url: {
    maxSize: 5 * 1024 * 1024,
    // 5MB
    allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    minDimensions: { width: 100, height: 100 },
    maxDimensions: { width: 2e3, height: 2e3 }
  },
  preferences: {
    max_favorite_colors: 10,
    max_favorite_brands: 20,
    max_favorite_styles: 15,
    max_clothing_sizes: 5,
    max_shoe_sizes: 3
  }
};
var getProfileTypeLabel = (type, locale = "fr-FR") => {
  const labels = {
    PARENT: {
      "fr-FR": "Profil principal",
      "en-US": "Main profile",
      "de-DE": "Hauptprofil",
      "es-ES": "Perfil principal",
      "it-IT": "Profilo principale",
      "nl-NL": "Hoofdprofiel",
      "pl-PL": "Profil g\u0142\xF3wny",
      "cs-CZ": "Hlavn\xED profil",
      "lt-LT": "Pagrindinis profilis"
    },
    CHILD: {
      "fr-FR": "Profil enfant",
      "en-US": "Child profile",
      "de-DE": "Kinderprofil",
      "es-ES": "Perfil infantil",
      "it-IT": "Profilo bambino",
      "nl-NL": "Kinderprofiel",
      "pl-PL": "Profil dziecka",
      "cs-CZ": "D\u011Btsk\xFD profil",
      "lt-LT": "Vaiko profilis"
    },
    BUYER: {
      "fr-FR": "Profil acheteur",
      "en-US": "Buyer profile",
      "de-DE": "K\xE4uferprofil",
      "es-ES": "Perfil comprador",
      "it-IT": "Profilo acquirente",
      "nl-NL": "Kopersprofiel",
      "pl-PL": "Profil kupuj\u0105cego",
      "cs-CZ": "N\xE1kupn\xED profil",
      "lt-LT": "Pirk\u0117jo profilis"
    },
    SELLER: {
      "fr-FR": "Profil vendeur",
      "en-US": "Seller profile",
      "de-DE": "Verk\xE4uferprofil",
      "es-ES": "Perfil vendedor",
      "it-IT": "Profilo venditore",
      "nl-NL": "Verkopersprofiel",
      "pl-PL": "Profil sprzedaj\u0105cego",
      "cs-CZ": "Prodejn\xED profil",
      "lt-LT": "Pardav\u0117jo profilis"
    }
  };
  return labels[type][locale] || labels[type]["fr-FR"];
};
var calculateProfileCompleteness = (profile) => {
  let score = 0;
  const maxScore = 100;
  if (profile.profile_name) score += 10;
  if (profile.age) score += 10;
  if (profile.avatar_url) score += 10;
  if (profile.size_preferences?.clothing_sizes?.length) score += 15;
  if (profile.color_preferences?.favorite?.length) score += 15;
  if (profile.brand_preferences?.favorite?.length) score += 15;
  if (profile.style_preferences?.favorite?.length) score += 15;
  if (profile.budget_limits?.max_price_tops > 0) score += 10;
  return Math.min(score, maxScore);
};
var getAgeCategory = (age) => {
  if (!age) return "unknown";
  if (age <= 2) return "baby";
  if (age <= 4) return "toddler";
  if (age <= 6) return "preschool";
  if (age <= 12) return "school_age";
  if (age <= 17) return "teen";
  return "adult";
};
var suggestSizesForAge = (age) => {
  return {
    clothing: getClothingSizesForAge(age),
    shoes: getShoeSizesForAge(age)
  };
};

// src/types/Categories.ts
var CATEGORY_VALIDATION = {
  slug: { minLength: 2, maxLength: 50, pattern: /^[a-z0-9-]+$/ },
  name: { minLength: 2, maxLength: 100 },
  description: { maxLength: 500 },
  maxDepth: 3,
  maxChildren: 50
};
var buildCategoryPath = (categories) => {
  return "/" + categories.map((cat) => cat.slug).join("/");
};
var getCategoryDepth = (category) => {
  return category.path.split("/").length - 1;
};

// src/types/Subscription.ts
var BOOST_CONSTRAINTS = {
  MIN_DURATION_DAYS: 1,
  MAX_DURATION_DAYS: 30,
  MAX_BOOSTS_PER_MONTH: 50,
  ARTICLE_MAX_DURATION: 7,
  BOUTIQUE_MAX_DURATION: 30
};
var SUBSCRIPTION_CONSTRAINTS = {
  MIN_PRICE: 0,
  MAX_PRICE: 999,
  MAX_VIDEOS_PER_SHOP: 100,
  MAX_THEMES_ALLOWED: 10
};
var isValidBoostType = (type) => {
  return ["ARTICLE", "BOUTIQUE"].includes(type);
};
var isValidSubscriptionPlan = (plan) => {
  return ["FREE", "BASIC", "PRO", "PREMIUM"].includes(plan);
};
var isValidSubscriptionStatus = (status) => {
  return ["ACTIVE", "CANCELED", "EXPIRED"].includes(status);
};

// src/types/Product.ts
var PRODUCT_COMMISSION_RATE = 5;
function canHaveMultipleQuantities(product) {
  return product.productType === "HANDMADE" && (product.stock ?? 0) > 1;
}
function isProductInStock(product) {
  if (product.productType === "SECONDHAND") {
    return product.status === "ACTIVE";
  }
  return (product.stock ?? 0) > 0;
}
function getMaxAvailableQuantity(product) {
  if (product.productType === "SECONDHAND") {
    return 1;
  }
  return product.stock ?? 0;
}
function getProductTypeLabel(productType) {
  const labels = {
    SECONDHAND: "Seconde main",
    HANDMADE: "Cr\xE9ation",
    NEW: "Neuf"
  };
  return labels[productType] ?? productType;
}
function getSellerSubTypeLabel(subType) {
  const labels = {
    CREATOR: "Cr\xE9atrice",
    SECOND_HAND: "Seconde main",
    NEW: "Boutique neuf",
    MIXED: "Multi-univers"
  };
  return labels[subType] ?? subType;
}
var CUSTOMIZATION_LIMITS = {
  MAX_FIELDS_PER_PRODUCT: 8,
  MAX_LABEL_LENGTH: 80,
  MAX_OPTIONS_PER_SELECT: 12,
  MAX_OPTION_LENGTH: 40,
  MAX_TEXT_RESPONSE_LENGTH: 500,
  MAX_TEXTAREA_RESPONSE_LENGTH: 2e3
};

// src/types/Shop.ts
var SHOP_THEMES = {
  vintage: {
    colors: {
      primary: "#8B4513",
      secondary: "#F5DEB3",
      accent: "#CD853F",
      background: "#FFF8DC",
      text: "#4A4A4A",
      border: "#DDD"
    },
    fontFamily: "serif",
    fontSize: "medium",
    headerStyle: "centered",
    productCardStyle: "detailed"
  },
  modern: {
    colors: {
      primary: "#2C3E50",
      secondary: "#34495E",
      accent: "#3498DB",
      background: "#FFFFFF",
      text: "#2C3E50",
      border: "#BDC3C7"
    },
    fontFamily: "sans-serif",
    fontSize: "medium",
    headerStyle: "minimal",
    productCardStyle: "simple"
  },
  luxury: {
    colors: {
      primary: "#000000",
      secondary: "#D4AF37",
      accent: "#C0C0C0",
      background: "#FFFFFF",
      text: "#000000",
      border: "#E8E8E8"
    },
    fontFamily: "serif",
    fontSize: "large",
    headerStyle: "full",
    productCardStyle: "detailed"
  },
  boho: {
    colors: {
      primary: "#B8860B",
      secondary: "#DEB887",
      accent: "#F4A460",
      background: "#FFF8E7",
      text: "#8B4513",
      border: "#D2B48C"
    },
    fontFamily: "serif",
    fontSize: "medium",
    headerStyle: "centered",
    productCardStyle: "detailed"
  },
  minimalist: {
    colors: {
      primary: "#333333",
      secondary: "#666666",
      accent: "#999999",
      background: "#FFFFFF",
      text: "#333333",
      border: "#EEEEEE"
    },
    fontFamily: "sans-serif",
    fontSize: "small",
    headerStyle: "minimal",
    productCardStyle: "minimal"
  },
  colorful: {
    colors: {
      primary: "#FF6B6B",
      secondary: "#4ECDC4",
      accent: "#45B7D1",
      background: "#F8F9FA",
      text: "#2C3E50",
      border: "#DEE2E6"
    },
    fontFamily: "sans-serif",
    fontSize: "medium",
    headerStyle: "full",
    productCardStyle: "simple"
  }
};
var SHOP_VALIDATION = {
  slug: {
    minLength: 3,
    maxLength: 50,
    pattern: /^[a-z0-9-]+$/,
    reserved: ["admin", "api", "shop", "user", "www", "mail", "support"]
  },
  name: { minLength: 2, maxLength: 100 },
  description: { maxLength: 500 },
  welcomeMessage: { maxLength: 200 },
  customCSS: { maxLength: 1e4 },
  // 10KB pour premium
  bannerImage: {
    maxSize: 2 * 1024 * 1024,
    // 2MB
    allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    dimensions: { width: 1200, height: 400 }
  },
  logoImage: {
    maxSize: 1 * 1024 * 1024,
    // 1MB
    allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    dimensions: { width: 200, height: 200 }
  }
};
var isValidHexColor = (color) => {
  return /^#[0-9A-F]{6}$/i.test(color);
};
var isValidShopSlug = (slug) => {
  return SHOP_VALIDATION.slug.pattern.test(slug) && !SHOP_VALIDATION.slug.reserved.includes(slug.toLowerCase());
};
var generateShopUrl = (slug) => {
  return `/shop/${slug}`;
};
var getShopThemePreview = (theme) => {
  return SHOP_THEMES[theme];
};

// src/types/Order.ts
var ORDER_VALIDATION = {
  notes: { maxLength: 500 },
  review: { maxLength: 1e3 },
  rating: { min: 1, max: 5 },
  refundReason: { maxLength: 500 }
};
var COMMISSION_RATE = 5;
var PAYMENT_PROCESSING_FEE_RATE = 2.9;
var getOrderStatusLabel = (status, locale) => {
  const labels = {
    PENDING: {
      "fr-FR": "En attente",
      "en-US": "Pending",
      "de-DE": "Ausstehend",
      "es-ES": "Pendiente",
      "it-IT": "In attesa",
      "nl-NL": "Wachtend",
      "pl-PL": "Oczekuje",
      "cs-CZ": "\u010Cekaj\xEDc\xED",
      "lt-LT": "Laukiama"
    },
    CONFIRMED: {
      "fr-FR": "Confirm\xE9e",
      "en-US": "Confirmed",
      "de-DE": "Best\xE4tigt",
      "es-ES": "Confirmado",
      "it-IT": "Confermato",
      "nl-NL": "Bevestigd",
      "pl-PL": "Potwierdzone",
      "cs-CZ": "Potvrzeno",
      "lt-LT": "Patvirtinta"
    },
    SHIPPED: {
      "fr-FR": "Exp\xE9di\xE9e",
      "en-US": "Shipped",
      "de-DE": "Versandt",
      "es-ES": "Enviado",
      "it-IT": "Spedito",
      "nl-NL": "Verzonden",
      "pl-PL": "Wys\u0142ane",
      "cs-CZ": "Odesl\xE1no",
      "lt-LT": "I\u0161si\u0173sta"
    },
    DELIVERED: {
      "fr-FR": "Livr\xE9e",
      "en-US": "Delivered",
      "de-DE": "Zugestellt",
      "es-ES": "Entregado",
      "it-IT": "Consegnato",
      "nl-NL": "Bezorgd",
      "pl-PL": "Dostarczone",
      "cs-CZ": "Doru\u010Deno",
      "lt-LT": "Pristatyta"
    },
    COMPLETED: {
      "fr-FR": "Termin\xE9e",
      "en-US": "Completed",
      "de-DE": "Abgeschlossen",
      "es-ES": "Completado",
      "it-IT": "Completato",
      "nl-NL": "Voltooid",
      "pl-PL": "Zako\u0144czone",
      "cs-CZ": "Dokon\u010Deno",
      "lt-LT": "U\u017Ebaigta"
    },
    CANCELLED: {
      "fr-FR": "Annul\xE9e",
      "en-US": "Cancelled",
      "de-DE": "Storniert",
      "es-ES": "Cancelado",
      "it-IT": "Annullato",
      "nl-NL": "Geannuleerd",
      "pl-PL": "Anulowane",
      "cs-CZ": "Zru\u0161eno",
      "lt-LT": "At\u0161aukta"
    },
    DISPUTED: {
      "fr-FR": "Litige",
      "en-US": "Disputed",
      "de-DE": "Streitfall",
      "es-ES": "Disputado",
      "it-IT": "Contestato",
      "nl-NL": "Geschil",
      "pl-PL": "Sporne",
      "cs-CZ": "Sporn\xE9",
      "lt-LT": "Gin\u010Dijama"
    },
    REFUNDED: {
      "fr-FR": "Rembours\xE9e",
      "en-US": "Refunded",
      "de-DE": "Erstattet",
      "es-ES": "Reembolsado",
      "it-IT": "Rimborsato",
      "nl-NL": "Terugbetaald",
      "pl-PL": "Zwr\xF3cone",
      "cs-CZ": "Vr\xE1ceno",
      "lt-LT": "Gr\u0105\u017Einta"
    }
  };
  return labels[status][locale] || labels[status]["en-US"];
};
var calculateCommission = (itemPrice, rate = COMMISSION_RATE) => {
  return Math.round(itemPrice * (rate / 100) * 100) / 100;
};
var calculatePaymentFee = (amount) => {
  return Math.round((amount * (PAYMENT_PROCESSING_FEE_RATE / 100) + 0.3) * 100) / 100;
};

// src/types/Chat.ts
var CHAT_VALIDATION = {
  message: {
    minLength: 1,
    maxLength: 2e3
  },
  offer: {
    minAmount: 1,
    maxDiscountPercent: 70
  },
  attachment: {
    maxSize: 10 * 1024 * 1024,
    // 10MB
    maxCount: 5,
    allowedTypes: [
      "image/jpeg",
      "image/png",
      "image/webp",
      "video/mp4",
      "video/webm",
      "audio/mp3",
      "audio/wav",
      "application/pdf"
    ]
  }
};
var MESSAGE_TEMPLATES = {
  GREETING: {
    "fr-FR": [
      "Bonjour ! Je suis int\xE9ress\xE9(e) par votre article.",
      "Salut ! Cet article est-il toujours disponible ?"
    ],
    "en-US": [
      "Hello! I'm interested in your item.",
      "Hi! Is this item still available?"
    ],
    "de-DE": [
      "Hallo! Ich interessiere mich f\xFCr Ihren Artikel.",
      "Hi! Ist dieser Artikel noch verf\xFCgbar?"
    ],
    "es-ES": [
      "\xA1Hola! Me interesa tu art\xEDculo.",
      "\xA1Hola! \xBFEste art\xEDculo sigue disponible?"
    ],
    "it-IT": [
      "Ciao! Sono interessato al tuo articolo.",
      "Ciao! Questo articolo \xE8 ancora disponibile?"
    ],
    "nl-NL": [
      "Hallo! Ik ben ge\xEFnteresseerd in je artikel.",
      "Hoi! Is dit artikel nog beschikbaar?"
    ],
    "pl-PL": [
      "Cze\u015B\u0107! Interesuje mnie Tw\xF3j artyku\u0142.",
      "Cze\u015B\u0107! Czy ten artyku\u0142 jest jeszcze dost\u0119pny?"
    ],
    "cs-CZ": [
      "Ahoj! Zaj\xEDm\xE1 m\u011B v\xE1\u0161 \u010Dl\xE1nek.",
      "Ahoj! Je tento \u010Dl\xE1nek st\xE1le dostupn\xFD?"
    ],
    "lt-LT": [
      "Sveiki! Mane domina j\u016Bs\u0173 prek\u0117.",
      "Sveiki! Ar \u0161i prek\u0117 vis dar prieinama?"
    ]
  },
  PRICE_QUESTION: {
    "fr-FR": [
      "Quel est votre meilleur prix ?",
      "Seriez-vous ouvert(e) \xE0 une n\xE9gociation ?"
    ],
    "en-US": ["What's your best price?", "Would you be open to negotiation?"],
    "de-DE": [
      "Was ist Ihr bester Preis?",
      "W\xE4ren Sie offen f\xFCr Verhandlungen?"
    ],
    "es-ES": ["\xBFCu\xE1l es tu mejor precio?", "\xBFEstar\xEDas abierto a negociar?"],
    "it-IT": ["Qual \xE8 il tuo miglior prezzo?", "Saresti aperto a negoziare?"],
    "nl-NL": [
      "Wat is je beste prijs?",
      "Zou je open staan voor onderhandeling?"
    ],
    "pl-PL": [
      "Jaka jest Twoja najlepsza cena?",
      "Czy by\u0142by\u015B otwarty na negocjacje?"
    ],
    "cs-CZ": [
      "Jak\xE1 je va\u0161e nejlep\u0161\xED cena?",
      "Byli byste otev\u0159eni vyjedn\xE1v\xE1n\xED?"
    ],
    "lt-LT": [
      "Koks j\u016Bs\u0173 geriausias pasi\u016Blymas?",
      "Ar b\u016Btum\u0117te atviri deryboms?"
    ]
  }
};
var getQuickReplies = (locale, context) => {
  if (context === "BUYER") {
    return [
      ...MESSAGE_TEMPLATES.GREETING[locale] || MESSAGE_TEMPLATES.GREETING["en-US"]
    ];
  } else {
    return [
      ...MESSAGE_TEMPLATES.PRICE_QUESTION[locale] || MESSAGE_TEMPLATES.PRICE_QUESTION["en-US"]
    ];
  }
};
var calculateOfferDiscount = (originalPrice, offerAmount) => {
  return Math.round((originalPrice - offerAmount) / originalPrice * 100);
};
var isOfferReasonable = (originalPrice, offerAmount) => {
  const discountPercent = calculateOfferDiscount(originalPrice, offerAmount);
  return discountPercent <= CHAT_VALIDATION.offer.maxDiscountPercent;
};

// src/types/Referral.ts
var REFERRAL_VALIDATION = {
  code: {
    minLength: 4,
    maxLength: 20,
    pattern: /^[A-Z0-9]+$/,
    reserved: ["ADMIN", "SUPPORT", "TEST", "PROMO", "SALE"]
  },
  reward: {
    minAmount: 1,
    maxAmount: 1e3,
    maxPercentage: 50
  },
  campaign: {
    minDuration: 1,
    // jour
    maxDuration: 365,
    // jours
    maxActivePerUser: 5
  }
};
var TIER_REQUIREMENTS = {
  BRONZE: {
    minReferrals: 0,
    minEarnings: 0,
    benefits: ["Lien de parrainage de base"]
  },
  SILVER: {
    minReferrals: 5,
    minEarnings: 100,
    benefits: ["QR Code personnalis\xE9", "Statistiques avanc\xE9es"]
  },
  GOLD: {
    minReferrals: 20,
    minEarnings: 500,
    benefits: ["R\xE9compenses bonus +10%", "Support prioritaire"]
  },
  PLATINUM: {
    minReferrals: 50,
    minEarnings: 2e3,
    benefits: ["R\xE9compenses bonus +25%", "Acc\xE8s API"]
  },
  DIAMOND: {
    minReferrals: 100,
    minEarnings: 5e3,
    benefits: ["R\xE9compenses bonus +50%", "Account manager d\xE9di\xE9"]
  }
};
var generateReferralCode = (firstName) => {
  const cleanName = firstName.replace(/[^A-Z]/gi, "").toUpperCase().slice(0, 4);
  const year = (/* @__PURE__ */ new Date()).getFullYear().toString().slice(-2);
  const random = Math.random().toString(36).substr(2, 4).toUpperCase();
  return `${cleanName}${year}${random}`;
};
var calculateTierProgress = (stats) => {
  const currentTier = stats.currentTier;
  if (currentTier === "DIAMOND") return 100;
  const nextTierKey = Object.keys(TIER_REQUIREMENTS)[Object.keys(TIER_REQUIREMENTS).indexOf(currentTier) + 1];
  const nextTierRequirements = TIER_REQUIREMENTS[nextTierKey];
  const referralsProgress = stats.successfulReferrals / nextTierRequirements.minReferrals * 100;
  const earningsProgress = stats.totalEarnings / nextTierRequirements.minEarnings * 100;
  return Math.min(Math.min(referralsProgress, earningsProgress), 100);
};
var formatRewardDescription = (type, amount, currency, locale = "fr-FR") => {
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency
  });
  switch (type) {
    case "CASHBACK":
      return `${formatter.format(amount)} rembours\xE9s`;
    case "STORE_CREDIT":
      return `${formatter.format(amount)} de cr\xE9dit boutique`;
    case "PERCENTAGE_DISCOUNT":
      return `${amount}% de r\xE9duction`;
    case "FIXED_AMOUNT":
      return `${formatter.format(amount)} de r\xE9duction`;
    case "FREE_SHIPPING":
      return "Livraison gratuite";
    case "COMMISSION_DISCOUNT":
      return `${amount}% de r\xE9duction sur commission`;
    case "PREMIUM_ACCESS":
      return `${amount} mois d'acc\xE8s premium`;
    default:
      return `R\xE9compense de ${formatter.format(amount)}`;
  }
};

// src/types/Stories.ts
var STORY_VALIDATION = {
  content: {
    maxDuration: 30,
    // secondes
    maxFileSize: 50 * 1024 * 1024,
    // 50MB
    allowedImageTypes: ["image/jpeg", "image/png", "image/webp"],
    allowedVideoTypes: ["video/mp4", "video/webm", "video/mov"],
    minWidth: 720,
    minHeight: 1280,
    aspectRatio: { min: 0.5, max: 2 }
  },
  text: {
    maxTextLength: 200,
    maxStickerCount: 10,
    allowedFonts: ["Arial", "Helvetica", "Times", "Courier", "Comic Sans"]
  },
  audience: {
    maxCustomUsers: 500,
    maxCountryFilters: 20
  }
};
var STORY_TEMPLATES = {
  PRODUCT_SHOWCASE: {
    backgroundColor: "#000000",
    textPosition: "BOTTOM",
    filterEffect: "CONTRAST",
    duration: 15
  },
  SALE_ANNOUNCEMENT: {
    backgroundColor: "#FF4444",
    textPosition: "CENTER",
    filterEffect: "BRIGHT",
    duration: 10
  },
  BEHIND_SCENES: {
    backgroundColor: "#F5F5F5",
    textPosition: "TOP",
    filterEffect: "WARM",
    duration: 20
  }
};
var getStoryExpirationTime = (createdAt) => {
  const created = new Date(createdAt);
  const expiry = new Date(created.getTime() + 24 * 60 * 60 * 1e3);
  return expiry.toISOString();
};
var isStoryExpired = (expiresAt) => {
  return /* @__PURE__ */ new Date() > new Date(expiresAt);
};
var calculateEngagementRate = (story) => {
  if (story.viewsCount === 0) return 0;
  const totalInteractions = story.likesCount + story.repliesCount + story.sharesCount;
  return totalInteractions / story.viewsCount * 100;
};
var getStoryDimensionsFromType = (contentType) => {
  return {
    width: 1080,
    height: 1920,
    // Format stories standard 9:16
    maxFileSize: contentType === "VIDEO" ? 50 * 1024 * 1024 : 10 * 1024 * 1024
  };
};

// src/types/Notifications.ts
var NOTIFICATION_VALIDATION = {
  title: { maxLength: 100 },
  message: { maxLength: 500 },
  actionLabel: { maxLength: 50 },
  maxDailyNotifications: { min: 1, max: 100 },
  batchSize: 1e3
  // Max notifications par batch
};
var getNotificationIcon = (type) => {
  const icons = {
    ORDER_CREATED: "\u{1F6CD}\uFE0F",
    ORDER_SHIPPED: "\u{1F4E6}",
    ORDER_DELIVERED: "\u2705",
    PAYMENT_SUCCESS: "\u{1F4B3}",
    PAYMENT_FAILED: "\u274C",
    MESSAGE_RECEIVED: "\u{1F4AC}",
    OFFER_RECEIVED: "\u{1F4B0}",
    OFFER_ACCEPTED: "\u{1F91D}",
    PRODUCT_LIKED: "\u2764\uFE0F",
    PRODUCT_SOLD: "\u{1F389}",
    NEW_FOLLOWER: "\u{1F465}",
    EVENT_REMINDER: "\u{1F4C5}",
    PRICE_DROP: "\u2B07\uFE0F",
    BACK_IN_STOCK: "\u{1F504}",
    SYSTEM_UPDATE: "\u{1F527}",
    SECURITY_ALERT: "\u{1F6E1}\uFE0F",
    MARKETING_PROMO: "\u{1F381}"
  };
  return icons[type] || "\u{1F514}";
};
var shouldSendNotification = (preferences, channel) => {
  if (!preferences[`${channel.toLowerCase()}Enabled`]) {
    return false;
  }
  const now = /* @__PURE__ */ new Date();
  const currentHour = now.getHours();
  const quietStart = preferences.quietHoursStart ? parseInt(preferences.quietHoursStart.split(":")[0]) : null;
  const quietEnd = preferences.quietHoursEnd ? parseInt(preferences.quietHoursEnd.split(":")[0]) : null;
  if (quietStart && quietEnd) {
    if (quietStart > quietEnd) {
      if (currentHour >= quietStart || currentHour < quietEnd) {
        return false;
      }
    } else {
      if (currentHour >= quietStart && currentHour < quietEnd) {
        return false;
      }
    }
  }
  return true;
};

// src/types/Cart.ts
var CART_CONSTANTS = {
  SHIPPING_QUOTE_TTL: 15 * 60 * 1e3,
  // 15 minutes
  SERVICE_POINT_SEARCH_RADIUS: 10,
  // km
  MAX_ITEMS_PER_VENDOR: 50,
  MIN_ORDER_VALUE_CENTS: 100,
  // 1€
  BUYER_PROTECTION: {
    FIXED_FEE_CENTS: 70,
    // 0.70€
    PERCENTAGE_FEE: 0.05,
    // 5%
    MAX_PERCENTAGE_FEE_CENTS: 500,
    // 5.00€
    MIN_TOTAL_PROTECTION_CENTS: 70
    // 0.70€
  }
};
var DELIVERY_METHOD_LABELS = {
  relay: "Point relais",
  locker: "Consigne automatique",
  home: "Domicile"
};
var CARRIER_LABELS = {
  MONDIAL_RELAY: "Mondial Relay",
  INPOST: "InPost",
  LA_POSTE: "La Poste",
  CHRONOPOST: "Chronopost",
  DPD: "DPD",
  OTHER: "Autre"
};
var validateCartForCheckout = (cart) => {
  const result = {
    isValid: true,
    errors: [],
    warnings: [],
    vendorValidation: []
  };
  if (!cart.itemsByVendor.length) {
    result.isValid = false;
    result.errors.push("Cart is empty");
    return result;
  }
  cart.itemsByVendor.forEach((vendor) => {
    const vendorResult = {
      vendorId: vendor.vendorId,
      isReady: true,
      errors: [],
      missingFields: []
    };
    if (!vendor.deliveryMethod) {
      vendorResult.isReady = false;
      vendorResult.missingFields.push("deliveryMethod");
    }
    if ((vendor.deliveryMethod?.type === "relay" || vendor.deliveryMethod?.type === "locker") && !vendor.selectedServicePoint) {
      vendorResult.isReady = false;
      vendorResult.missingFields.push("servicePoint");
    }
    if (vendor.deliveryMethod?.type === "home" && !vendor.homeAddress) {
      vendorResult.isReady = false;
      vendorResult.missingFields.push("homeAddress");
    }
    if (!vendor.shippingQuote) {
      vendorResult.isReady = false;
      vendorResult.missingFields.push("shippingQuote");
    }
    if (vendor.shippingQuote && CartUtils.isShippingQuoteExpired(vendor.lastQuoteAt)) {
      vendorResult.isReady = false;
      vendorResult.errors.push("Shipping quote expired, please refresh");
    }
    if (vendor.selectedServicePoint && vendor.shippingQuote) {
      const isCarrierCompatible = vendor.selectedServicePoint.carrier === vendor.shippingQuote.carrier || vendor.selectedServicePoint.carrier === "OTHER" || vendor.shippingQuote.carrier === "OTHER";
      if (!isCarrierCompatible) {
        vendorResult.isReady = false;
        vendorResult.errors.push(
          `Incompatible carrier: point=${vendor.selectedServicePoint.carrier}, quote=${vendor.shippingQuote.carrier}`
        );
      }
    }
    const unavailableItems = vendor.items.filter(
      (item) => !item.productSnapshot.isStillAvailable
    );
    if (unavailableItems.length > 0) {
      vendorResult.isReady = false;
      vendorResult.errors.push(
        `${unavailableItems.length} item(s) no longer available`
      );
    }
    if (!vendorResult.isReady) {
      result.isValid = false;
    }
    result.vendorValidation.push(vendorResult);
  });
  return result;
};
var CartUtils = {
  calculateBuyerProtectionCents: (subtotalCents) => {
    const fixed = CART_CONSTANTS.BUYER_PROTECTION.FIXED_FEE_CENTS;
    const percentage = Math.round(
      subtotalCents * CART_CONSTANTS.BUYER_PROTECTION.PERCENTAGE_FEE
    );
    const maxPercentage = CART_CONSTANTS.BUYER_PROTECTION.MAX_PERCENTAGE_FEE_CENTS;
    return Math.max(
      CART_CONSTANTS.BUYER_PROTECTION.MIN_TOTAL_PROTECTION_CENTS,
      fixed + Math.min(percentage, maxPercentage)
    );
  },
  calculateVendorTotalCents: (vendor) => {
    const protection = vendor.fees?.buyerProtectionCents || 0;
    const shipping = vendor.shippingCostCents || 0;
    return (vendor.subtotalCents || 0) + shipping + protection;
  },
  isVendorReady: (vendor) => {
    if (!vendor.deliveryMethod) return false;
    if ((vendor.deliveryMethod.type === "relay" || vendor.deliveryMethod.type === "locker") && !vendor.selectedServicePoint) {
      return false;
    }
    if (vendor.deliveryMethod.type === "home" && !vendor.homeAddress) {
      return false;
    }
    if (!vendor.shippingQuote) return false;
    if (vendor.selectedServicePoint && vendor.shippingQuote) {
      const isCarrierCompatible = vendor.selectedServicePoint.carrier === vendor.shippingQuote.carrier || vendor.selectedServicePoint.carrier === "OTHER" || vendor.shippingQuote.carrier === "OTHER";
      if (!isCarrierCompatible) return false;
    }
    const hasUnavailableItems = vendor.items.some(
      (item) => !item.productSnapshot.isStillAvailable
    );
    if (hasUnavailableItems) return false;
    return true;
  },
  formatDistance: (distanceKm) => {
    if (distanceKm < 1) {
      return `${Math.round(distanceKm * 1e3)}m`;
    }
    return `${distanceKm.toFixed(1)}km`;
  },
  estimateDeliveryDate: (quote) => {
    const now = /* @__PURE__ */ new Date();
    const { max, unit } = quote.eta;
    const ms = unit === "hours" ? max * 36e5 : unit === "days" ? max * 864e5 : max * 7 * 864e5;
    const deliveryDate = new Date(now.getTime() + ms);
    return deliveryDate.toISOString().split("T")[0];
  },
  formatPriceCents: (cents, _currency = "EUR") => {
    const euros = cents / 100;
    return `${euros.toFixed(2)}\u20AC`;
  },
  isShippingQuoteExpired: (lastQuoteAt) => {
    if (!lastQuoteAt) return true;
    const now = Date.now();
    const quoteTime = new Date(lastQuoteAt).getTime();
    return now - quoteTime > CART_CONSTANTS.SHIPPING_QUOTE_TTL;
  }
};

// src/constants/categories.ts
var BUSINESS_CONSTANTS = {
  COMMISSION_RATE: 5,
  PAYMENT_PROCESSING_FEE: 2.9,
  MAX_PRODUCT_IMAGES: 8,
  MAX_IMAGE_SIZE: 10 * 1024 * 1024,
  MAX_PRODUCT_TITLE_LENGTH: 100,
  MAX_PRODUCT_DESCRIPTION_LENGTH: 2e3,
  MAX_MESSAGE_LENGTH: 2e3,
  MAX_ATTACHMENTS_PER_MESSAGE: 5,
  OFFER_EXPIRY_HOURS: 48,
  MAX_DISCOUNT_PERCENT: 70,
  MAX_SHOP_NAME_LENGTH: 100,
  MAX_SHOP_DESCRIPTION_LENGTH: 500,
  MIN_SHOP_SLUG_LENGTH: 3,
  MAX_SHOP_SLUG_LENGTH: 50,
  MIN_EVENT_DURATION_MINUTES: 60,
  MAX_EVENT_DURATION_MINUTES: 480,
  MAX_EVENT_PARTICIPANTS: 1e3,
  MIN_EVENT_PARTICIPANTS: 10,
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  MIN_SEARCH_QUERY_LENGTH: 2,
  MAX_SEARCH_RESULTS: 1e3
};
var USER_PROFILES = [
  { id: "ADULT_WOMAN", translationKey: "woman", icon: "User" },
  { id: "ADULT_MAN", translationKey: "man", icon: "User" },
  { id: "CHILD_GIRL", translationKey: "girl", icon: "UserCircle" },
  { id: "CHILD_BOY", translationKey: "boy", icon: "UserCircle" },
  { id: "BABY_GIRL", translationKey: "baby_girl", icon: "Baby" },
  { id: "BABY_BOY", translationKey: "baby_boy", icon: "Baby" }
];
var CATEGORY_STRUCTURE = {
  ADULT_WOMAN: {
    mainCategories: [
      "clothing",
      "shoes",
      "bags",
      "accessories",
      "home",
      "books",
      "sport",
      "crafts",
      "handmade",
      "custom",
      "toys"
    ],
    subcategories: {
      clothing: {
        hauts: [
          "tshirt",
          "top",
          "polo",
          "chemise",
          "chemisier",
          "blouse",
          "body",
          "bustier",
          "crop-top"
        ],
        pulls: [
          "pull",
          "cardigan",
          "gilet",
          "sweat",
          "hoodie",
          "poncho",
          "cape"
        ],
        robes: [
          "robe-courte",
          "robe-midi",
          "robe-longue",
          "robe-maxi",
          "robe-cocktail",
          "robe-soiree",
          "robe-ceremonie",
          "robe-pull",
          "robe-chemise",
          "robe-tshirt"
        ],
        jupes: [
          "jupe-courte",
          "jupe-midi",
          "jupe-longue",
          "jupe-crayon",
          "jupe-plissee",
          "jupe-patineuse",
          "jupe-trapeze",
          "jupe-portefeuille"
        ],
        pantalons: [
          "jean-skinny",
          "jean-slim",
          "jean-droit",
          "jean-regular",
          "jean-boyfriend",
          "jean-mom",
          "jean-flare",
          "jean-bootcut",
          "jean-wide-leg",
          "pantalon-tailleur",
          "pantalon-cigarette",
          "pantalon-large",
          "pantalon-palazzo",
          "pantalon-carrot",
          "chino",
          "cargo",
          "pantalon-fluide",
          "pantalon-cuir",
          "legging",
          "tregging",
          "jogging"
        ],
        shorts: [
          "short-jean",
          "short-tailleur",
          "short-chino",
          "short-cycliste",
          "bermuda"
        ],
        combinaisons: [
          "combinaison-pantalon",
          "combinaison-short",
          "salopette-jean",
          "salopette-short",
          "combi-pilote"
        ],
        manteaux: [
          "manteau",
          "caban",
          "trench",
          "parka",
          "doudoune",
          "blouson-cuir",
          "blouson-jean",
          "blouson-bomber",
          "blouson-teddy",
          "perfecto",
          "blazer",
          "veste-tailleur",
          "veste-costume",
          "kimono"
        ],
        lingerie: [
          "soutien-gorge",
          "bralette",
          "triangle",
          "bandeau-lingerie",
          "culotte",
          "string",
          "shorty",
          "tanga",
          "boxer-femme",
          "body-lingerie",
          "nuisette",
          "deshabille",
          "kimono-satin",
          "pyjama",
          "chemise-nuit",
          "ensemble-lingerie"
        ],
        maillots: [
          "maillot-1-piece",
          "maillot-2-pieces",
          "bikini",
          "tankini",
          "monokini"
        ],
        sport: [
          "brassiere-sport",
          "top-sport",
          "legging-sport",
          "short-sport",
          "pantalon-sport",
          "veste-sport",
          "coupe-vent",
          "ensemble-sport"
        ],
        maternite: [
          "jean-grossesse",
          "pantalon-grossesse",
          "legging-grossesse",
          "jupe-grossesse",
          "robe-grossesse",
          "top-grossesse",
          "pull-grossesse",
          "soutien-gorge-allaitement",
          "body-allaitement"
        ],
        ensembles: [
          "tailleur-jupe",
          "tailleur-pantalon",
          "ensemble-coordonne",
          "twinset"
        ],
        "tenues-religieuses": [
          "abaya",
          "niqab",
          "hijab",
          "djellaba",
          "kaftan",
          "caftan",
          "jilbab",
          "khimar",
          "ample-dress"
        ],
        "tenues-ceremonie-monde": [
          "sari",
          "lehenga",
          "salwar-kameez",
          "wax",
          "boubou",
          "dashiki",
          "hanbok",
          "ao-dai",
          "kimono-ceremonie",
          "robe-traditionnelle"
        ]
      },
      shoes: {
        baskets: [
          "baskets-basses",
          "baskets-montantes",
          "sneakers-running",
          "sneakers-lifestyle"
        ],
        escarpins: ["escarpins", "compensees"],
        sandales: [
          "sandales-talons",
          "sandales-plates",
          "nu-pieds",
          "tongs",
          "claquettes",
          "espadrilles"
        ],
        bottes: [
          "bottines",
          "bottines-chelsea",
          "bottines-western",
          "bottes-hautes",
          "bottes-cavalieres",
          "cuissardes",
          "bottes-pluie",
          "bottes-neige"
        ],
        ballerines: ["ballerines", "babies"],
        mocassins: ["mocassins", "derbies", "loafers"],
        mules: ["mules-talons", "mules-plates", "sabots"]
      },
      bags: {
        "sacs-main": [
          "sac-main",
          "sac-epaule",
          "sac-porte-main",
          "cabas",
          "tote-bag",
          "panier",
          "sac-bowling",
          "sac-docteur",
          "sac-seau"
        ],
        bandouliere: ["sac-bandouliere", "besace", "sacoche", "sac-cartable"],
        pochettes: ["pochette-soiree", "clutch", "minaudiere"],
        "sacs-dos": ["sac-dos", "sac-dos-mini"],
        voyage: [
          "sac-voyage",
          "sac-weekend",
          "valise-cabine",
          "valise",
          "vanity-case",
          "trousse"
        ],
        maroquinerie: ["portefeuille", "porte-monnaie", "porte-carte"]
      },
      accessories: {
        bijoux: [
          "collier",
          "sautoir",
          "ras-cou",
          "pendentif",
          "bracelet",
          "jonc",
          "manchette",
          "bague",
          "chevaliere",
          "boucles-oreilles-puces",
          "boucles-oreilles-creoles",
          "boucles-oreilles-pendantes",
          "broche",
          "parure"
        ],
        echarpes: [
          "echarpe",
          "foulard",
          "cheche",
          "etole",
          "pashmina",
          "snood"
        ],
        ceintures: ["ceinture-cuir", "ceinture-tissu", "ceinture-chaine"],
        chapeaux: [
          "chapeau",
          "capeline",
          "bob",
          "casquette",
          "beret",
          "bonnet",
          "bandeau",
          "serre-tete"
        ],
        lunettes: ["lunettes-soleil", "lunettes-vue"],
        gants: ["gants", "mitaines"],
        montres: ["montre", "montre-connectee"]
      },
      home: {
        decoration: [
          "bougie",
          "vase",
          "cadre-photo",
          "miroir",
          "horloge",
          "statue",
          "figurine",
          "photophore",
          "guirlande",
          "tableau",
          "affiche",
          "poster"
        ],
        linge: [
          "coussin",
          "plaid",
          "couverture",
          "nappe",
          "serviette",
          "torchon",
          "rideau",
          "housse-coussin",
          "drap",
          "parure-lit",
          "tapis",
          "tapis-bain"
        ],
        rangement: [
          "panier-rangement",
          "boite",
          "organisateur",
          "porte-bijoux",
          "porte-manteau",
          "patere",
          "etagere"
        ],
        papeterie: [
          "carnet",
          "agenda",
          "journal-intime",
          "stylo",
          "marque-page",
          "carte-postale"
        ],
        jardin: [
          "pot-fleur",
          "jardiniere",
          "arrosoir",
          "decoration-jardin",
          "lanterne"
        ],
        luminaires: [
          "lampe-poser",
          "lampe-chevet",
          "guirlande-lumineuse",
          "bougeoir",
          "applique"
        ]
      },
      books: {
        romans: [
          "roman",
          "polar",
          "thriller",
          "science-fiction",
          "fantasy",
          "romance",
          "historique",
          "classique",
          "contemporain",
          "nouvelle"
        ],
        "bien-etre": [
          "developpement-personnel",
          "meditation",
          "yoga",
          "sante",
          "psychologie",
          "spiritualite"
        ],
        art: [
          "livre-art",
          "livre-mode",
          "livre-photo",
          "beau-livre",
          "livre-design",
          "livre-architecture"
        ],
        cuisine: [
          "livre-cuisine",
          "livre-patisserie",
          "livre-vin",
          "livre-vegan"
        ],
        voyage: ["guide-voyage", "recit-voyage", "atlas", "carnet-voyage"],
        jeunesse: [
          "album-illustre",
          "conte",
          "livre-eveil",
          "livre-ado",
          "documentaire-jeunesse"
        ],
        bd: ["bd", "manga", "comics", "graphic-novel"],
        scolaire: [
          "manuel-scolaire",
          "cahier-vacances",
          "annale",
          "livre-parascolaire",
          "dictionnaire",
          "encyclopedie"
        ],
        pratique: [
          "livre-bricolage",
          "livre-jardinage",
          "livre-couture",
          "livre-diy",
          "livre-informatique",
          "langue-etrangere"
        ],
        magazines: [
          "magazine-mode",
          "magazine-cuisine",
          "magazine-deco",
          "magazine-sport",
          "magazine-actualite",
          "magazine-science",
          "magazine-jeunesse",
          "revue",
          "journal"
        ],
        biographie: ["biographie", "autobiographie", "memoires", "temoignage"]
      },
      sport: {
        fitness: [
          "legging-sport",
          "brassiere-sport",
          "top-sport",
          "short-sport",
          "ensemble-sport"
        ],
        running: ["chaussures-running", "tenue-running", "accessoires-running"],
        yoga: ["tapis-yoga", "tenue-yoga", "accessoires-yoga"],
        natation: [
          "maillot-sport",
          "lunettes-natation",
          "bonnet-natation",
          "serviette-microfibre"
        ],
        randonnee: [
          "chaussures-randonnee",
          "sac-randonnee",
          "vetements-randonnee",
          "baton-marche"
        ],
        ski: [
          "combinaison-ski",
          "pantalon-ski",
          "veste-ski",
          "accessoires-ski"
        ],
        velo: ["maillot-velo", "cuissard", "casque-velo", "accessoires-velo"],
        equipement: [
          "gourde",
          "sac-sport",
          "tapis-sol",
          "elastique-fitness",
          "halteres",
          "corde-sauter"
        ]
      },
      crafts: {
        couture: [
          "machine-coudre",
          "tissu",
          "patron",
          "fil-couture",
          "mercerie",
          "kit-couture"
        ],
        crochet: [
          "laine-crochet",
          "crochet-outil",
          "kit-crochet",
          "amigurumi-patron",
          "fil-coton"
        ],
        tricot: [
          "laine-tricot",
          "aiguilles-tricoter",
          "kit-tricot",
          "fil-mohair",
          "fil-merinos"
        ],
        broderie: [
          "kit-broderie",
          "toile-broder",
          "fil-broder",
          "tambour-broderie",
          "motif-broderie"
        ],
        scrapbooking: [
          "album-scrapbooking",
          "papier-scrapbooking",
          "stickers-scrapbooking",
          "tampons",
          "perforatrice",
          "washi-tape"
        ],
        peinture: [
          "peinture-acrylique",
          "peinture-huile",
          "peinture-aquarelle",
          "toile-peinture",
          "pinceau",
          "chevalet"
        ],
        dessin: [
          "crayon-couleur",
          "feutre-art",
          "pastel",
          "carnet-croquis",
          "marqueur"
        ],
        poterie: [
          "argile",
          "tour-potier",
          "kit-poterie",
          "outils-modelage",
          "email-ceramique"
        ],
        macrame: [
          "corde-macrame",
          "kit-macrame",
          "support-macrame",
          "anneau-macrame"
        ],
        enfant: [
          "pate-modeler",
          "perles",
          "kit-creatif",
          "coloriage",
          "peinture-enfant",
          "gommettes",
          "sable-magique"
        ]
      },
      handmade: {
        bijoux: [
          "collier-fait-main",
          "bracelet-fait-main",
          "boucles-oreilles-fait-main",
          "bague-fait-main"
        ],
        decoration: [
          "bougie-artisanale",
          "poterie",
          "cadre-fait-main",
          "objet-deco-fait-main",
          "crochet-deco",
          "macrame"
        ],
        vetements: [
          "tricot-fait-main",
          "crochet",
          "crochet-vetement",
          "couture-fait-main",
          "broderie"
        ],
        accessoires: [
          "sac-fait-main",
          "echarpe-fait-main",
          "bonnet-tricote",
          "pochette-fait-main",
          "crochet-accessoire"
        ],
        peluches: [
          "amigurumi",
          "crochet-peluche",
          "doudou-crochet",
          "peluche-fait-main"
        ],
        bebe: [
          "doudou-fait-main",
          "vetement-bebe-fait-main",
          "couverture-bebe",
          "mobile-bebe"
        ],
        papeterie: [
          "carnet-fait-main",
          "carte-fait-main",
          "stickers-fait-main",
          "marque-page-fait-main"
        ]
      },
      custom: {
        vetements: [
          "tshirt-personnalise",
          "sweat-personnalise",
          "hoodie-personnalise",
          "body-personnalise",
          "tablier-personnalise"
        ],
        sacs: [
          "tote-bag-personnalise",
          "pochette-personnalisee",
          "sac-dos-personnalise",
          "trousse-personnalisee"
        ],
        bijoux: [
          "collier-prenom",
          "bracelet-grave",
          "bague-initiale",
          "medaille-gravee",
          "bijou-personnalise"
        ],
        decoration: [
          "coussin-personnalise",
          "mug-personnalise",
          "affiche-prenom",
          "cadre-personnalise",
          "bougie-personnalisee",
          "poster-personnalise"
        ],
        papeterie: [
          "carnet-personnalise",
          "agenda-personnalise",
          "stickers-personnalises",
          "faire-part-personnalise",
          "carte-personnalisee"
        ],
        accessoires: [
          "casquette-personnalisee",
          "coque-telephone-personnalisee",
          "porte-cle-personnalise",
          "badge-personnalise"
        ],
        bebe: [
          "doudou-brode",
          "bavoir-personnalise",
          "cape-bain-prenom",
          "gigoteuse-personnalisee",
          "attache-tetine-personnalisee"
        ],
        cadeaux: [
          "coffret-cadeau-personnalise",
          "kit-personnalise",
          "boite-souvenir",
          "album-personnalise"
        ]
      },
      toys: {
        jeux: [
          "jeu-societe",
          "puzzle",
          "jeu-cartes",
          "jeu-construction",
          "jeu-educatif"
        ],
        jouets: [
          "poupee",
          "figurine-jouet",
          "peluche",
          "doudou",
          "jouet-bois",
          "jouet-eveil",
          "dinette",
          "maison-poupee"
        ],
        "jeux-exterieur": [
          "velo-enfant",
          "trottinette",
          "draisienne",
          "rollers",
          "jeu-plein-air"
        ]
      }
    }
  },
  ADULT_MAN: {
    mainCategories: [
      "clothing",
      "shoes",
      "bags",
      "accessories",
      "home",
      "books",
      "sport",
      "crafts",
      "handmade",
      "custom",
      "toys"
    ],
    subcategories: {
      clothing: {
        hauts: [
          "tshirt",
          "polo",
          "henley",
          "debardeur",
          "chemise-casual",
          "chemise-jean",
          "chemise-flanelle",
          "chemise-lin",
          "chemise-costume",
          "chemise-smoking"
        ],
        pulls: ["pull", "cardigan", "gilet-maille", "sweat", "hoodie"],
        pantalons: [
          "jean-slim",
          "jean-droit",
          "jean-regular",
          "jean-straight",
          "jean-relaxed",
          "jean-tapered",
          "pantalon-smoking",
          "chino-slim",
          "chino-regular",
          "cargo",
          "pantalon-toile",
          "pantalon-velours",
          "jogging",
          "pantalon-survetement"
        ],
        shorts: [
          "short-jean",
          "short-chino",
          "short-cargo",
          "short-toile",
          "bermuda"
        ],
        combinaisons: [
          "combinaison-pantalon",
          "combinaison-short",
          "salopette"
        ],
        manteaux: [
          "manteau",
          "caban",
          "duffle-coat",
          "trench",
          "parka",
          "doudoune",
          "blouson-cuir",
          "blouson-aviateur",
          "perfecto",
          "blouson-jean",
          "blouson-bomber",
          "blouson-harrington",
          "blouson-teddy",
          "blazer"
        ],
        costumes: [
          "costume-2-pieces",
          "costume-3-pieces",
          "smoking",
          "veste-costume",
          "pantalon-costume",
          "gilet-costume"
        ],
        "sous-vetements": [
          "boxer-long",
          "boxer-court",
          "calecon",
          "slip",
          "maillot-corps",
          "chaussettes"
        ],
        pyjamas: ["pyjama", "pyjama-short", "robe-chambre", "pantalon-pyjama"],
        maillots: ["short-bain", "slip-bain", "boxer-bain"],
        sport: [
          "tshirt-sport",
          "debardeur-sport",
          "short-sport",
          "pantalon-sport",
          "veste-sport",
          "coupe-vent-sport",
          "ensemble-survetement"
        ],
        "tenues-religieuses": [
          "djellaba-homme",
          "kaftan-homme",
          "boubou-homme",
          "qamis",
          "thobe",
          "dashiki-homme",
          "tenue-traditionnelle-homme"
        ]
      },
      shoes: {
        baskets: [
          "baskets-basses",
          "baskets-montantes",
          "sneakers-cuir",
          "sneakers-toile",
          "running",
          "training",
          "basketball"
        ],
        ville: ["richelieu", "derby", "oxford", "mocassins", "loafers"],
        bottes: [
          "boots-chelsea",
          "boots-chukka",
          "boots-desert",
          "bottines-lacets",
          "bottes-cavalieres",
          "bottes-motardes"
        ],
        sandales: ["sandales-cuir", "sandales-sport", "tongs", "claquettes"]
      },
      bags: {
        "sacs-dos": ["sac-dos-ordinateur", "sac-dos-ville", "sac-dos-sport"],
        sacoches: [
          "sacoche-ordinateur",
          "besace",
          "messenger-bag",
          "sac-bandouliere"
        ],
        voyage: [
          "sac-voyage",
          "sac-weekend",
          "sac-sport",
          "valise-cabine",
          "valise",
          "housse-costume"
        ],
        business: ["mallette", "porte-documents", "attache-case"],
        maroquinerie: ["portefeuille", "porte-carte", "porte-monnaie"]
      },
      accessories: {
        montres: [
          "montre-automatique",
          "montre-quartz",
          "montre-chronographe",
          "montre-connectee"
        ],
        ceintures: [
          "ceinture-cuir",
          "ceinture-toile",
          "ceinture-tressee",
          "bretelles"
        ],
        cravates: ["cravate", "noeud-papillon", "pochette-costume"],
        echarpes: ["echarpe", "cheche", "snood"],
        chapeaux: ["casquette", "bob", "bonnet", "chapeau"],
        lunettes: ["lunettes-soleil", "lunettes-vue"],
        gants: ["gants"],
        bijoux: [
          "bracelet",
          "collier",
          "bague-chevaliere",
          "boutons-manchette"
        ]
      },
      home: {
        decoration: [
          "bougie",
          "vase",
          "cadre-photo",
          "miroir",
          "horloge",
          "statue",
          "figurine",
          "photophore",
          "guirlande",
          "tableau",
          "affiche",
          "poster"
        ],
        linge: [
          "coussin",
          "plaid",
          "couverture",
          "nappe",
          "serviette",
          "torchon",
          "rideau",
          "housse-coussin",
          "drap",
          "parure-lit",
          "tapis",
          "tapis-bain"
        ],
        rangement: [
          "panier-rangement",
          "boite",
          "organisateur",
          "porte-bijoux",
          "porte-manteau",
          "patere",
          "etagere"
        ],
        papeterie: [
          "carnet",
          "agenda",
          "journal-intime",
          "stylo",
          "marque-page",
          "carte-postale"
        ],
        jardin: [
          "pot-fleur",
          "jardiniere",
          "arrosoir",
          "decoration-jardin",
          "lanterne"
        ],
        luminaires: [
          "lampe-poser",
          "lampe-chevet",
          "guirlande-lumineuse",
          "bougeoir",
          "applique"
        ]
      },
      books: {
        romans: [
          "roman",
          "polar",
          "thriller",
          "science-fiction",
          "fantasy",
          "romance",
          "historique",
          "classique",
          "contemporain",
          "nouvelle"
        ],
        "bien-etre": [
          "developpement-personnel",
          "meditation",
          "yoga",
          "sante",
          "psychologie",
          "spiritualite"
        ],
        art: [
          "livre-art",
          "livre-mode",
          "livre-photo",
          "beau-livre",
          "livre-design",
          "livre-architecture"
        ],
        cuisine: [
          "livre-cuisine",
          "livre-patisserie",
          "livre-vin",
          "livre-vegan"
        ],
        voyage: ["guide-voyage", "recit-voyage", "atlas", "carnet-voyage"],
        jeunesse: [
          "album-illustre",
          "conte",
          "livre-eveil",
          "livre-ado",
          "documentaire-jeunesse"
        ],
        bd: ["bd", "manga", "comics", "graphic-novel"],
        scolaire: [
          "manuel-scolaire",
          "cahier-vacances",
          "annale",
          "livre-parascolaire",
          "dictionnaire",
          "encyclopedie"
        ],
        pratique: [
          "livre-bricolage",
          "livre-jardinage",
          "livre-couture",
          "livre-diy",
          "livre-informatique",
          "langue-etrangere"
        ],
        magazines: [
          "magazine-mode",
          "magazine-cuisine",
          "magazine-deco",
          "magazine-sport",
          "magazine-actualite",
          "magazine-science",
          "magazine-jeunesse",
          "revue",
          "journal"
        ],
        biographie: ["biographie", "autobiographie", "memoires", "temoignage"]
      },
      sport: {
        fitness: [
          "legging-sport",
          "brassiere-sport",
          "top-sport",
          "short-sport",
          "ensemble-sport"
        ],
        running: ["chaussures-running", "tenue-running", "accessoires-running"],
        yoga: ["tapis-yoga", "tenue-yoga", "accessoires-yoga"],
        natation: [
          "maillot-sport",
          "lunettes-natation",
          "bonnet-natation",
          "serviette-microfibre"
        ],
        randonnee: [
          "chaussures-randonnee",
          "sac-randonnee",
          "vetements-randonnee",
          "baton-marche"
        ],
        ski: [
          "combinaison-ski",
          "pantalon-ski",
          "veste-ski",
          "accessoires-ski"
        ],
        velo: ["maillot-velo", "cuissard", "casque-velo", "accessoires-velo"],
        equipement: [
          "gourde",
          "sac-sport",
          "tapis-sol",
          "elastique-fitness",
          "halteres",
          "corde-sauter"
        ]
      },
      crafts: {
        couture: [
          "machine-coudre",
          "tissu",
          "patron",
          "fil-couture",
          "mercerie",
          "kit-couture"
        ],
        crochet: [
          "laine-crochet",
          "crochet-outil",
          "kit-crochet",
          "amigurumi-patron",
          "fil-coton"
        ],
        tricot: [
          "laine-tricot",
          "aiguilles-tricoter",
          "kit-tricot",
          "fil-mohair",
          "fil-merinos"
        ],
        broderie: [
          "kit-broderie",
          "toile-broder",
          "fil-broder",
          "tambour-broderie",
          "motif-broderie"
        ],
        scrapbooking: [
          "album-scrapbooking",
          "papier-scrapbooking",
          "stickers-scrapbooking",
          "tampons",
          "perforatrice",
          "washi-tape"
        ],
        peinture: [
          "peinture-acrylique",
          "peinture-huile",
          "peinture-aquarelle",
          "toile-peinture",
          "pinceau",
          "chevalet"
        ],
        dessin: [
          "crayon-couleur",
          "feutre-art",
          "pastel",
          "carnet-croquis",
          "marqueur"
        ],
        poterie: [
          "argile",
          "tour-potier",
          "kit-poterie",
          "outils-modelage",
          "email-ceramique"
        ],
        macrame: [
          "corde-macrame",
          "kit-macrame",
          "support-macrame",
          "anneau-macrame"
        ],
        enfant: [
          "pate-modeler",
          "perles",
          "kit-creatif",
          "coloriage",
          "peinture-enfant",
          "gommettes",
          "sable-magique"
        ]
      },
      handmade: {
        bijoux: [
          "collier-fait-main",
          "bracelet-fait-main",
          "boucles-oreilles-fait-main",
          "bague-fait-main"
        ],
        decoration: [
          "bougie-artisanale",
          "poterie",
          "cadre-fait-main",
          "objet-deco-fait-main",
          "crochet-deco",
          "macrame"
        ],
        vetements: [
          "tricot-fait-main",
          "crochet",
          "crochet-vetement",
          "couture-fait-main",
          "broderie"
        ],
        accessoires: [
          "sac-fait-main",
          "echarpe-fait-main",
          "bonnet-tricote",
          "pochette-fait-main",
          "crochet-accessoire"
        ],
        peluches: [
          "amigurumi",
          "crochet-peluche",
          "doudou-crochet",
          "peluche-fait-main"
        ],
        papeterie: [
          "carnet-fait-main",
          "carte-fait-main",
          "stickers-fait-main",
          "marque-page-fait-main"
        ]
      },
      custom: {
        vetements: [
          "tshirt-personnalise",
          "sweat-personnalise",
          "hoodie-personnalise",
          "tablier-personnalise"
        ],
        sacs: [
          "tote-bag-personnalise",
          "pochette-personnalisee",
          "sac-dos-personnalise",
          "trousse-personnalisee"
        ],
        bijoux: [
          "collier-prenom",
          "bracelet-grave",
          "bague-initiale",
          "medaille-gravee",
          "bijou-personnalise"
        ],
        decoration: [
          "coussin-personnalise",
          "mug-personnalise",
          "affiche-prenom",
          "cadre-personnalise",
          "bougie-personnalisee",
          "poster-personnalise"
        ],
        papeterie: [
          "carnet-personnalise",
          "agenda-personnalise",
          "stickers-personnalises",
          "faire-part-personnalise",
          "carte-personnalisee"
        ],
        accessoires: [
          "casquette-personnalisee",
          "coque-telephone-personnalisee",
          "porte-cle-personnalise",
          "badge-personnalise"
        ],
        cadeaux: [
          "coffret-cadeau-personnalise",
          "kit-personnalise",
          "boite-souvenir",
          "album-personnalise"
        ]
      },
      toys: {
        jeux: [
          "jeu-societe",
          "puzzle",
          "jeu-cartes",
          "jeu-construction",
          "jeu-educatif"
        ],
        jouets: [
          "poupee",
          "figurine-jouet",
          "peluche",
          "doudou",
          "jouet-bois",
          "jouet-eveil",
          "dinette",
          "maison-poupee"
        ],
        "jeux-exterieur": [
          "velo-enfant",
          "trottinette",
          "draisienne",
          "rollers",
          "jeu-plein-air"
        ]
      }
    }
  },
  CHILD_GIRL: {
    mainCategories: [
      "clothing",
      "shoes",
      "accessories",
      "books",
      "toys",
      "crafts",
      "school",
      "baby-gear",
      "handmade",
      "custom"
    ],
    subcategories: {
      clothing: {
        robes: [
          "robe-ceremonie",
          "robe-cocktail",
          "robe-princesse",
          "robe-courte",
          "robe-midi",
          "robe-longue",
          "robe-tshirt",
          "robe-pull",
          "robe-tutu"
        ],
        jupes: [
          "jupe-courte",
          "jupe-midi",
          "jupe-tutu",
          "jupe-plissee",
          "jupe-jean"
        ],
        hauts: [
          "tshirt",
          "top",
          "polo",
          "debardeur",
          "chemise",
          "chemisier",
          "blouse"
        ],
        pulls: ["pull", "gilet", "cardigan", "sweat", "hoodie"],
        pantalons: ["jean", "pantalon", "legging", "tregging", "jogging"],
        shorts: ["short-jean", "short", "bermuda", "cycliste"],
        combinaisons: [
          "combinaison-pantalon",
          "combinaison-short",
          "salopette",
          "salopette-short"
        ],
        manteaux: ["manteau", "blouson", "parka", "doudoune", "impermeable"],
        pyjamas: ["pyjama", "chemise-nuit", "robe-chambre"],
        "sous-vetements": ["body", "culottes", "collants", "chaussettes"],
        maillots: ["maillot-bain-1-piece", "maillot-bain-2-pieces", "bikini"],
        danse: ["justaucorps", "tutu-danse", "tenue-danse"],
        deguisements: [
          "deguisement-princesse",
          "deguisement-fee",
          "deguisement"
        ],
        ensembles: ["ensemble-2-pieces", "ensemble-coordonne"]
      },
      shoes: {
        baskets: ["baskets", "sneakers", "tennis"],
        ballerines: ["ballerines", "babies", "salome"],
        sandales: ["sandales", "nu-pieds", "tongs"],
        bottes: ["bottes-pluie", "bottes-neige", "bottines"],
        chaussons: ["chaussons"]
      },
      accessories: {
        sacs: ["sac-dos-ecole", "cartable", "sac-main", "sac-bandouliere"],
        bijoux: ["bijoux-fantaisie", "bracelet", "collier", "bague"],
        cheveux: [
          "barrettes",
          "elastiques",
          "bandeau",
          "serre-tete",
          "couronne"
        ],
        chapeaux: ["chapeau", "casquette", "bonnet", "bob"],
        hiver: ["echarpe", "snood", "gants", "moufles"],
        autres: ["ceinture", "lunettes-soleil"]
      },
      books: {
        jeunesse: [
          "album-illustre",
          "conte",
          "livre-eveil",
          "livre-ado",
          "documentaire-jeunesse"
        ],
        bd: ["bd", "manga", "comics"],
        scolaire: [
          "manuel-scolaire",
          "cahier-vacances",
          "annale",
          "livre-parascolaire",
          "dictionnaire"
        ],
        romans: [
          "roman-jeunesse",
          "fantasy",
          "science-fiction",
          "polar-jeunesse"
        ],
        pratique: ["loisirs-creatifs", "coloriage", "activites"],
        magazines: ["magazine-jeunesse", "magazine-science", "revue"]
      },
      toys: {
        jeux: [
          "jeu-societe",
          "puzzle",
          "jeu-cartes",
          "jeu-construction",
          "jeu-educatif"
        ],
        jouets: [
          "poupee",
          "figurine-jouet",
          "peluche",
          "doudou",
          "jouet-bois",
          "jouet-eveil",
          "dinette",
          "maison-poupee"
        ],
        "jeux-exterieur": [
          "velo-enfant",
          "trottinette",
          "draisienne",
          "rollers",
          "jeu-plein-air"
        ],
        "loisirs-creatifs": [
          "peinture",
          "dessin",
          "pate-modeler",
          "perles",
          "kit-creatif",
          "coloriage",
          "couture",
          "crochet",
          "scrapbooking"
        ],
        electronique: [
          "console-enfant",
          "tablette-enfant",
          "jeu-video-enfant",
          "montre-enfant"
        ]
      },
      crafts: {
        peinture: [
          "peinture-enfant",
          "peinture-doigt",
          "gouache",
          "aquarelle-enfant"
        ],
        dessin: [
          "crayon-couleur",
          "feutre-enfant",
          "pastel-enfant",
          "carnet-dessin-enfant"
        ],
        modelage: [
          "pate-modeler",
          "argile-enfant",
          "sable-magique",
          "platre-mouler"
        ],
        perles: [
          "perles",
          "perles-repasser",
          "bijoux-enfant",
          "bracelets-elastiques"
        ],
        kits: [
          "kit-creatif",
          "kit-couture-enfant",
          "kit-crochet-enfant",
          "kit-broderie-enfant",
          "kit-scrapbooking-enfant"
        ],
        coloriage: ["coloriage", "gommettes", "pochoirs", "tampons-enfant"]
      },
      school: {
        fournitures: [
          "cartable-ecole",
          "trousse",
          "cahier",
          "classeur",
          "stylo-plume",
          "crayons",
          "gomme",
          "regle",
          "calculatrice"
        ],
        "sacs-ecole": ["sac-dos-ecole", "sac-sport-ecole", "sac-piscine"],
        accessoires: [
          "gourde-enfant",
          "lunch-box",
          "tablier-ecole",
          "etiquettes"
        ]
      },
      "baby-gear": {
        portage: ["porte-bebe", "echarpe-portage", "mei-tai"],
        repas: [
          "chaise-haute",
          "biberon",
          "tire-lait",
          "sterilisateur",
          "chauffe-biberon",
          "vaisselle-bebe",
          "bavoir-silicone"
        ],
        sommeil: [
          "lit-bebe",
          "lit-parapluie",
          "matelas-bebe",
          "tour-lit",
          "mobile",
          "veilleuse",
          "babyphone"
        ],
        bain: [
          "baignoire-bebe",
          "transat-bain",
          "thermometre-bain",
          "cape-bain"
        ],
        securite: [
          "barriere-securite",
          "bloque-porte",
          "coin-table",
          "ecoute-bebe"
        ],
        "sante-soin": [
          "thermometre",
          "mouche-bebe",
          "trousse-soin",
          "coupe-ongles-bebe"
        ]
      },
      handmade: {
        vetements: [
          "tricot-fait-main",
          "crochet",
          "crochet-vetement",
          "couture-fait-main"
        ],
        decoration: [
          "crochet-deco",
          "objet-deco-fait-main",
          "cadre-fait-main",
          "guirlande-fait-main"
        ],
        peluches: [
          "amigurumi",
          "crochet-peluche",
          "doudou-crochet",
          "peluche-fait-main",
          "doudou-fait-main"
        ],
        accessoires: [
          "bonnet-tricote",
          "echarpe-fait-main",
          "crochet-accessoire"
        ],
        bebe: ["vetement-bebe-fait-main", "couverture-bebe", "mobile-bebe"]
      },
      custom: {
        vetements: [
          "tshirt-personnalise",
          "sweat-personnalise",
          "hoodie-personnalise",
          "body-personnalise"
        ],
        accessoires: [
          "casquette-personnalisee",
          "sac-dos-personnalise",
          "trousse-personnalisee",
          "gourde-personnalisee"
        ],
        decoration: [
          "coussin-personnalise",
          "affiche-prenom",
          "cadre-personnalise",
          "poster-personnalise"
        ],
        bebe: [
          "doudou-brode",
          "bavoir-personnalise",
          "cape-bain-prenom",
          "gigoteuse-personnalisee",
          "attache-tetine-personnalisee"
        ],
        cadeaux: [
          "coffret-cadeau-personnalise",
          "kit-personnalise",
          "boite-souvenir"
        ]
      }
    }
  },
  CHILD_BOY: {
    mainCategories: [
      "clothing",
      "shoes",
      "accessories",
      "books",
      "toys",
      "crafts",
      "school",
      "baby-gear",
      "handmade",
      "custom"
    ],
    subcategories: {
      clothing: {
        hauts: ["tshirt", "polo", "debardeur", "chemise"],
        pulls: ["pull", "gilet", "cardigan", "sweat", "hoodie"],
        pantalons: ["jean", "pantalon", "chino", "cargo", "jogging"],
        shorts: ["short-jean", "short", "bermuda"],
        combinaisons: [
          "combinaison-pantalon",
          "combinaison-short",
          "salopette"
        ],
        manteaux: ["manteau", "blouson", "parka", "doudoune", "impermeable"],
        pyjamas: ["pyjama", "robe-chambre"],
        "sous-vetements": ["boxer", "calecon", "slip", "chaussettes"],
        maillots: ["short-bain", "boxer-bain"],
        sport: ["tenue-sport", "survetement"],
        deguisements: ["deguisement-super-heros", "deguisement"],
        ensembles: ["ensemble-2-pieces"]
      },
      shoes: {
        baskets: ["baskets", "sneakers", "tennis", "running"],
        ville: ["chaussures-ville", "mocassins"],
        sandales: ["sandales", "nu-pieds", "tongs"],
        bottes: ["bottes-pluie", "bottes-neige", "bottines"],
        chaussons: ["chaussons"]
      },
      accessories: {
        sacs: ["sac-dos-ecole", "cartable", "sac-sport"],
        chapeaux: ["casquette", "chapeau", "bonnet", "bob"],
        hiver: ["echarpe", "snood", "gants", "moufles"],
        autres: ["ceinture", "lunettes-soleil"]
      },
      books: {
        jeunesse: [
          "album-illustre",
          "conte",
          "livre-eveil",
          "livre-ado",
          "documentaire-jeunesse"
        ],
        bd: ["bd", "manga", "comics"],
        scolaire: [
          "manuel-scolaire",
          "cahier-vacances",
          "annale",
          "livre-parascolaire",
          "dictionnaire"
        ],
        romans: [
          "roman-jeunesse",
          "fantasy",
          "science-fiction",
          "polar-jeunesse"
        ],
        pratique: ["loisirs-creatifs", "coloriage", "activites"],
        magazines: ["magazine-jeunesse", "magazine-science", "revue"]
      },
      toys: {
        jeux: [
          "jeu-societe",
          "puzzle",
          "jeu-cartes",
          "jeu-construction",
          "jeu-educatif"
        ],
        jouets: [
          "figurine-jouet",
          "figurine-action",
          "voiture-jouet",
          "circuit",
          "peluche",
          "doudou",
          "jouet-bois",
          "jouet-eveil",
          "robot-jouet"
        ],
        "jeux-exterieur": [
          "velo-enfant",
          "trottinette",
          "draisienne",
          "rollers",
          "skateboard",
          "jeu-plein-air",
          "ballon"
        ],
        "loisirs-creatifs": [
          "peinture",
          "dessin",
          "pate-modeler",
          "kit-creatif",
          "coloriage",
          "maquette",
          "couture",
          "crochet",
          "scrapbooking"
        ],
        electronique: [
          "console-enfant",
          "tablette-enfant",
          "jeu-video-enfant",
          "montre-enfant"
        ]
      },
      crafts: {
        peinture: [
          "peinture-enfant",
          "peinture-doigt",
          "gouache",
          "aquarelle-enfant"
        ],
        dessin: [
          "crayon-couleur",
          "feutre-enfant",
          "pastel-enfant",
          "carnet-dessin-enfant"
        ],
        modelage: [
          "pate-modeler",
          "argile-enfant",
          "sable-magique",
          "platre-mouler"
        ],
        perles: [
          "perles",
          "perles-repasser",
          "bijoux-enfant",
          "bracelets-elastiques"
        ],
        kits: [
          "kit-creatif",
          "kit-couture-enfant",
          "kit-crochet-enfant",
          "kit-broderie-enfant",
          "kit-scrapbooking-enfant"
        ],
        coloriage: [
          "coloriage",
          "gommettes",
          "pochoirs",
          "tampons-enfant",
          "maquette"
        ]
      },
      school: {
        fournitures: [
          "cartable-ecole",
          "trousse",
          "cahier",
          "classeur",
          "stylo-plume",
          "crayons",
          "gomme",
          "regle",
          "calculatrice"
        ],
        "sacs-ecole": ["sac-dos-ecole", "sac-sport-ecole", "sac-piscine"],
        accessoires: [
          "gourde-enfant",
          "lunch-box",
          "tablier-ecole",
          "etiquettes"
        ]
      },
      "baby-gear": {
        portage: ["porte-bebe", "echarpe-portage", "mei-tai"],
        repas: [
          "chaise-haute",
          "biberon",
          "tire-lait",
          "sterilisateur",
          "chauffe-biberon",
          "vaisselle-bebe",
          "bavoir-silicone"
        ],
        sommeil: [
          "lit-bebe",
          "lit-parapluie",
          "matelas-bebe",
          "tour-lit",
          "mobile",
          "veilleuse",
          "babyphone"
        ],
        bain: [
          "baignoire-bebe",
          "transat-bain",
          "thermometre-bain",
          "cape-bain"
        ],
        securite: [
          "barriere-securite",
          "bloque-porte",
          "coin-table",
          "ecoute-bebe"
        ],
        "sante-soin": [
          "thermometre",
          "mouche-bebe",
          "trousse-soin",
          "coupe-ongles-bebe"
        ]
      },
      handmade: {
        vetements: [
          "tricot-fait-main",
          "crochet",
          "crochet-vetement",
          "couture-fait-main"
        ],
        decoration: [
          "crochet-deco",
          "objet-deco-fait-main",
          "cadre-fait-main",
          "guirlande-fait-main"
        ],
        peluches: [
          "amigurumi",
          "crochet-peluche",
          "doudou-crochet",
          "peluche-fait-main",
          "doudou-fait-main"
        ],
        accessoires: [
          "bonnet-tricote",
          "echarpe-fait-main",
          "crochet-accessoire"
        ],
        bebe: ["vetement-bebe-fait-main", "couverture-bebe", "mobile-bebe"]
      },
      custom: {
        vetements: [
          "tshirt-personnalise",
          "sweat-personnalise",
          "hoodie-personnalise",
          "body-personnalise"
        ],
        accessoires: [
          "casquette-personnalisee",
          "sac-dos-personnalise",
          "trousse-personnalisee",
          "gourde-personnalisee"
        ],
        decoration: [
          "coussin-personnalise",
          "affiche-prenom",
          "cadre-personnalise",
          "poster-personnalise"
        ],
        bebe: [
          "doudou-brode",
          "bavoir-personnalise",
          "cape-bain-prenom",
          "gigoteuse-personnalisee",
          "attache-tetine-personnalisee"
        ],
        cadeaux: [
          "coffret-cadeau-personnalise",
          "kit-personnalise",
          "boite-souvenir"
        ]
      }
    }
  },
  BABY_GIRL: {
    mainCategories: ["clothing", "accessories", "baby-gear"],
    subcategories: {
      clothing: {
        bodies: [
          "body-manches-courtes",
          "body-manches-longues",
          "body-sans-manches"
        ],
        pyjamas: [
          "grenouillere",
          "pyjama-1-piece",
          "pyjama-2-pieces",
          "dors-bien",
          "surpyjama"
        ],
        ensembles: ["ensemble-2-pieces", "ensemble-3-pieces", "tenue-complete"],
        hauts: ["tshirt-bebe", "top-bebe"],
        pulls: ["pull-bebe", "gilet-bebe", "cardigan-bebe", "sweat-bebe"],
        bas: ["pantalon-bebe", "salopette-bebe", "jean-bebe"],
        robes: ["robe-bebe", "jupe-bebe"],
        manteaux: [
          "manteau-bebe",
          "blouson-bebe",
          "parka-bebe",
          "doudoune-bebe",
          "combinaison-pilote",
          "combi-ski",
          "nid-ange"
        ],
        chaussures: [
          "chaussons-souples",
          "chaussons-fourres",
          "chaussettes-bebe"
        ],
        sommeil: ["turbulette-ete", "turbulette-hiver", "gigoteuse"]
      },
      accessories: {
        chapeaux: [
          "bonnet-naissance",
          "bonnet-hiver",
          "chapeau-soleil",
          "casquette-bebe",
          "bandeau-bebe"
        ],
        bavoirs: ["bavoir-bandana", "bavoir-classique"],
        textiles: [
          "lange",
          "couverture-polaire",
          "couverture-coton",
          "plaid-bebe"
        ],
        gants: ["moufles-bebe", "gants-bebe"]
      },
      "baby-gear": {
        portage: ["porte-bebe", "echarpe-portage", "mei-tai"],
        repas: [
          "chaise-haute",
          "biberon",
          "tire-lait",
          "sterilisateur",
          "chauffe-biberon",
          "vaisselle-bebe",
          "bavoir-silicone"
        ],
        sommeil: [
          "lit-bebe",
          "lit-parapluie",
          "matelas-bebe",
          "tour-lit",
          "mobile",
          "veilleuse",
          "babyphone"
        ],
        bain: [
          "baignoire-bebe",
          "transat-bain",
          "thermometre-bain",
          "cape-bain"
        ],
        securite: [
          "barriere-securite",
          "bloque-porte",
          "coin-table",
          "ecoute-bebe"
        ],
        "sante-soin": [
          "thermometre",
          "mouche-bebe",
          "trousse-soin",
          "coupe-ongles-bebe"
        ]
      }
    }
  },
  BABY_BOY: {
    mainCategories: ["clothing", "accessories", "baby-gear"],
    subcategories: {
      clothing: {
        bodies: [
          "body-manches-courtes",
          "body-manches-longues",
          "body-sans-manches"
        ],
        pyjamas: [
          "grenouillere",
          "pyjama-1-piece",
          "pyjama-2-pieces",
          "dors-bien",
          "surpyjama"
        ],
        ensembles: ["ensemble-2-pieces", "ensemble-3-pieces", "tenue-complete"],
        hauts: ["tshirt-bebe", "top-bebe"],
        pulls: ["pull-bebe", "gilet-bebe", "cardigan-bebe", "sweat-bebe"],
        bas: ["pantalon-bebe", "salopette-bebe", "jean-bebe", "short-bebe"],
        manteaux: [
          "manteau-bebe",
          "blouson-bebe",
          "parka-bebe",
          "doudoune-bebe",
          "combinaison-pilote",
          "combi-ski",
          "nid-ange"
        ],
        chaussures: [
          "chaussons-souples",
          "chaussons-fourres",
          "chaussettes-bebe"
        ],
        sommeil: ["turbulette-ete", "turbulette-hiver", "gigoteuse"]
      },
      accessories: {
        chapeaux: [
          "bonnet-naissance",
          "bonnet-hiver",
          "chapeau-soleil",
          "casquette-bebe"
        ],
        bavoirs: ["bavoir-bandana", "bavoir-classique"],
        textiles: [
          "lange",
          "couverture-polaire",
          "couverture-coton",
          "plaid-bebe"
        ],
        gants: ["moufles-bebe", "gants-bebe"]
      },
      "baby-gear": {
        portage: ["porte-bebe", "echarpe-portage", "mei-tai"],
        repas: [
          "chaise-haute",
          "biberon",
          "tire-lait",
          "sterilisateur",
          "chauffe-biberon",
          "vaisselle-bebe",
          "bavoir-silicone"
        ],
        sommeil: [
          "lit-bebe",
          "lit-parapluie",
          "matelas-bebe",
          "tour-lit",
          "mobile",
          "veilleuse",
          "babyphone"
        ],
        bain: [
          "baignoire-bebe",
          "transat-bain",
          "thermometre-bain",
          "cape-bain"
        ],
        securite: [
          "barriere-securite",
          "bloque-porte",
          "coin-table",
          "ecoute-bebe"
        ],
        "sante-soin": [
          "thermometre",
          "mouche-bebe",
          "trousse-soin",
          "coupe-ongles-bebe"
        ]
      }
    }
  },
  // 🔄 ANCIENS TYPES POUR RÉTROCOMPATIBILITÉ
  FEMME: {
    mainCategories: [
      "clothing",
      "shoes",
      "bags",
      "accessories",
      "home",
      "books",
      "toys"
    ],
    subcategories: {}
    // Alias vers ADULT_WOMAN
  },
  HOMME: {
    mainCategories: [
      "clothing",
      "shoes",
      "bags",
      "accessories",
      "home",
      "books",
      "toys"
    ],
    subcategories: {}
    // Alias vers ADULT_MAN
  },
  ENFANT_FILLE: {
    mainCategories: [
      "clothing",
      "shoes",
      "accessories",
      "books",
      "toys",
      "school",
      "baby-gear"
    ],
    subcategories: {}
    // Alias vers CHILD_GIRL
  },
  ENFANT_GARCON: {
    mainCategories: [
      "clothing",
      "shoes",
      "accessories",
      "books",
      "toys",
      "school",
      "baby-gear"
    ],
    subcategories: {}
    // Alias vers CHILD_BOY
  },
  BEBE: {
    mainCategories: ["clothing", "accessories", "baby-gear"],
    subcategories: {}
    // Alias vers BABY_GIRL
  }
};
CATEGORY_STRUCTURE.FEMME.subcategories = CATEGORY_STRUCTURE.ADULT_WOMAN.subcategories;
CATEGORY_STRUCTURE.HOMME.subcategories = CATEGORY_STRUCTURE.ADULT_MAN.subcategories;
CATEGORY_STRUCTURE.ENFANT_FILLE.subcategories = CATEGORY_STRUCTURE.CHILD_GIRL.subcategories;
CATEGORY_STRUCTURE.ENFANT_GARCON.subcategories = CATEGORY_STRUCTURE.CHILD_BOY.subcategories;
CATEGORY_STRUCTURE.BEBE.subcategories = CATEGORY_STRUCTURE.BABY_GIRL.subcategories;
function getSubcategoryGroups(profile, mainCategory) {
  const structure = CATEGORY_STRUCTURE[profile];
  if (!structure) return {};
  const subcategories = structure.subcategories[mainCategory];
  return subcategories || {};
}
function getSubcategoryItems(profile, mainCategory, subcategoryGroup) {
  const groups = getSubcategoryGroups(profile, mainCategory);
  return groups[subcategoryGroup] || [];
}
function getAllCategoryItems(profile, mainCategory) {
  const groups = getSubcategoryGroups(profile, mainCategory);
  if (!groups || typeof groups !== "object") return [];
  return Object.values(groups).flat();
}
function hasSubcategoryGroups(profile, mainCategory) {
  const subcategories = getSubcategoryGroups(profile, mainCategory);
  return Object.keys(subcategories).length > 0;
}
function getCategoryStructure(profile) {
  return CATEGORY_STRUCTURE[profile];
}
var PRODUCT_FILTERS = {
  // === FILTRES GÉNÉRAUX (tous produits) ===
  taille_position: [
    { id: "taille-haute", label: "Taille haute" },
    { id: "taille-moyenne", label: "Taille moyenne" },
    { id: "taille-basse", label: "Taille basse" }
  ],
  longueur: [
    { id: "court", label: "Court" },
    { id: "mi-long", label: "Mi-long" },
    { id: "long", label: "Long" },
    { id: "extra-long", label: "Extra long" }
  ],
  manches: [
    { id: "sans-manches", label: "Sans manches" },
    { id: "manches-courtes", label: "Manches courtes" },
    { id: "manches-3-4", label: "Manches 3/4" },
    { id: "manches-longues", label: "Manches longues" }
  ],
  // === COLS ===
  col_type: [
    { id: "col-rond", label: "Col rond" },
    { id: "col-v", label: "Col V" },
    { id: "col-roule", label: "Col roul\xE9" },
    { id: "col-cheminee", label: "Col chemin\xE9e" },
    { id: "col-benitier", label: "Col b\xE9nitier" },
    { id: "col-mao", label: "Col Mao" },
    { id: "col-chemise", label: "Col chemise" },
    { id: "col-claudine", label: "Col Claudine" },
    { id: "sans-col", label: "Sans col" }
  ],
  // === STYLES JEANS ===
  jean_style: [
    { id: "brut", label: "Brut" },
    { id: "delave", label: "D\xE9lav\xE9" },
    { id: "vintage", label: "Vintage" },
    { id: "dechire", label: "D\xE9chir\xE9" },
    { id: "troue", label: "Trou\xE9" },
    { id: "noir", label: "Noir" },
    { id: "blanc", label: "Blanc" },
    { id: "colore", label: "Color\xE9" }
  ],
  // === MAILLE ===
  maille_type: [
    { id: "fine", label: "Fine" },
    { id: "epaisse", label: "\xC9paisse" },
    { id: "torsadee", label: "Torsad\xE9e" },
    { id: "ajouree", label: "Ajour\xE9e" },
    { id: "cotes", label: "C\xF4tes" }
  ],
  // === ROBES SPÉCIFIQUES ===
  robe_style: [
    { id: "droite", label: "Droite" },
    { id: "trapeze", label: "Trap\xE8ze" },
    { id: "patineuse", label: "Patineuse" },
    { id: "fourreau", label: "Fourreau" },
    { id: "empire", label: "Empire" },
    { id: "portefeuille", label: "Portefeuille" },
    { id: "bustier", label: "Bustier" },
    { id: "dos-nu", label: "Dos nu" },
    { id: "bretelles", label: "\xC0 bretelles" },
    { id: "plissee", label: "Pliss\xE9e" },
    { id: "fendue", label: "Fendue" },
    { id: "asymetrique", label: "Asym\xE9trique" }
  ],
  // === JUPES SPÉCIFIQUES ===
  jupe_style: [
    { id: "droite", label: "Droite" },
    { id: "evasee", label: "\xC9vas\xE9e" },
    { id: "portefeuille", label: "Portefeuille" },
    { id: "boutonnee", label: "Boutonn\xE9e" },
    { id: "fendue", label: "Fendue" },
    { id: "asymetrique", label: "Asym\xE9trique" }
  ],
  // === FERMETURES ===
  fermeture: [
    { id: "boutons", label: "Boutons" },
    { id: "zip", label: "Zip" },
    { id: "lacets", label: "Lacets" },
    { id: "scratch", label: "Scratch" },
    { id: "elastique", label: "\xC9lastique" },
    { id: "ceinture", label: "Ceinture" }
  ],
  // === MANTEAUX SPÉCIFIQUES ===
  manteau_details: [
    { id: "avec-capuche", label: "Avec capuche" },
    { id: "sans-capuche", label: "Sans capuche" },
    { id: "fourrure", label: "Avec fourrure" },
    { id: "ceinture", label: "Avec ceinture" },
    { id: "croise", label: "Crois\xE9" },
    { id: "droit", label: "Droit" },
    { id: "oversize", label: "Oversize" }
  ],
  // === CHAUSSURES ===
  talon_type: [
    { id: "plat", label: "Plat" },
    { id: "petit-talon", label: "Petit talon (1-3cm)" },
    { id: "moyen-talon", label: "Moyen (3-7cm)" },
    { id: "haut-talon", label: "Haut (7cm+)" },
    { id: "talon-aiguille", label: "Aiguille" },
    { id: "talon-carre", label: "Carr\xE9" },
    { id: "talon-bobine", label: "Bobine" },
    { id: "compense", label: "Compens\xE9" }
  ],
  bout_type: [
    { id: "bout-rond", label: "Bout rond" },
    { id: "bout-pointu", label: "Bout pointu" },
    { id: "bout-carre", label: "Bout carr\xE9" },
    { id: "bout-ouvert", label: "Bout ouvert" }
  ],
  // === SACS ===
  sac_taille: [
    { id: "mini", label: "Mini" },
    { id: "petit", label: "Petit" },
    { id: "moyen", label: "Moyen" },
    { id: "grand", label: "Grand" },
    { id: "xxl", label: "XXL" }
  ],
  sac_fermeture: [
    { id: "fermeture-eclair", label: "Fermeture \xE9clair" },
    { id: "fermeture-magnetique", label: "Fermeture magn\xE9tique" },
    { id: "rabat", label: "Rabat" },
    { id: "clip", label: "Clip" },
    { id: "ouvert", label: "Ouvert" }
  ],
  // === DÉTAILS & FINITIONS ===
  details: [
    { id: "dentelle", label: "Dentelle" },
    { id: "broderie", label: "Broderie" },
    { id: "paillettes", label: "Paillettes" },
    { id: "sequins", label: "Sequins" },
    { id: "clous", label: "Clous" },
    { id: "franges", label: "Franges" },
    { id: "volants", label: "Volants" },
    { id: "smock", label: "Smock" },
    { id: "poches", label: "Avec poches" },
    { id: "sans-poches", label: "Sans poches" }
  ],
  // === MOTIFS ===
  motifs: [
    { id: "uni", label: "Uni" },
    { id: "raye", label: "Ray\xE9" },
    { id: "pois", label: "\xC0 pois" },
    { id: "floral", label: "Floral" },
    { id: "geometrique", label: "G\xE9om\xE9trique" },
    { id: "animal", label: "Imprim\xE9 animal" },
    { id: "camouflage", label: "Camouflage" },
    { id: "carreaux", label: "Carreaux" },
    { id: "tie-dye", label: "Tie & dye" },
    { id: "jacquard", label: "Jacquard" }
  ],
  // === STYLE GÉNÉRAL ===
  style_general: [
    { id: "classique", label: "Classique" },
    { id: "moderne", label: "Moderne" },
    { id: "vintage", label: "Vintage" },
    { id: "boheme", label: "Boh\xE8me" },
    { id: "rock", label: "Rock" },
    { id: "preppy", label: "Preppy" },
    { id: "streetwear", label: "Streetwear" },
    { id: "elegance", label: "\xC9l\xE9gant" },
    { id: "decontracte", label: "D\xE9contract\xE9" },
    { id: "sexy", label: "Sexy" },
    { id: "romantique", label: "Romantique" }
  ]
};
var COLORS = [
  { id: "noir", label: "Noir", hex: "#000000" },
  { id: "blanc", label: "Blanc", hex: "#FFFFFF" },
  { id: "gris", label: "Gris", hex: "#9CA3AF" },
  { id: "gris-anthracite", label: "Gris anthracite", hex: "#4B5563" },
  { id: "beige", label: "Beige", hex: "#D4C5B9" },
  { id: "ecru", label: "\xC9cru", hex: "#F5F5DC" },
  { id: "marron", label: "Marron", hex: "#8B4513" },
  { id: "camel", label: "Camel", hex: "#C19A6B" },
  { id: "dore", label: "Dor\xE9", hex: "#C8A882" },
  { id: "rouge", label: "Rouge", hex: "#DC2626" },
  { id: "bordeaux", label: "Bordeaux", hex: "#7F1D1D" },
  { id: "rose", label: "Rose", hex: "#EC4899" },
  { id: "rose-poudre", label: "Rose poudr\xE9", hex: "#F9A8D4" },
  { id: "orange", label: "Orange", hex: "#F97316" },
  { id: "corail", label: "Corail", hex: "#FF7F50" },
  { id: "jaune", label: "Jaune", hex: "#FCD34D" },
  { id: "moutarde", label: "Moutarde", hex: "#FFDB58" },
  { id: "vert", label: "Vert", hex: "#059669" },
  { id: "vert-kaki", label: "Vert kaki", hex: "#6B7280" },
  { id: "vert-emeraude", label: "Vert \xE9meraude", hex: "#10B981" },
  { id: "bleu", label: "Bleu", hex: "#3B82F6" },
  { id: "bleu-clair", label: "Bleu clair", hex: "#93C5FD" },
  { id: "bleu-marine", label: "Bleu marine", hex: "#1E3A8A" },
  { id: "bleu-canard", label: "Bleu canard", hex: "#0E7490" },
  { id: "violet", label: "Violet", hex: "#9333EA" },
  { id: "mauve", label: "Mauve", hex: "#C084FC" },
  { id: "argente", label: "Argent\xE9", hex: "#C0C0C0" },
  { id: "bronze", label: "Bronze", hex: "#CD7F32" },
  { id: "transparent", label: "Transparent", hex: "rgba(0,0,0,0.05)" },
  { id: "multicolore", label: "Multicolore", hex: "#FFFFFF", isGradient: true }
];
var MATERIALS = [
  "coton",
  "coton-bio",
  "polyester",
  "laine",
  "laine-merinos",
  "cachemire",
  "mohair",
  "alpaga",
  "soie",
  "satin",
  "lin",
  "cuir",
  "cuir-vegan",
  "daim",
  "nubuck",
  "denim",
  "jean",
  "viscose",
  "modal",
  "elasthanne",
  "lycra",
  "nylon",
  "acrylique",
  "velours",
  "velours-cotele",
  "dentelle",
  "tulle",
  "mousseline",
  "organza",
  "tweed",
  "toile",
  "canvas",
  "mesh",
  "jersey",
  "molleton",
  "polaire",
  "fourrure",
  "fausse-fourrure",
  "plumes",
  "paillettes",
  "sequins",
  "simili-cuir",
  "autre"
];
var BRANDS = {
  FAST_FASHION: [
    "ZARA",
    "H&M",
    "MANGO",
    "UNIQLO",
    "PULL&BEAR",
    "BERSHKA",
    "STRADIVARIUS",
    "FOREVER 21",
    "PRIMARK",
    "NEW LOOK",
    "TOPSHOP",
    "ASOS",
    "SHEIN",
    "BOOHOO"
  ],
  MILIEU_GAMME: [
    "COS",
    "ARKET",
    "& OTHER STORIES",
    "MASSIMO DUTTI",
    "COMPTOIR DES COTONNIERS",
    "PROMOD",
    "CAMA\xCFEU",
    "NAF NAF",
    "ETAM",
    "PIMKIE",
    "JULES",
    "CELIO",
    "ESPRIT",
    "MONOPRIX",
    "LA REDOUTE",
    "KIABI"
  ],
  PREMIUM: [
    "SANDRO",
    "MAJE",
    "BA&SH",
    "CLAUDIE PIERLOT",
    "ZADIG&VOLTAIRE",
    "THE KOOPLES",
    "GERARD DAREL",
    "S\xC9ZANE",
    "ROUJE",
    "INES DE LA FRESSANGE",
    "SESSUN",
    "AMERICAN VINTAGE",
    "IRO",
    "VANESSA BRUNO"
  ],
  LUXE: [
    "LOUIS VUITTON",
    "CHANEL",
    "HERM\xC8S",
    "GUCCI",
    "PRADA",
    "DIOR",
    "SAINT LAURENT",
    "BALENCIAGA",
    "GIVENCHY",
    "BURBERRY",
    "VALENTINO",
    "CELINE",
    "BOTTEGA VENETA",
    "FENDI",
    "VERSACE",
    "DOLCE&GABBANA"
  ],
  SPORTSWEAR: [
    "NIKE",
    "ADIDAS",
    "PUMA",
    "REEBOK",
    "NEW BALANCE",
    "ASICS",
    "UNDER ARMOUR",
    "FILA",
    "CHAMPION",
    "VANS",
    "CONVERSE",
    "DECATHLON",
    "SALOMON",
    "THE NORTH FACE",
    "PATAGONIA",
    "LULULEMON"
  ],
  STREETWEAR: [
    "SUPREME",
    "ST\xDCSSY",
    "PALACE",
    "BAPE",
    "OFF-WHITE",
    "CARHARTT",
    "DICKIES",
    "VEJA",
    "LACOSTE",
    "FRED PERRY",
    "LEVI'S",
    "DIESEL",
    "G-STAR RAW",
    "ACNE STUDIOS"
  ],
  LINGERIE: [
    "ETAM",
    "DARJEELING",
    "UNDIZ",
    "INTIMISSIMI",
    "CALVIN KLEIN",
    "VICTORIA'S SECRET",
    "AUBADE",
    "CHANTELLE",
    "SIMONE P\xC9R\xC8LE",
    "PRINCESSE TAM TAM",
    "TRIUMPH",
    "DIM"
  ],
  ENFANT: [
    "PETIT BATEAU",
    "JACADI",
    "BONPOINT",
    "TARTINE ET CHOCOLAT",
    "ORCHESTRA",
    "OKA\xCFDI",
    "OBAIBI",
    "VERTBAUDET",
    "LA REDOUTE",
    "KIABI",
    "DPAM",
    "SERGENT MAJOR",
    "IKKS"
  ],
  AUTRE: ["VINTAGE", "FAIT MAIN", "SANS MARQUE", "AUTRE"]
};
var PRODUCT_CONDITIONS = [
  "NEW_WITH_TAG",
  "NEW_WITHOUT_TAG",
  "PERFECT",
  "VERY_GOOD",
  "GOOD",
  "SATISFACTORY"
];
var CONDITIONS_DETAILS = [
  {
    id: "NEW_WITH_TAG",
    label: "Neuf avec \xE9tiquette",
    shortLabel: "Neuf avec \xE9tiquette",
    description: "Article neuf, absolument jamais port\xE9/utilis\xE9 avec son \xE9tiquette et/ou emballage d'origine",
    icon: "Sparkles",
    showDefectsSection: false
  },
  {
    id: "NEW_WITHOUT_TAG",
    label: "Neuf sans \xE9tiquette",
    shortLabel: "Neuf sans \xE9tiquette",
    description: "Article neuf, jamais port\xE9/utilis\xE9 mais sans \xE9tiquette ni emballage d'origine",
    icon: "Star",
    showDefectsSection: false
  },
  {
    id: "PERFECT",
    label: "Parfait \xE9tat",
    shortLabel: "Parfait \xE9tat",
    description: "Article comme neuf, \xE0 peine port\xE9/utilis\xE9, vraiment dans le m\xEAme \xE9tat que s'il \xE9tait neuf",
    icon: "CheckCircle",
    showDefectsSection: false
  },
  {
    id: "VERY_GOOD",
    label: "Tr\xE8s bon \xE9tat",
    shortLabel: "Tr\xE8s bon \xE9tat",
    description: "Article tr\xE8s peu port\xE9/utilis\xE9 qui peut pr\xE9senter de l\xE9g\xE8res imperfections, mais qui reste en tr\xE8s bon \xE9tat",
    icon: "Circle",
    showDefectsSection: true
  },
  {
    id: "GOOD",
    label: "Bon \xE9tat",
    shortLabel: "Bon \xE9tat",
    description: "Article port\xE9/utilis\xE9 quelques fois, pr\xE9sentant des imperfections et des signes d'usure",
    icon: "AlertCircle",
    showDefectsSection: true
  },
  {
    id: "SATISFACTORY",
    label: "Satisfaisant",
    shortLabel: "Satisfaisant",
    description: "Article port\xE9/utilis\xE9 plusieurs fois pr\xE9sentant des imperfections et des signes d'usure",
    icon: "Info",
    showDefectsSection: true
  }
];
var PRODUCT_SIZES = [
  "XXS",
  "XS",
  "S",
  "M",
  "L",
  "XL",
  "XXL",
  "XXXL",
  "32",
  "34",
  "36",
  "38",
  "40",
  "42",
  "44",
  "46",
  "48",
  "50",
  "ONE_SIZE"
];
var AI_STYLES = [
  "vintage",
  "modern",
  "casual",
  "elegant",
  "bohemian",
  "classic",
  "sporty",
  "trendy",
  "formal",
  "streetwear",
  "minimalist",
  "romantic",
  "rock",
  "preppy",
  "chic",
  "urbain"
];
var AI_OCCASIONS = [
  "work",
  "party",
  "casual",
  "formal",
  "vacation",
  "date",
  "sport",
  "wedding",
  "everyday",
  "special",
  "bureau",
  "soiree",
  "plage",
  "ceremonie"
];
var SEASONS = [
  "printemps",
  "ete",
  "automne",
  "hiver",
  "mi-saison",
  "toutes-saisons"
];
var SHOP_THEME_STYLES = [
  "vintage",
  "modern",
  "luxury",
  "boho",
  "minimalist",
  "colorful"
];
var EVENT_THEMES = [
  "VINTAGE",
  "LUXURY",
  "CASUAL",
  "FESTIVAL",
  "FASHION_WEEK",
  "MARKETPLACE"
];
var FILE_TYPES = {
  IMAGES: ["image/jpeg", "image/png", "image/webp", "image/gif"],
  DOCUMENTS: ["application/pdf", "application/msword"],
  VIDEOS: ["video/mp4", "video/webm", "video/ogg"]
};
var MAX_FILE_SIZES = {
  AVATAR: 2 * 1024 * 1024,
  PRODUCT_IMAGE: 5 * 1024 * 1024,
  BANNER: 3 * 1024 * 1024,
  DOCUMENT: 10 * 1024 * 1024
};
var PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
  MIN_LIMIT: 1
};
var REGEX_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^[+]?[1-9][\d]{0,15}$/,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
  SLUG: /^[a-z0-9-]+$/,
  HEX_COLOR: /^#[0-9A-F]{6}$/i,
  URL: /^https?:\/\/.+/,
  INSTAGRAM_HANDLE: /^[a-zA-Z0-9._]{1,30}$/
};
var SOCIAL_PLATFORMS = {
  INSTAGRAM: "instagram",
  FACEBOOK: "facebook",
  TWITTER: "twitter",
  TIKTOK: "tiktok",
  WEBSITE: "website"
};
var DEFAULT_AVATARS = [
  "/images/avatars/default-1.png",
  "/images/avatars/default-2.png",
  "/images/avatars/default-3.png",
  "/images/avatars/default-4.png",
  "/images/avatars/default-5.png"
];
function getApplicableFilters(category) {
  const filters = [];
  if (category.includes("tshirt") || category.includes("top") || category.includes("chemise") || category.includes("pull")) {
    filters.push("manches", "col_type");
  }
  if (category.includes("jean")) {
    filters.push("taille_position", "jean_style");
  }
  if (category.includes("pantalon")) {
    filters.push("taille_position", "longueur");
  }
  if (category.includes("robe")) {
    filters.push("manches", "longueur", "robe_style");
  }
  if (category.includes("jupe")) {
    filters.push("taille_position", "longueur", "jupe_style");
  }
  if (category.includes("manteau") || category.includes("blouson") || category.includes("parka") || category.includes("doudoune")) {
    filters.push("longueur", "manteau_details");
  }
  if (category.includes("escarpins") || category.includes("talons") || category.includes("compensees")) {
    filters.push("talon_type", "bout_type");
  }
  if (category.includes("baskets") || category.includes("sneakers") || category.includes("ballerines")) {
    filters.push("bout_type");
  }
  if (category.includes("sac")) {
    filters.push("sac_taille", "sac_fermeture");
  }
  filters.push("details", "motifs", "style_general");
  return filters;
}
function getFilterOptions(filterKey) {
  return PRODUCT_FILTERS[filterKey] || [];
}
function getAllBrands() {
  return Object.values(BRANDS).flat().sort();
}
function getBrandsByCategory(category) {
  if (category.includes("sport"))
    return [...BRANDS.SPORTSWEAR, ...BRANDS.AUTRE];
  if (category.includes("streetwear"))
    return [...BRANDS.STREETWEAR, ...BRANDS.AUTRE];
  if (category.includes("lingerie"))
    return [...BRANDS.LINGERIE, ...BRANDS.AUTRE];
  if (category.includes("enfant") || category.includes("bebe"))
    return [...BRANDS.ENFANT, ...BRANDS.AUTRE];
  return getAllBrands();
}
function getColorHex(colorId) {
  return COLORS.find((c) => c.id === colorId)?.hex || "#000000";
}
function getColorLabel(colorId) {
  return COLORS.find((c) => c.id === colorId)?.label || colorId;
}
function getMaterialLabel(materialId) {
  return MATERIALS.find((m) => m === materialId) || materialId;
}
function getConditionDetails(conditionId) {
  return CONDITIONS_DETAILS.find((c) => c.id === conditionId);
}
function shouldShowDefectsSection(conditionId) {
  const condition = CONDITIONS_DETAILS.find((c) => c.id === conditionId);
  return condition?.showDefectsSection || false;
}
function getProfileFromCategorySlug(slug) {
  if (!slug) return null;
  const normalizedSlug = slug.toLowerCase().trim();
  for (const [profileKey, profileData] of Object.entries(CATEGORY_STRUCTURE)) {
    if (profileData.mainCategories.includes(normalizedSlug)) {
      return profileKey;
    }
    for (const [_mainCat, subCats] of Object.entries(
      profileData.subcategories
    )) {
      for (const [subCatKey, items] of Object.entries(
        subCats
      )) {
        if (subCatKey === normalizedSlug) {
          return profileKey;
        }
        if (Array.isArray(items) && items.includes(normalizedSlug)) {
          return profileKey;
        }
      }
    }
  }
  return null;
}
var ATTRIBUTE_EXTRACTION_RULES = [
  // === JEANS ===
  {
    pattern: /^jean-skinny$/,
    extract: () => ({ subtype: "jean", coupe: "skinny" })
  },
  {
    pattern: /^jean-slim$/,
    extract: () => ({ subtype: "jean", coupe: "slim" })
  },
  {
    pattern: /^jean-droit$/,
    extract: () => ({ subtype: "jean", coupe: "droit" })
  },
  {
    pattern: /^jean-regular$/,
    extract: () => ({ subtype: "jean", coupe: "regular" })
  },
  {
    pattern: /^jean-boyfriend$/,
    extract: () => ({ subtype: "jean", coupe: "boyfriend" })
  },
  { pattern: /^jean-mom$/, extract: () => ({ subtype: "jean", coupe: "mom" }) },
  {
    pattern: /^jean-flare$/,
    extract: () => ({ subtype: "jean", coupe: "flare" })
  },
  {
    pattern: /^jean-bootcut$/,
    extract: () => ({ subtype: "jean", coupe: "bootcut" })
  },
  {
    pattern: /^jean-wide-leg$/,
    extract: () => ({ subtype: "jean", coupe: "wide-leg" })
  },
  {
    pattern: /^jean-straight$/,
    extract: () => ({ subtype: "jean", coupe: "straight" })
  },
  {
    pattern: /^jean-relaxed$/,
    extract: () => ({ subtype: "jean", coupe: "relaxed" })
  },
  {
    pattern: /^jean-tapered$/,
    extract: () => ({ subtype: "jean", coupe: "tapered" })
  },
  // === PANTALONS ===
  {
    pattern: /^pantalon-tailleur$/,
    extract: () => ({ subtype: "pantalon-tailleur", coupe: "tailleur" })
  },
  {
    pattern: /^pantalon-cigarette$/,
    extract: () => ({ subtype: "pantalon", coupe: "cigarette" })
  },
  {
    pattern: /^pantalon-large$/,
    extract: () => ({ subtype: "pantalon", coupe: "large" })
  },
  {
    pattern: /^pantalon-palazzo$/,
    extract: () => ({ subtype: "pantalon", coupe: "palazzo" })
  },
  {
    pattern: /^pantalon-carrot$/,
    extract: () => ({ subtype: "pantalon", coupe: "carrot" })
  },
  {
    pattern: /^pantalon-fluide$/,
    extract: () => ({ subtype: "pantalon", coupe: "fluide" })
  },
  { pattern: /^pantalon-cuir$/, extract: () => ({ subtype: "pantalon-cuir" }) },
  {
    pattern: /^pantalon-smoking$/,
    extract: () => ({ subtype: "pantalon-smoking", coupe: "tailleur" })
  },
  {
    pattern: /^pantalon-velours$/,
    extract: () => ({ subtype: "pantalon-velours" })
  },
  {
    pattern: /^pantalon-toile$/,
    extract: () => ({ subtype: "pantalon-toile" })
  },
  {
    pattern: /^pantalon-survetement$/,
    extract: () => ({ subtype: "survetement" })
  },
  {
    pattern: /^chino-slim$/,
    extract: () => ({ subtype: "chino", coupe: "slim" })
  },
  {
    pattern: /^chino-regular$/,
    extract: () => ({ subtype: "chino", coupe: "regular" })
  },
  // === ROBES ===
  {
    pattern: /^robe-courte$/,
    extract: () => ({ subtype: "robe", longueur: "court" })
  },
  {
    pattern: /^robe-midi$/,
    extract: () => ({ subtype: "robe", longueur: "midi" })
  },
  {
    pattern: /^robe-longue$/,
    extract: () => ({ subtype: "robe", longueur: "long" })
  },
  {
    pattern: /^robe-maxi$/,
    extract: () => ({ subtype: "robe", longueur: "maxi" })
  },
  {
    pattern: /^robe-cocktail$/,
    extract: () => ({ subtype: "robe-cocktail", style_detail: "cocktail" })
  },
  {
    pattern: /^robe-soiree$/,
    extract: () => ({ subtype: "robe-soiree", style_detail: "soiree" })
  },
  {
    pattern: /^robe-ceremonie$/,
    extract: () => ({ subtype: "robe-ceremonie", style_detail: "ceremonie" })
  },
  { pattern: /^robe-pull$/, extract: () => ({ subtype: "robe-pull" }) },
  { pattern: /^robe-chemise$/, extract: () => ({ subtype: "robe-chemise" }) },
  { pattern: /^robe-tshirt$/, extract: () => ({ subtype: "robe-tshirt" }) },
  {
    pattern: /^robe-princesse$/,
    extract: () => ({ subtype: "robe-princesse", style_detail: "princesse" })
  },
  {
    pattern: /^robe-tutu$/,
    extract: () => ({ subtype: "robe-tutu", style_detail: "tutu" })
  },
  // === JUPES ===
  {
    pattern: /^jupe-courte$/,
    extract: () => ({ subtype: "jupe", longueur: "court" })
  },
  {
    pattern: /^jupe-midi$/,
    extract: () => ({ subtype: "jupe", longueur: "midi" })
  },
  {
    pattern: /^jupe-longue$/,
    extract: () => ({ subtype: "jupe", longueur: "long" })
  },
  {
    pattern: /^jupe-crayon$/,
    extract: () => ({ subtype: "jupe", style_detail: "crayon" })
  },
  {
    pattern: /^jupe-plissee$/,
    extract: () => ({ subtype: "jupe", style_detail: "plissee" })
  },
  {
    pattern: /^jupe-patineuse$/,
    extract: () => ({ subtype: "jupe", style_detail: "patineuse" })
  },
  {
    pattern: /^jupe-trapeze$/,
    extract: () => ({ subtype: "jupe", style_detail: "trapeze" })
  },
  {
    pattern: /^jupe-portefeuille$/,
    extract: () => ({ subtype: "jupe", style_detail: "portefeuille" })
  },
  { pattern: /^jupe-jean$/, extract: () => ({ subtype: "jupe-jean" }) },
  {
    pattern: /^jupe-tutu$/,
    extract: () => ({ subtype: "jupe-tutu", style_detail: "tutu" })
  },
  // === HAUTS ===
  {
    pattern: /^crop-top$/,
    extract: () => ({ subtype: "crop-top", longueur: "court" })
  },
  {
    pattern: /^chemise-casual$/,
    extract: () => ({ subtype: "chemise", style_detail: "casual" })
  },
  {
    pattern: /^chemise-jean$/,
    extract: () => ({ subtype: "chemise", style_detail: "jean" })
  },
  {
    pattern: /^chemise-flanelle$/,
    extract: () => ({ subtype: "chemise", style_detail: "flanelle" })
  },
  {
    pattern: /^chemise-lin$/,
    extract: () => ({ subtype: "chemise", style_detail: "lin" })
  },
  {
    pattern: /^chemise-costume$/,
    extract: () => ({ subtype: "chemise", style_detail: "costume" })
  },
  {
    pattern: /^chemise-smoking$/,
    extract: () => ({ subtype: "chemise", style_detail: "smoking" })
  },
  // === MANTEAUX ===
  { pattern: /^blouson-cuir$/, extract: () => ({ subtype: "blouson-cuir" }) },
  { pattern: /^blouson-jean$/, extract: () => ({ subtype: "blouson-jean" }) },
  {
    pattern: /^blouson-bomber$/,
    extract: () => ({ subtype: "blouson-bomber", style_detail: "bomber" })
  },
  {
    pattern: /^blouson-teddy$/,
    extract: () => ({ subtype: "blouson-teddy", style_detail: "teddy" })
  },
  {
    pattern: /^blouson-aviateur$/,
    extract: () => ({ subtype: "blouson-aviateur", style_detail: "aviateur" })
  },
  {
    pattern: /^blouson-harrington$/,
    extract: () => ({
      subtype: "blouson-harrington",
      style_detail: "harrington"
    })
  },
  {
    pattern: /^veste-tailleur$/,
    extract: () => ({ subtype: "veste-tailleur" })
  },
  { pattern: /^veste-costume$/, extract: () => ({ subtype: "veste-costume" }) },
  { pattern: /^duffle-coat$/, extract: () => ({ subtype: "duffle-coat" }) },
  // === BASKETS / CHAUSSURES ===
  {
    pattern: /^baskets-basses$/,
    extract: () => ({ subtype: "baskets", style_detail: "basses" })
  },
  {
    pattern: /^baskets-montantes$/,
    extract: () => ({ subtype: "baskets", style_detail: "montantes" })
  },
  {
    pattern: /^sneakers-running$/,
    extract: () => ({ subtype: "sneakers", style_detail: "running" })
  },
  {
    pattern: /^sneakers-lifestyle$/,
    extract: () => ({ subtype: "sneakers", style_detail: "lifestyle" })
  },
  {
    pattern: /^sneakers-cuir$/,
    extract: () => ({ subtype: "sneakers", style_detail: "cuir" })
  },
  {
    pattern: /^sneakers-toile$/,
    extract: () => ({ subtype: "sneakers", style_detail: "toile" })
  },
  {
    pattern: /^sandales-talons$/,
    extract: () => ({ subtype: "sandales", talon_type: "haut-talon" })
  },
  {
    pattern: /^sandales-plates$/,
    extract: () => ({ subtype: "sandales", talon_type: "plat" })
  },
  {
    pattern: /^sandales-cuir$/,
    extract: () => ({ subtype: "sandales", style_detail: "cuir" })
  },
  {
    pattern: /^sandales-sport$/,
    extract: () => ({ subtype: "sandales", style_detail: "sport" })
  },
  {
    pattern: /^mules-talons$/,
    extract: () => ({ subtype: "mules", talon_type: "haut-talon" })
  },
  {
    pattern: /^mules-plates$/,
    extract: () => ({ subtype: "mules", talon_type: "plat" })
  },
  {
    pattern: /^bottines-chelsea$/,
    extract: () => ({ subtype: "bottines", style_detail: "chelsea" })
  },
  {
    pattern: /^bottines-western$/,
    extract: () => ({ subtype: "bottines", style_detail: "western" })
  },
  {
    pattern: /^bottines-lacets$/,
    extract: () => ({ subtype: "bottines", style_detail: "lacets" })
  },
  {
    pattern: /^bottes-hautes$/,
    extract: () => ({ subtype: "bottes", longueur: "long" })
  },
  {
    pattern: /^bottes-cavalieres$/,
    extract: () => ({ subtype: "bottes", style_detail: "cavalieres" })
  },
  {
    pattern: /^bottes-pluie$/,
    extract: () => ({ subtype: "bottes", style_detail: "pluie" })
  },
  {
    pattern: /^bottes-neige$/,
    extract: () => ({ subtype: "bottes", style_detail: "neige" })
  },
  {
    pattern: /^bottes-motardes$/,
    extract: () => ({ subtype: "bottes", style_detail: "motardes" })
  },
  {
    pattern: /^boots-chelsea$/,
    extract: () => ({ subtype: "boots", style_detail: "chelsea" })
  },
  {
    pattern: /^boots-chukka$/,
    extract: () => ({ subtype: "boots", style_detail: "chukka" })
  },
  {
    pattern: /^boots-desert$/,
    extract: () => ({ subtype: "boots", style_detail: "desert" })
  },
  // === SACS ===
  { pattern: /^sac-main$/, extract: () => ({ subtype: "sac-main" }) },
  { pattern: /^sac-epaule$/, extract: () => ({ subtype: "sac-epaule" }) },
  {
    pattern: /^sac-porte-main$/,
    extract: () => ({ subtype: "sac-porte-main" })
  },
  { pattern: /^sac-bowling$/, extract: () => ({ subtype: "sac-bowling" }) },
  { pattern: /^sac-docteur$/, extract: () => ({ subtype: "sac-docteur" }) },
  { pattern: /^sac-seau$/, extract: () => ({ subtype: "sac-seau" }) },
  {
    pattern: /^sac-bandouliere$/,
    extract: () => ({ subtype: "sac-bandouliere" })
  },
  { pattern: /^sac-cartable$/, extract: () => ({ subtype: "sac-cartable" }) },
  {
    pattern: /^pochette-soiree$/,
    extract: () => ({ subtype: "pochette", style_detail: "soiree" })
  },
  {
    pattern: /^sac-dos-mini$/,
    extract: () => ({ subtype: "sac-dos", sac_taille: "mini" })
  },
  {
    pattern: /^sac-dos-ordinateur$/,
    extract: () => ({ subtype: "sac-dos", style_detail: "ordinateur" })
  },
  {
    pattern: /^sac-dos-ville$/,
    extract: () => ({ subtype: "sac-dos", style_detail: "ville" })
  },
  {
    pattern: /^sac-dos-sport$/,
    extract: () => ({ subtype: "sac-dos", style_detail: "sport" })
  },
  {
    pattern: /^sacoche-ordinateur$/,
    extract: () => ({ subtype: "sacoche", style_detail: "ordinateur" })
  },
  {
    pattern: /^sac-voyage$/,
    extract: () => ({ subtype: "sac-voyage", sac_taille: "grand" })
  },
  {
    pattern: /^sac-weekend$/,
    extract: () => ({ subtype: "sac-weekend", sac_taille: "grand" })
  },
  {
    pattern: /^valise-cabine$/,
    extract: () => ({ subtype: "valise", sac_taille: "moyen" })
  },
  // === LINGERIE ===
  { pattern: /^bandeau-lingerie$/, extract: () => ({ subtype: "bandeau" }) },
  { pattern: /^body-lingerie$/, extract: () => ({ subtype: "body-lingerie" }) },
  {
    pattern: /^kimono-satin$/,
    extract: () => ({ subtype: "kimono", style_detail: "satin" })
  },
  { pattern: /^chemise-nuit$/, extract: () => ({ subtype: "chemise-nuit" }) },
  {
    pattern: /^ensemble-lingerie$/,
    extract: () => ({ subtype: "ensemble-lingerie" })
  },
  // === MAILLOTS ===
  {
    pattern: /^maillot-1-piece$/,
    extract: () => ({ subtype: "maillot-1-piece" })
  },
  {
    pattern: /^maillot-2-pieces$/,
    extract: () => ({ subtype: "maillot-2-pieces" })
  },
  // === SPORT ===
  {
    pattern: /^brassiere-sport$/,
    extract: () => ({ subtype: "brassiere-sport" })
  },
  { pattern: /^top-sport$/, extract: () => ({ subtype: "top-sport" }) },
  { pattern: /^legging-sport$/, extract: () => ({ subtype: "legging-sport" }) },
  { pattern: /^short-sport$/, extract: () => ({ subtype: "short-sport" }) },
  {
    pattern: /^pantalon-sport$/,
    extract: () => ({ subtype: "pantalon-sport" })
  },
  { pattern: /^veste-sport$/, extract: () => ({ subtype: "veste-sport" }) },
  {
    pattern: /^ensemble-sport$/,
    extract: () => ({ subtype: "ensemble-sport" })
  },
  { pattern: /^coupe-vent/, extract: () => ({ subtype: "coupe-vent" }) },
  {
    pattern: /^ensemble-survetement$/,
    extract: () => ({ subtype: "survetement" })
  },
  { pattern: /^tshirt-sport$/, extract: () => ({ subtype: "tshirt-sport" }) },
  {
    pattern: /^debardeur-sport$/,
    extract: () => ({ subtype: "debardeur-sport" })
  },
  // === SHORTS ===
  { pattern: /^short-jean$/, extract: () => ({ subtype: "short-jean" }) },
  {
    pattern: /^short-tailleur$/,
    extract: () => ({ subtype: "short-tailleur", style_detail: "tailleur" })
  },
  { pattern: /^short-chino$/, extract: () => ({ subtype: "short-chino" }) },
  {
    pattern: /^short-cycliste$/,
    extract: () => ({ subtype: "short-cycliste", coupe: "cycliste" })
  },
  { pattern: /^short-cargo$/, extract: () => ({ subtype: "short-cargo" }) },
  { pattern: /^short-toile$/, extract: () => ({ subtype: "short-toile" }) },
  { pattern: /^short-bain$/, extract: () => ({ subtype: "short-bain" }) },
  // === COMBINAISONS ===
  {
    pattern: /^combinaison-pantalon$/,
    extract: () => ({ subtype: "combinaison-pantalon" })
  },
  {
    pattern: /^combinaison-short$/,
    extract: () => ({ subtype: "combinaison-short" })
  },
  {
    pattern: /^salopette-jean$/,
    extract: () => ({ subtype: "salopette-jean" })
  },
  {
    pattern: /^salopette-short$/,
    extract: () => ({ subtype: "salopette-short" })
  },
  { pattern: /^combi-pilote$/, extract: () => ({ subtype: "combi-pilote" }) },
  // === COSTUMES HOMME ===
  {
    pattern: /^costume-2-pieces$/,
    extract: () => ({ subtype: "costume-2-pieces" })
  },
  {
    pattern: /^costume-3-pieces$/,
    extract: () => ({ subtype: "costume-3-pieces" })
  },
  {
    pattern: /^pantalon-costume$/,
    extract: () => ({ subtype: "pantalon-costume" })
  },
  { pattern: /^gilet-costume$/, extract: () => ({ subtype: "gilet-costume" }) },
  // === BIJOUX ===
  {
    pattern: /^boucles-oreilles-puces$/,
    extract: () => ({ subtype: "boucles-oreilles", style_detail: "puces" })
  },
  {
    pattern: /^boucles-oreilles-creoles$/,
    extract: () => ({ subtype: "boucles-oreilles", style_detail: "creoles" })
  },
  {
    pattern: /^boucles-oreilles-pendantes$/,
    extract: () => ({ subtype: "boucles-oreilles", style_detail: "pendantes" })
  },
  {
    pattern: /^bague-chevaliere$/,
    extract: () => ({ subtype: "bague", style_detail: "chevaliere" })
  },
  {
    pattern: /^boutons-manchette$/,
    extract: () => ({ subtype: "boutons-manchette" })
  },
  // === CHAPEAUX / ACCESSOIRES ===
  {
    pattern: /^lunettes-soleil$/,
    extract: () => ({ subtype: "lunettes-soleil" })
  },
  { pattern: /^lunettes-vue$/, extract: () => ({ subtype: "lunettes-vue" }) },
  {
    pattern: /^ceinture-cuir$/,
    extract: () => ({ subtype: "ceinture", style_detail: "cuir" })
  },
  {
    pattern: /^ceinture-tissu$/,
    extract: () => ({ subtype: "ceinture", style_detail: "tissu" })
  },
  {
    pattern: /^ceinture-chaine$/,
    extract: () => ({ subtype: "ceinture", style_detail: "chaine" })
  },
  {
    pattern: /^ceinture-toile$/,
    extract: () => ({ subtype: "ceinture", style_detail: "toile" })
  },
  {
    pattern: /^ceinture-tressee$/,
    extract: () => ({ subtype: "ceinture", style_detail: "tressee" })
  },
  {
    pattern: /^montre-automatique$/,
    extract: () => ({ subtype: "montre", style_detail: "automatique" })
  },
  {
    pattern: /^montre-quartz$/,
    extract: () => ({ subtype: "montre", style_detail: "quartz" })
  },
  {
    pattern: /^montre-chronographe$/,
    extract: () => ({ subtype: "montre", style_detail: "chronographe" })
  },
  {
    pattern: /^montre-connectee$/,
    extract: () => ({ subtype: "montre", style_detail: "connectee" })
  },
  // === MATERNITÉ ===
  {
    pattern: /^jean-grossesse$/,
    extract: () => ({ subtype: "jean-grossesse" })
  },
  {
    pattern: /^pantalon-grossesse$/,
    extract: () => ({ subtype: "pantalon-grossesse" })
  },
  {
    pattern: /^legging-grossesse$/,
    extract: () => ({ subtype: "legging-grossesse" })
  },
  {
    pattern: /^jupe-grossesse$/,
    extract: () => ({ subtype: "jupe-grossesse" })
  },
  {
    pattern: /^robe-grossesse$/,
    extract: () => ({ subtype: "robe-grossesse" })
  },
  { pattern: /^top-grossesse$/, extract: () => ({ subtype: "top-grossesse" }) },
  {
    pattern: /^pull-grossesse$/,
    extract: () => ({ subtype: "pull-grossesse" })
  },
  {
    pattern: /^soutien-gorge-allaitement$/,
    extract: () => ({ subtype: "soutien-gorge-allaitement" })
  },
  {
    pattern: /^body-allaitement$/,
    extract: () => ({ subtype: "body-allaitement" })
  },
  // === BÉBÉ VÊTEMENTS ===
  {
    pattern: /^body-manches-courtes$/,
    extract: () => ({ subtype: "body", manches: "manches-courtes" })
  },
  {
    pattern: /^body-manches-longues$/,
    extract: () => ({ subtype: "body", manches: "manches-longues" })
  },
  {
    pattern: /^body-sans-manches$/,
    extract: () => ({ subtype: "body", manches: "sans-manches" })
  },
  {
    pattern: /^pyjama-1-piece$/,
    extract: () => ({ subtype: "pyjama-1-piece" })
  },
  {
    pattern: /^pyjama-2-pieces$/,
    extract: () => ({ subtype: "pyjama-2-pieces" })
  },
  {
    pattern: /^ensemble-2-pieces$/,
    extract: () => ({ subtype: "ensemble-2-pieces" })
  },
  {
    pattern: /^ensemble-3-pieces$/,
    extract: () => ({ subtype: "ensemble-3-pieces" })
  },
  {
    pattern: /^tenue-complete$/,
    extract: () => ({ subtype: "tenue-complete" })
  },
  {
    pattern: /^combinaison-pilote$/,
    extract: () => ({ subtype: "combinaison-pilote" })
  },
  { pattern: /^combi-ski$/, extract: () => ({ subtype: "combi-ski" }) },
  { pattern: /^nid-ange$/, extract: () => ({ subtype: "nid-ange" }) },
  {
    pattern: /^turbulette-ete$/,
    extract: () => ({ subtype: "turbulette", style_detail: "ete" })
  },
  {
    pattern: /^turbulette-hiver$/,
    extract: () => ({ subtype: "turbulette", style_detail: "hiver" })
  },
  {
    pattern: /^chaussons-souples$/,
    extract: () => ({ subtype: "chaussons", style_detail: "souples" })
  },
  {
    pattern: /^chaussons-fourres$/,
    extract: () => ({ subtype: "chaussons", style_detail: "fourres" })
  },
  {
    pattern: /^bonnet-naissance$/,
    extract: () => ({ subtype: "bonnet", style_detail: "naissance" })
  },
  {
    pattern: /^bonnet-hiver$/,
    extract: () => ({ subtype: "bonnet", style_detail: "hiver" })
  },
  {
    pattern: /^chapeau-soleil$/,
    extract: () => ({ subtype: "chapeau", style_detail: "soleil" })
  },
  { pattern: /^casquette-bebe$/, extract: () => ({ subtype: "casquette" }) },
  { pattern: /^bandeau-bebe$/, extract: () => ({ subtype: "bandeau" }) },
  {
    pattern: /^bavoir-bandana$/,
    extract: () => ({ subtype: "bavoir", style_detail: "bandana" })
  },
  {
    pattern: /^bavoir-classique$/,
    extract: () => ({ subtype: "bavoir", style_detail: "classique" })
  },
  {
    pattern: /^couverture-polaire$/,
    extract: () => ({ subtype: "couverture", style_detail: "polaire" })
  },
  {
    pattern: /^couverture-coton$/,
    extract: () => ({ subtype: "couverture", style_detail: "coton" })
  },
  { pattern: /^plaid-bebe$/, extract: () => ({ subtype: "plaid" }) },
  // === HANDMADE ===
  { pattern: /^collier-fait-main$/, extract: () => ({ subtype: "collier" }) },
  { pattern: /^bracelet-fait-main$/, extract: () => ({ subtype: "bracelet" }) },
  {
    pattern: /^boucles-oreilles-fait-main$/,
    extract: () => ({ subtype: "boucles-oreilles" })
  },
  { pattern: /^bague-fait-main$/, extract: () => ({ subtype: "bague" }) },
  { pattern: /^bougie-artisanale$/, extract: () => ({ subtype: "bougie" }) },
  { pattern: /^cadre-fait-main$/, extract: () => ({ subtype: "cadre" }) },
  {
    pattern: /^objet-deco-fait-main$/,
    extract: () => ({ subtype: "objet-deco" })
  },
  { pattern: /^tricot-fait-main$/, extract: () => ({ subtype: "tricot" }) },
  { pattern: /^couture-fait-main$/, extract: () => ({ subtype: "couture" }) },
  { pattern: /^sac-fait-main$/, extract: () => ({ subtype: "sac" }) },
  { pattern: /^echarpe-fait-main$/, extract: () => ({ subtype: "echarpe" }) },
  { pattern: /^bonnet-tricote$/, extract: () => ({ subtype: "bonnet" }) },
  { pattern: /^pochette-fait-main$/, extract: () => ({ subtype: "pochette" }) },
  { pattern: /^doudou-fait-main$/, extract: () => ({ subtype: "doudou" }) },
  {
    pattern: /^vetement-bebe-fait-main$/,
    extract: () => ({ subtype: "vetement-bebe" })
  },
  { pattern: /^couverture-bebe$/, extract: () => ({ subtype: "couverture" }) },
  { pattern: /^mobile-bebe$/, extract: () => ({ subtype: "mobile" }) },
  { pattern: /^carnet-fait-main$/, extract: () => ({ subtype: "carnet" }) },
  { pattern: /^carte-fait-main$/, extract: () => ({ subtype: "carte" }) },
  { pattern: /^stickers-fait-main$/, extract: () => ({ subtype: "stickers" }) },
  {
    pattern: /^marque-page-fait-main$/,
    extract: () => ({ subtype: "marque-page" })
  },
  // === MAILLOTS HOMME ===
  { pattern: /^slip-bain$/, extract: () => ({ subtype: "slip-bain" }) },
  { pattern: /^boxer-bain$/, extract: () => ({ subtype: "boxer-bain" }) },
  // === ENSEMBLES FEMME ===
  { pattern: /^tailleur-jupe$/, extract: () => ({ subtype: "tailleur-jupe" }) },
  {
    pattern: /^tailleur-pantalon$/,
    extract: () => ({ subtype: "tailleur-pantalon" })
  },
  {
    pattern: /^ensemble-coordonne$/,
    extract: () => ({ subtype: "ensemble-coordonne" })
  },
  // === MAILLOTS BAIN ENFANT ===
  {
    pattern: /^maillot-bain-1-piece$/,
    extract: () => ({ subtype: "maillot-1-piece" })
  },
  {
    pattern: /^maillot-bain-2-pieces$/,
    extract: () => ({ subtype: "maillot-2-pieces" })
  }
];
var _leafToGroupCache = null;
function buildLeafToGroupMap() {
  const map = /* @__PURE__ */ new Map();
  const PRIMARY_PROFILES = [
    "ADULT_WOMAN",
    "ADULT_MAN",
    "CHILD_GIRL",
    "CHILD_BOY",
    "BABY_GIRL",
    "BABY_BOY"
  ];
  for (const profileKey of PRIMARY_PROFILES) {
    const profileData = CATEGORY_STRUCTURE[profileKey];
    if (!profileData?.subcategories) continue;
    for (const [mainCat, subCats] of Object.entries(
      profileData.subcategories
    )) {
      for (const [groupKey, items] of Object.entries(
        subCats
      )) {
        if (!Array.isArray(items)) continue;
        for (const leafSlug of items) {
          if (map.has(leafSlug)) continue;
          const info = {
            group: groupKey,
            mainCategory: mainCat,
            profile: profileKey
          };
          for (const rule of ATTRIBUTE_EXTRACTION_RULES) {
            const match = leafSlug.match(rule.pattern);
            if (match) {
              Object.assign(info, rule.extract(match));
              break;
            }
          }
          if (!info.subtype) {
            info.subtype = leafSlug;
          }
          map.set(leafSlug, info);
        }
      }
    }
  }
  return map;
}
function getGroupForLeaf(leafSlug) {
  if (!leafSlug) return null;
  if (!_leafToGroupCache) {
    _leafToGroupCache = buildLeafToGroupMap();
  }
  return _leafToGroupCache.get(leafSlug) ?? null;
}
function isGroupSlug(slug) {
  if (!slug) return false;
  const PRIMARY_PROFILES = [
    "ADULT_WOMAN",
    "ADULT_MAN",
    "CHILD_GIRL",
    "CHILD_BOY",
    "BABY_GIRL",
    "BABY_BOY"
  ];
  for (const profileKey of PRIMARY_PROFILES) {
    const profileData = CATEGORY_STRUCTURE[profileKey];
    if (!profileData?.subcategories) continue;
    for (const subCats of Object.values(profileData.subcategories)) {
      if (slug in subCats) {
        return true;
      }
    }
  }
  return false;
}
function getLeafsForGroup(groupSlug) {
  if (!_leafToGroupCache) {
    _leafToGroupCache = buildLeafToGroupMap();
  }
  const leafs = [];
  for (const [leaf, info] of _leafToGroupCache.entries()) {
    if (info.group === groupSlug) {
      leafs.push(leaf);
    }
  }
  return leafs;
}

// src/constants/categoryIcons.tsx
import {
  Shirt,
  Triangle,
  Minus,
  Package,
  Layers,
  Wind,
  CloudRain,
  Snowflake,
  Sun,
  Waves,
  Flame,
  Activity,
  Sparkles,
  Heart,
  Star,
  Crown,
  Footprints,
  ShoppingBag,
  Backpack,
  Briefcase,
  Watch,
  Inbox,
  Gift,
  Gem,
  Diamond,
  Eye,
  Hand,
  User,
  UserCircle,
  Baby,
  Search,
  X,
  ChevronRight,
  Check,
  TrendingUp,
  Clock,
  CheckCircle,
  Circle,
  AlertCircle,
  Info,
  Mountain,
  Target,
  Home,
  Moon,
  Music,
  Palette,
  Compass,
  Glasses
} from "lucide-react";
var SIMPLE_CATEGORY_ICONS = {
  // CATÉGORIES PRINCIPALES
  clothing: Shirt,
  shoes: Footprints,
  bags: ShoppingBag,
  accessories: Watch,
  // PROFILS
  woman: User,
  man: User,
  girl: UserCircle,
  boy: UserCircle,
  baby: Baby,
  femme: User,
  homme: User,
  fille: UserCircle,
  garcon: UserCircle,
  bebe: Baby,
  // HAUTS
  tops: Shirt,
  hauts: Shirt,
  "t-shirts": Shirt,
  tshirts: Shirt,
  chemises: Shirt,
  shirts: Shirt,
  chemisiers: Shirt,
  blouses: Shirt,
  debardeurs: Shirt,
  "crop-tops": Shirt,
  bustiers: Heart,
  // PULLS & GILETS
  sweaters: Layers,
  pulls: Layers,
  cardigans: Layers,
  gilets: Layers,
  sweatshirts: Layers,
  hoodies: Layers,
  // ROBES & JUPES
  dresses: Sparkles,
  robes: Sparkles,
  "robes-courtes": Sparkles,
  "robes-longues": Sparkles,
  "robes-soiree": Crown,
  "robes-cocktail": Crown,
  skirts: Triangle,
  jupes: Triangle,
  "jupes-courtes": Triangle,
  "jupes-longues": Triangle,
  "jupes-midi": Triangle,
  // BAS
  pants: Minus,
  pantalons: Minus,
  jeans: Minus,
  leggings: Minus,
  joggers: Activity,
  chinos: Minus,
  shorts: Sun,
  bermudas: Sun,
  // COMBINAISONS & ENSEMBLES
  jumpsuits: Package,
  combinaisons: Package,
  salopettes: Package,
  combishorts: Package,
  sets: Gift,
  ensembles: Gift,
  "tenues-completes": Gift,
  bodysuits: Package,
  bodies: Package,
  // MANTEAUX & VESTES
  outerwear: CloudRain,
  manteaux: CloudRain,
  vestes: CloudRain,
  blousons: CloudRain,
  parkas: CloudRain,
  trenchs: CloudRain,
  impermeables: CloudRain,
  doudounes: Snowflake,
  anoraks: Snowflake,
  // LINGERIE & SOUS-VÊTEMENTS
  lingerie: Heart,
  "sous-vetements": Heart,
  underwear: Heart,
  "soutiens-gorge": Heart,
  culottes: Heart,
  boxers: Package,
  calecons: Package,
  chaussettes: Minus,
  collants: Minus,
  // MAILLOTS DE BAIN
  swimwear: Waves,
  maillots: Waves,
  "maillots-bain": Waves,
  bikinis: Waves,
  "maillots-une-piece": Waves,
  // SPORT (VÊTEMENTS)
  sportswear: Activity,
  "sport-vetements": Activity,
  fitness: Activity,
  yoga: Activity,
  "running-vetements": Activity,
  "football-vetements": Activity,
  // MATERNITÉ
  maternity: Heart,
  maternite: Heart,
  grossesse: Heart,
  allaitement: Heart,
  // COSTUMES & FORMEL
  suits: Briefcase,
  costumes: Briefcase,
  tailleurs: Briefcase,
  smokings: Briefcase,
  // NUIT & MAISON
  sleepwear: Moon,
  pyjamas: Moon,
  "chemises-nuit": Moon,
  peignoirs: Home,
  "robes-chambre": Home,
  sleepbags: Snowflake,
  gigoteuses: Snowflake,
  "nids-ange": Snowflake,
  // DÉGUISEMENTS
  deguisements: Palette,
  carnaval: Palette,
  halloween: Palette,
  // DANSE
  danse: Music,
  tutu: Music,
  justaucorps: Music,
  leotards: Music,
  // CHAUSSURES
  footwear: Footprints,
  chaussures: Footprints,
  sneakers: Activity,
  baskets: Activity,
  "tennis-chaussures": Activity,
  heels: Crown,
  talons: Crown,
  escarpins: Crown,
  boots: Mountain,
  bottes: Mountain,
  bottines: Mountain,
  bottillons: Mountain,
  sandals: Sun,
  sandales: Sun,
  "nu-pieds": Sun,
  tongs: Sun,
  flats: Star,
  ballerines: Star,
  mocassins: Star,
  derbies: Star,
  mules: Sparkles,
  sabots: Sparkles,
  dress: Briefcase,
  "chaussures-ville": Briefcase,
  richelieu: Briefcase,
  "sport-chaussures": Activity,
  slippers: Home,
  pantoufles: Home,
  chaussons: Home,
  "chaussons-bebe": Baby,
  // SACS
  handbags: ShoppingBag,
  "sacs-main": ShoppingBag,
  sacs: ShoppingBag,
  crossbody: ShoppingBag,
  bandouliere: ShoppingBag,
  clutches: Gem,
  pochettes: Gem,
  "mini-sacs": Gem,
  backpacks: Backpack,
  "sacs-dos": Backpack,
  totes: Inbox,
  cabas: Inbox,
  "sacs-shopping": Inbox,
  travel: Gift,
  "sacs-voyage": Gift,
  valises: Gift,
  "sacs-weekend": Gift,
  messenger: Briefcase,
  besaces: Briefcase,
  cartables: Briefcase,
  wallets: Package,
  portefeuilles: Package,
  "porte-monnaie": Package,
  // ACCESSOIRES
  jewelry: Gem,
  bijoux: Gem,
  colliers: Gem,
  bracelets: Gem,
  bagues: Diamond,
  "boucles-oreilles": Gem,
  broches: Star,
  scarves: Wind,
  echarpes: Wind,
  foulards: Wind,
  etoles: Wind,
  cheches: Wind,
  headwear: Compass,
  chapeaux: Compass,
  bonnets: Snowflake,
  casquettes: Sun,
  bandeaux: Minus,
  barrettes: Star,
  "serre-tete": Sparkles,
  eyewear: Glasses,
  lunettes: Glasses,
  "lunettes-soleil": Sun,
  "lunettes-vue": Eye,
  gloves: Hand,
  gants: Hand,
  moufles: Snowflake,
  mitaines: Hand,
  watches: Watch,
  montres: Watch,
  belts: Target,
  ceintures: Target,
  ties: Briefcase,
  cravates: Briefcase,
  "noeuds-papillon": Star,
  winter: Snowflake,
  hiver: Snowflake,
  "cache-cou": Wind,
  "cache-oreilles": Snowflake,
  bibs: Baby,
  bavoirs: Baby,
  bavettes: Baby,
  blankets: Heart,
  couvertures: Heart,
  plaids: Heart,
  doudous: Heart,
  // FAIT MAIN / HANDMADE
  handmade: Heart,
  "fait-main": Heart,
  artisanal: Heart,
  "collier-fait-main": Gem,
  "bracelet-fait-main": Gem,
  "boucles-oreilles-fait-main": Gem,
  "bague-fait-main": Diamond,
  "bougie-artisanale": Flame,
  poterie: Home,
  "cadre-fait-main": Home,
  "objet-deco-fait-main": Home,
  "tricot-fait-main": Heart,
  crochet: Heart,
  "couture-fait-main": Heart,
  broderie: Sparkles,
  "sac-fait-main": ShoppingBag,
  "echarpe-fait-main": Wind,
  "bonnet-tricote": Snowflake,
  "pochette-fait-main": Gem,
  "doudou-fait-main": Heart,
  "vetement-bebe-fait-main": Baby,
  "couverture-bebe": Heart,
  "mobile-bebe": Star,
  "carnet-fait-main": Package,
  "carte-fait-main": Gift,
  "stickers-fait-main": Sparkles,
  "marque-page-fait-main": Package,
  // PERSONNALISABLE / CUSTOM
  custom: Palette,
  personnalisable: Palette,
  "tshirt-personnalise": Shirt,
  "sweat-personnalise": Shirt,
  "hoodie-personnalise": Shirt,
  "body-personnalise": Baby,
  "tote-bag-personnalise": ShoppingBag,
  "collier-prenom": Gem,
  "bracelet-grave": Gem,
  "bijou-personnalise": Gem,
  "coussin-personnalise": Heart,
  "mug-personnalise": Gift,
  "affiche-prenom": Palette,
  "doudou-brode": Heart,
  "bavoir-personnalise": Baby,
  "coffret-cadeau-personnalise": Gift,
  // UTILS
  search: Search,
  close: X,
  chevronRight: ChevronRight,
  check: Check,
  heart: Heart,
  trending: TrendingUp,
  clock: Clock,
  star: Star,
  Sparkles,
  Star,
  CheckCircle,
  Circle,
  AlertCircle,
  Info
};
function getCategoryIcon(categoryKey) {
  const normalizedKey = categoryKey.toLowerCase().trim();
  return SIMPLE_CATEGORY_ICONS[normalizedKey] || Shirt;
}
var CATEGORY_ICONS = SIMPLE_CATEGORY_ICONS;

// src/constants/pricing.ts
var GLOWP_SERVICE_FEE_FIXED = 0.6;
var GLOWP_SERVICE_FEE_RATE = 0.05;
var GLOWP_SERVICE_FEE_MAX = 20;
function calculateGlowpServiceFee(itemPrice) {
  const raw = GLOWP_SERVICE_FEE_FIXED + itemPrice * GLOWP_SERVICE_FEE_RATE;
  return parseFloat(Math.min(raw, GLOWP_SERVICE_FEE_MAX).toFixed(2));
}

// src/components/CategorySelector.tsx
import React, { useState as useState7, useMemo as useMemo7 } from "react";
import { Search as Search2, ChevronRight as ChevronRight2, Sparkles as Sparkles2, User as User2, Baby as Baby2, UserCircle as UserCircle2 } from "lucide-react";
var CategorySelector = ({
  selectedProfile,
  selectedMainCategory,
  selectedSubCategory,
  onProfileChange,
  onMainCategoryChange,
  onSubCategoryChange,
  className = ""
}) => {
  const [searchQuery, setSearchQuery] = useState7("");
  const availableMainCategories = useMemo7(() => {
    if (!selectedProfile) return [];
    const structure = CATEGORY_STRUCTURE[selectedProfile];
    return structure?.mainCategories || [];
  }, [selectedProfile]);
  const availableSubCategories = useMemo7(() => {
    if (!selectedProfile || !selectedMainCategory) return [];
    return getAllCategoryItems(selectedProfile, selectedMainCategory);
  }, [selectedProfile, selectedMainCategory]);
  const filteredSubCategories = useMemo7(() => {
    if (!searchQuery) return availableSubCategories;
    return availableSubCategories.filter(
      (sub) => sub.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, availableSubCategories]);
  const handleProfileSelect = (profile) => {
    onProfileChange(profile);
    onMainCategoryChange("");
    onSubCategoryChange("");
  };
  const handleMainCategorySelect = (categoryId) => {
    onMainCategoryChange(categoryId);
    onSubCategoryChange("");
  };
  return /* @__PURE__ */ React.createElement("div", { className: `space-y-6 ${className}` }, /* @__PURE__ */ React.createElement("div", { className: "mb-6" }, /* @__PURE__ */ React.createElement("label", { className: "flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3" }, /* @__PURE__ */ React.createElement(Sparkles2, { className: "w-4 h-4 text-brand-500" }), "1. Pour qui vendez-vous ? ", /* @__PURE__ */ React.createElement("span", { className: "text-red-500" }, "*")), /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-2 sm:grid-cols-3 gap-3" }, USER_PROFILES.map((profile) => {
    const ProfileIcon = profile.id === "BABY_GIRL" || profile.id === "BABY_BOY" ? Baby2 : profile.id === "CHILD_GIRL" || profile.id === "CHILD_BOY" ? UserCircle2 : User2;
    const labels = {
      "ADULT_WOMAN": "Femme",
      "ADULT_MAN": "Homme",
      "CHILD_GIRL": "Fille",
      "CHILD_BOY": "Gar\xE7on",
      "BABY_GIRL": "B\xE9b\xE9 Fille",
      "BABY_BOY": "B\xE9b\xE9 Gar\xE7on"
    };
    return /* @__PURE__ */ React.createElement(
      "button",
      {
        key: profile.id,
        onClick: () => handleProfileSelect(profile.id),
        className: `group flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all hover:scale-105 ${selectedProfile === profile.id ? "border-brand-500 bg-gradient-to-br from-brand-50 to-brand-100 shadow-lg" : "border-gray-200 hover:border-brand-300 bg-white hover:bg-brand-50"}`
      },
      /* @__PURE__ */ React.createElement("div", { className: `w-12 h-12 rounded-full flex items-center justify-center transition-all ${selectedProfile === profile.id ? "bg-brand-500 text-white" : "bg-gray-100 text-gray-600 group-hover:bg-brand-200 group-hover:text-brand-600"}` }, /* @__PURE__ */ React.createElement(ProfileIcon, { className: "w-6 h-6" })),
      /* @__PURE__ */ React.createElement("span", { className: `text-sm font-semibold text-center ${selectedProfile === profile.id ? "text-brand-700" : "text-gray-900"}` }, labels[profile.id])
    );
  }))), selectedProfile && availableMainCategories.length > 0 && /* @__PURE__ */ React.createElement("div", { className: "mb-6" }, /* @__PURE__ */ React.createElement("label", { className: "flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3" }, "2. Cat\xE9gorie principale ", /* @__PURE__ */ React.createElement("span", { className: "text-red-500" }, "*")), /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-2 sm:grid-cols-4 gap-3" }, availableMainCategories.map((categoryId) => {
    const Icon = getCategoryIcon(categoryId);
    const labels = {
      "clothing": "V\xEAtements",
      "shoes": "Chaussures",
      "bags": "Sacs",
      "accessories": "Accessoires"
    };
    return /* @__PURE__ */ React.createElement(
      "button",
      {
        key: categoryId,
        onClick: () => handleMainCategorySelect(categoryId),
        className: `group flex items-center gap-3 p-4 rounded-xl border-2 transition-all hover:scale-105 ${selectedMainCategory === categoryId ? "border-brand-500 bg-gradient-to-br from-brand-50 to-brand-100 shadow-lg" : "border-gray-200 hover:border-brand-300 bg-white hover:bg-brand-50"}`
      },
      /* @__PURE__ */ React.createElement("div", { className: `w-10 h-10 rounded-lg flex items-center justify-center transition-all ${selectedMainCategory === categoryId ? "bg-brand-500 text-white" : "bg-gray-100 text-gray-600 group-hover:bg-brand-200 group-hover:text-brand-600"}` }, /* @__PURE__ */ React.createElement(Icon, { className: "w-5 h-5" })),
      /* @__PURE__ */ React.createElement("span", { className: `text-sm font-semibold flex-1 text-left ${selectedMainCategory === categoryId ? "text-brand-700" : "text-gray-900"}` }, labels[categoryId] || categoryId),
      /* @__PURE__ */ React.createElement(ChevronRight2, { className: "w-4 h-4 text-gray-400" })
    );
  }))), selectedMainCategory && availableSubCategories.length > 0 && /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("label", { className: "flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3" }, "3. Type pr\xE9cis"), /* @__PURE__ */ React.createElement("div", { className: "relative mb-4" }, /* @__PURE__ */ React.createElement(Search2, { className: "absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" }), /* @__PURE__ */ React.createElement(
    "input",
    {
      type: "text",
      value: searchQuery,
      onChange: (e) => setSearchQuery(e.target.value),
      placeholder: "Rechercher un type...",
      className: "w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:outline-none transition-all"
    }
  )), /* @__PURE__ */ React.createElement("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto" }, filteredSubCategories.map((subCatId) => /* @__PURE__ */ React.createElement(
    "button",
    {
      key: subCatId,
      onClick: () => onSubCategoryChange(subCatId),
      className: `flex items-center gap-3 p-3 rounded-lg border transition-all hover:scale-105 text-left ${selectedSubCategory === subCatId ? "border-brand-500 bg-brand-50 shadow-md" : "border-gray-200 hover:border-brand-300 hover:bg-brand-50"}`
    },
    /* @__PURE__ */ React.createElement("div", { className: `w-2 h-2 rounded-full ${selectedSubCategory === subCatId ? "bg-brand-500" : "bg-gray-300"}` }),
    /* @__PURE__ */ React.createElement("span", { className: `text-sm font-medium ${selectedSubCategory === subCatId ? "text-brand-700" : "text-gray-900"}` }, subCatId)
  ))), filteredSubCategories.length === 0 && /* @__PURE__ */ React.createElement("p", { className: "text-sm text-gray-500 text-center py-6" }, 'Aucun r\xE9sultat pour "', searchQuery, '"')));
};
export {
  ADULT_CLOTHING_SIZES,
  ADULT_SHOE_SIZES_MAN,
  ADULT_SHOE_SIZES_WOMAN,
  AI_OCCASIONS,
  AI_STYLES,
  AUTH_CONSTANTS,
  AuthEventType,
  AuthProvider,
  BABY_SHOE_SIZES,
  BABY_SIZES,
  BOOST_CONSTRAINTS,
  BRANDS,
  BUSINESS_CONSTANTS,
  CARRIER_LABELS,
  CART_CONSTANTS,
  CATEGORY_ICONS,
  CATEGORY_STRUCTURE,
  CATEGORY_VALIDATION,
  CHAT_VALIDATION,
  COLORS,
  COMMISSION_RATE,
  CONDITIONS_DETAILS,
  CORE_BRANDS,
  CORE_BRANDS_DATA,
  CORE_BRAND_NAMES,
  CORE_COLORS,
  CORE_CONDITIONS,
  CORE_MATERIALS,
  CORE_STYLES,
  CURRENCIES,
  CURRENT_PRICING_PHASE,
  CUSTOMIZATION_LIMITS,
  CartUtils,
  CategorySelector,
  DATE_FORMATS,
  DEFAULT_AVATARS,
  DEFAULT_LOCALE,
  DELIVERY_METHOD_LABELS,
  DeviceType,
  EVENT_THEMES,
  FALLBACK_LOCALE,
  FAMILY_PROFILE_VALIDATION,
  FILE_TYPES,
  FOUNDER_LIMIT,
  GLOWP_SERVICE_FEE_FIXED,
  GLOWP_SERVICE_FEE_MAX,
  GLOWP_SERVICE_FEE_RATE,
  I18N_NAMESPACES,
  KIDS_SHOE_SIZES,
  KIDS_SIZES,
  LOCALES_INFO,
  LOCALE_CURRENCIES,
  MATERIALS,
  MAX_FILE_SIZES,
  MESSAGE_TEMPLATES,
  NOTIFICATION_VALIDATION,
  ORDER_VALIDATION,
  PAGINATION,
  PAYMENT_PROCESSING_FEE_RATE,
  PLAN_PRICING,
  PRODUCT_COMMISSION_RATE,
  PRODUCT_CONDITIONS,
  PRODUCT_FILTERS,
  PRODUCT_SIZES,
  REFERRAL_VALIDATION,
  REGEX_PATTERNS,
  RTL_LOCALES,
  SEASONS,
  SHOP_THEMES,
  SHOP_THEME_STYLES,
  SHOP_VALIDATION,
  SOCIAL_PLATFORMS,
  STORY_TEMPLATES,
  STORY_VALIDATION,
  SUBSCRIPTION_CONSTRAINTS,
  SUBSCRIPTION_LIMITS,
  SUPPORTED_LOCALES,
  SessionStatus,
  TIER_REQUIREMENTS,
  TOP_BRANDS,
  TokenType,
  USER_PROFILES,
  UserRole,
  buildCategoryPath,
  buildSendcloudStreet,
  calculateCommission,
  calculateEngagementRate,
  calculateGlowpServiceFee,
  calculateOfferDiscount,
  calculatePaymentFee,
  calculatePlanValue,
  calculateProfileCompleteness,
  calculateTierProgress,
  canHaveMultipleQuantities,
  canUserBoost,
  canUserPerformAction,
  detectLocaleFromBrowser,
  findAllShoeMatches,
  findAllSizeMatches,
  findBrandByName,
  findBrandsByStyle,
  findBrandsByTier,
  findMatchingBrands,
  findMatchingColors,
  findMatchingStyles,
  findShoesByAge,
  findShoesBySearch,
  findSizesByAge,
  findSizesBySearch,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatFileSize,
  formatNumber,
  formatPercentage,
  formatPlanPrice,
  formatPrice,
  formatRelativeTime,
  formatRewardDescription,
  generateBrandSlug,
  generateReferralCode,
  generateShopUrl,
  getAgeCategory,
  getAgeFromSize,
  getAgeGroupFromProfile,
  getAllBrands,
  getAllCategoryItems,
  getApplicableFilters,
  getBrandFitInfo,
  getBrandsByAge,
  getBrandsByCategory,
  getCategoryDepth,
  getCategoryIcon,
  getCategoryStructure,
  getClothingSizesByProfile,
  getClothingSizesForAge,
  getColorHex,
  getColorLabel,
  getConditionDetails,
  getCurrentPrice,
  getCurrentStripePriceId,
  getFilterOptions,
  getGenderFromProfile,
  getGroupForLeaf,
  getLeafsForGroup,
  getLocaleInfo,
  getLocalizedBoostInfo,
  getLocalizedPlanInfo,
  getMaterialLabel,
  getMaxAvailableQuantity,
  getNotificationIcon,
  getOrderStatusLabel,
  getPackageSizeByCategory,
  getPlanLabel,
  getProductTypeLabel,
  getProfileFromCategorySlug,
  getProfileTypeLabel,
  getQuickReplies,
  getSellerClothingSizes,
  getSellerShoesSizes,
  getSellerSizesByCategory,
  getSellerSubTypeLabel,
  getShoeDisplayName,
  getShoeSizesByProfile,
  getShoeSizesForAge,
  getShoeWithDescription,
  getShopThemePreview,
  getSizeDisplayName,
  getSizeGrowthSuggestions,
  getSizePopularity,
  getSizeWithDescription,
  getStoryDimensionsFromType,
  getStoryExpirationTime,
  getSubcategoryGroups,
  getSubcategoryItems,
  getUserLimits,
  hasSubcategoryGroups,
  i18nConfig,
  isGroupSlug,
  isOfferReasonable,
  isProductInStock,
  isRTL,
  isSizeCompatible,
  isStoryExpired,
  isValidBoostType,
  isValidHexColor,
  isValidShopSlug,
  isValidSubscriptionPlan,
  isValidSubscriptionStatus,
  loadTranslations,
  migrateOldProfileType,
  normalizeSendcloudPoint,
  sanitizeSendcloudValue,
  saveCustomPreference,
  shouldSendNotification,
  shouldShowDefectsSection,
  slugify,
  suggestSizesForAge,
  truncateText,
  useAsync,
  useAsyncMutation,
  useAsyncQuery,
  useBreakpoint,
  useBreakpointMatch,
  useDebounce,
  useDebouncedCallback,
  useLocalStorage,
  useProfiles_default as useProfiles,
  useResponsive,
  useResponsiveClasses,
  useResponsiveValue,
  useSimpleDebounce,
  useSimpleLocalStorage,
  useTranslation2 as useTranslation,
  useValidatedLocalStorage,
  validateCartForCheckout,
  validateSellerSize
};
